import React, { useState, useEffect } from 'react';
import { 
  Globe, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, FileText, 
  Sprout, Building, Anchor, Siren, ShieldAlert, Radio, Satellite, TrendingUp, Cpu, Pickaxe, Wind, Droplets,
  Sun, Compass, CheckSquare, Activity, Volume2, AlertTriangle, Navigation, MapPin, Clock, Crosshair,
  Layers, Play, Upload, Maximize2, Image as ImageIcon, X, Calendar
} from 'lucide-react';
import { autoDownloadReport } from '../utils/autoDownloader';
import TelegramExportButton from './TelegramExportButton';
import { formatPriceWithCurrency, Currency } from '../utils/i18n';

export type SpaceAgentId = 
  | 'solotvyno-geomonitor-orchestrator'
  | 'insar-subsidence-timeseries-analyst'
  | 'gnss-sat-fusion-geodesist'
  | 'karst-sinkhole-optical-morphometrist'
  | 'subsidence-trough-geomechanical-predictor'
  | 'mine-infrastructure-structural-auditor'
  | 'hydro-karst-salt-dissolution-modeler'
  | 'tisza-transboundary-eco-monitor'
  | 'agrisat-monitor'
  | 'urban-sat-analyst'
  | 'maritime-sat-analyst'
  | 'disaster-sat-response'
  | 'geoint-defense-analyst'
  | 'sar-radar-analyst'
  | 'space-spectrum-consultant'
  | 'newspace-investment-analyst'
  | 'satellite-payload-engineer'
  | 'geological-sat-explorer'
  | 'ghg-sat-tracker'
  | 'ncuvkz-mission-control'
  | 'ncuvkz-sda-debris-tracker'
  | 'ncuvkz-space-weather-monitor'
  | 'ncuvkz-gnss-geodesy-auditor'
  | 'ncuvkz-spacecraft-testing-engineer'
  | 'gcsk-seismic-auditor'
  | 'gcsk-acoustic-infrasound-analyst'
  | 'gcsk-radiation-chemical-monitor'
  | 'gcsk-geomagnetic-seismoacoustic-analyst'
  | 'gcsk-emergency-situation-notifier'
  | 'knp-gnss-integrity-auditor'
  | 'knp-rtk-diff-correction-operator'
  | 'knp-time-frequency-metrologist'
  | 'knp-antijamming-antispoofing-analyst'
  | 'knp-defense-navigation-briefing-analyst'
  | 'war-damage-sat-auditor'
  | 'sat-image-harvester'
  | 'sat-thematic-processor'
  | 'sat-drought-water-stress-auditor';

interface SpaceSatAgentViewProps {
  agentId: SpaceAgentId;
  language: 'uk' | 'en' | 'pl';
  onLanguageChange?: (lang: 'uk' | 'en' | 'pl') => void;
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
  currency?: Currency;
}

interface AgentMeta {
  titleUk: string;
  titleEn: string;
  crewName: string;
  icon: any;
  loadingStepsUk: string[];
  loadingStepsEn: string[];
  presetsUk: Array<{
    title: string;
    type: string;
    region: string;
    timeframe: string;
    sources: string;
    context: string;
  }>;
}

const SPACE_AGENTS_META: Record<SpaceAgentId, AgentMeta> = {
  'solotvyno-geomonitor-orchestrator': {
    titleUk: 'Головний Монітор Геодинаміки & Деформацій Солотвино (ДЗЗ)',
    titleEn: 'Solotvyno Geodynamic Subsidence & Sinkhole Chief Orchestrator',
    crewName: 'Головний Монітор Геодинаміки & Деформацій Солотвино (ДЗЗ)',
    icon: Compass,
    loadingStepsUk: [
      'Ініціалізація мультиагентного комплексу геодинамічного моніторингу Солотвино...',
      'Отримання інтерферометричних радарних даних Sentinel-1 InSAR / TerraSAR-X...',
      'Аналіз динаміки розвитку карстових проваль та 3D-морфометрії лійок...',
      'Будівельна оцінка стійкості копрів та споруд шахт №8 і №9 за ДСТУ-Н Б В.1.2-18:2016...',
      'Формування підсумкового Акту-Бюлетеня за ДСТУ 4163:2020...'
    ],
    loadingStepsEn: [
      'Initializing Solotvyno Geodynamic Multi-Agent monitoring system...',
      'Fetching Sentinel-1 InSAR / TerraSAR-X radar displacement time-series...',
      'Evaluating karst sinkhole perimeter growth and 3D volume change...',
      'Auditing structural integrity of shafts #8 & #9 headframes per DSTU-N...',
      'Generating final engineering DSTU 4163:2020 official report...'
    ],
    presetsUk: [
      {
        title: 'Проммайданчик шахт №8 і №9 (Повний аудит)',
        type: 'Комплексний геодинамічний та будівельний аудит деформацій',
        region: 'смт Солотвино, Закарпатська обл., шахти №8 і №9 (47.957° N, 23.871° E)',
        timeframe: '2015 – 2026 рр. (Багаторічний моніторинг)',
        sources: 'Sentinel-1 InSAR + TerraSAR-X + VHR Оптика (0.5м) + GNSS/Нівелювання',
        context: 'Оцінка швидкості осідання (V_vert), стійкості надшахтних копрів за ДСТУ-Н Б В.1.2-18:2016, ризиків розростання карстових лійок до автодороги Н-09 та залізниці, загрози скиду розсолів у р. Тиса.'
      },
      {
        title: 'Зона Центрального карстового провалля',
        type: 'Оцінка мульди осідання та обвалення соляного купола',
        region: 'Солотвино, південно-західний фланг соляного штоку',
        timeframe: 'Останні 12 місяців (2025–2026 рр.)',
        sources: 'Sentinel-1 InSAR + PlanetScope (3m Daily)',
        context: 'Контроль наближення країв провальних лійок до житлового сектору та лінії електропередач.'
      }
    ]
  },
  'insar-subsidence-timeseries-analyst': {
    titleUk: 'Інженер Радарної Інтерферометрії Часових Рядів (PS/SBAS InSAR)',
    titleEn: 'InSAR Subsidence Time-Series & Persistent Scatterer Analyst',
    crewName: 'Інженер Радарної Інтерферометрії Часових Рядів (PS/SBAS InSAR)',
    icon: Radio,
    loadingStepsUk: [
      'Ініціалізація модуля PS/SBAS InSAR обробки радарних даних...',
      'Фазове розгортання інтерферограм Sentinel-1 (C-band) та TerraSAR-X (X-band)...',
      'Виділення постійних відбивачів (PS) на будівлях та металоконструкціях шахт...',
      'Декомпозиція векторів у вертикальну (V_vert) та горизонтальну (V_EW) компоненти...',
      'Складання технічного звіту радарної інтерферометрії...'
    ],
    loadingStepsEn: [
      'Initializing PS/SBAS InSAR radar processing engine...',
      'Unwrapping phase interferograms from Sentinel-1 & TerraSAR-X...',
      'Extracting Persistent Scatterers (PS) on mine shafts & structures...',
      'Decomposing velocity vectors into vertical (V_vert) and horizontal (V_EW)...',
      'Compiling final InSAR interferometric technical report...'
    ],
    presetsUk: [
      {
        title: 'PS-InSAR моніторинг будівель шахт №8 і №9',
        type: 'Часові ряди вертикальних осідань PS-InSAR (V_vert, мм/рік)',
        region: 'ДП «Солотвинський солерудник», шахти №8 і №9 (47.957° N, 23.871° E)',
        timeframe: '2020 – 2026 рр.',
        sources: 'Sentinel-1 SAR (C-band) + TerraSAR-X (X-band)',
        context: 'Визначення середньорічних швидкостей осідання та кумулятивних зміщень на фундаментах і спорудах підйому.'
      }
    ]
  },
  'gnss-sat-fusion-geodesist': {
    titleUk: 'Геодезист Комплексування InSAR з GNSS-Реперами & Нівелюванням',
    titleEn: 'InSAR & In-Situ GNSS Geodetic Calibration Specialist',
    crewName: 'Геодезист Комплексування InSAR з GNSS-Реперами & Нівелюванням',
    icon: Crosshair,
    loadingStepsUk: [
      'Ініціалізація модуля геодезичного комплексування та калібрування...',
      'Імпорт каталогів реперів спостережних ліній шахт №8 і №9...',
      'Фільтрація тропосферних шумів та розрахунок СКП (RMSE)...',
      'Абсолютна висотна привʼязка до системи УСК-2000 / Балтійської...',
      'Формування Акту геодезичної верифікації...'
    ],
    loadingStepsEn: [
      'Initializing geodetic data fusion & calibration engine...',
      'Importing benchmark catalogs of observation lines for shafts #8 & #9...',
      'Filtering tropospheric phase delays & computing RMSE...',
      'Aligning to absolute geodetic datum (USK-2000 / ETRS89)...',
      'Generating Geodetic Verification Report...'
    ],
    presetsUk: [
      {
        title: 'Калібрування InSAR за спостережними реперами',
        type: 'Геодезичне зведення в УСК-2000 та оцінка точності RMSE',
        region: 'Солотвино, реперна мережа шахт №8, №9',
        timeframe: '2022 – 2026 рр.',
        sources: 'Геометричне нівелювання I-II класів + GNSS RTK + Sentinel-1 InSAR',
        context: 'Математичне зіставлення супутникових та маркшейдерських відміток, усунення тропосферного дрейфу.'
      }
    ]
  },
  'karst-sinkhole-optical-morphometrist': {
    titleUk: 'Морфометрист Карстових Провалів & 3D Change Detection (ДЗЗ)',
    titleEn: 'Karst Sinkhole Optical Morphometry & 3D Volume Change Analyst',
    crewName: 'Морфометрист Карстових Провалів & 3D Change Detection (ДЗЗ)',
    icon: Layers,
    loadingStepsUk: [
      'Ініціалізація дешифрувальника оптичного ДЗЗ надвисокої роздільності...',
      'Оконтурювання бровок провальних лійок та тріщин розриву...',
      'Побудова цифрової моделі рельєфу (DEM) та розрахунок різниці ΔDEM...',
      'Обчислення обʼєму винесеної гірської маси (ΔV, м³)...',
      'Формування бюлетеня морфометричного моніторингу...'
    ],
    loadingStepsEn: [
      'Initializing VHR optical decipherer & 3D Change Detection...',
      'Tracing sinkhole perimeters & ground tensile fracture lines...',
      'Generating differential digital elevation model (ΔDEM)...',
      'Calculating volumetric rock displacement (ΔV, m³)...',
      'Exporting Karst Morphometry Bulletin...'
    ],
    presetsUk: [
      {
        title: 'Морфометрія та обʼєм провальних лійок №1 і №2',
        type: '3D Change Detection, площа та обʼєм винесених порід (ΔV)',
        region: 'Солотвинський карстовий масив (47.96° N, 23.87° E)',
        timeframe: '2020 – 2026 рр.',
        sources: 'WorldView-3 (0.3м) + Pléiades Neo + Stereo-DEM',
        context: 'Розрахунок швидкості відступу бровок провалів до фундаментів споруд шахт №8 і №9 та обʼєму карстових пустот.'
      }
    ]
  },
  'subsidence-trough-geomechanical-predictor': {
    titleUk: 'Аналітик Мульд Зсування & Гірничої Геомеханіки',
    titleEn: 'Mining Subsidence Trough & Geomechanical Fracture Predictor',
    crewName: 'Аналітик Мульд Зсування & Гірничої Геомеханіки',
    icon: Activity,
    loadingStepsUk: [
      'Ініціалізація модуля гірничої геомеханіки та зрушення порід...',
      'Розрахунок граничних кутів зсуву соляного масиву (δ, β, γ)...',
      'Побудова ізоліній нахилів (i), кривизни (K) та деформацій (ε)...',
      'Прогнозування радіуса небезпечної зони на 1, 3 та 5 років...',
      'Складання Геомеханічного Паспорта мульди осідання...'
    ],
    loadingStepsEn: [
      'Initializing mining geomechanics & rock displacement engine...',
      'Computing limit angle of draw (δ, β, γ) in salt rock mass...',
      'Isoline mapping of tilt (i), curvature (K), and strain (ε)...',
      'Forecasting 1, 3, and 5-year hazard zone propagation radius...',
      'Generating Geomechanical Subsidence Forecast Passport...'
    ],
    presetsUk: [
      {
        title: 'Геомеханічний прогноз мульди осідання',
        type: 'Розрахунок кутів зрушення (δ, β, γ), нахилів (i) та кривизни (K)',
        region: 'Гірничий відвід шахт №8 і №9 ДП «Солотвинський солерудник»',
        timeframe: 'Прогноз на 2026 – 2030 рр.',
        sources: 'Плани гірничих робіт + InSAR вектори деформацій',
        context: 'Визначення зон критичних розтягуючих деформацій та загрози для автодороги Н-09 Мукачево — Рогатин.'
      }
    ]
  },
  'mine-infrastructure-structural-auditor': {
    titleUk: 'Експерт Технічного Стану Будівель & Копрів Шахт (ДСТУ-Н/ДБН)',
    titleEn: 'Mine Shaft & Surface Infrastructure Structural Health Auditor',
    crewName: 'Експерт Технічного Стану Будівель & Копрів Шахт (ДСТУ-Н/ДБН)',
    icon: Building,
    loadingStepsUk: [
      'Ініціалізація інженерно-будівельного модуля за ДСТУ-Н Б В.1.2-18:2016...',
      'Розрахунок напружено-деформованого стану копрів та каркасів споруд...',
      'Аналіз граничних нерівномірних осідань та перекосів фундаментів...',
      'Присвоєння категорії технічного стану (I - нормальний ... IV - аварійний)...',
      'Формування офіційного Технічного Звіту експертизи...'
    ],
    loadingStepsEn: [
      'Initializing structural engineering engine per DSTU-N B V.1.2-18:2016...',
      'Computing stress-strain states of mine headframes & load-bearing frames...',
      'Analyzing differential foundation settlements & angular distortions...',
      'Assigning technical condition category (Cat I to Cat IV Hazardous)...',
      'Generating Official Structural Health Engineering Report...'
    ],
    presetsUk: [
      {
        title: 'Експертиза копрів та споруд шахт №8 і №9',
        type: 'Категоризація технічного стану за ДСТУ-Н Б В.1.2-18:2016',
        region: 'Проммайданчик шахти №8 та шахти №9 Солотвино',
        timeframe: 'Стан на 2026 р.',
        sources: 'Дані геодезичних нахилів, тріщиноутворення, InSAR-зміщення',
        context: 'Оцінка кренів шахтних копрів, перекосів фундаментів підйому, несучої здатності залізобетонних та сталевих каркасів.'
      }
    ]
  },
  'hydro-karst-salt-dissolution-modeler': {
    titleUk: 'Гідрогеолог Розчинення Соляного Масиву & Водопритоку',
    titleEn: 'Hydrogeological Salt Karst Dissolution & Inflow Modeler',
    crewName: 'Гідрогеолог Розчинення Соляного Масиву & Водопритоку',
    icon: Pickaxe,
    loadingStepsUk: [
      'Ініціалізація гідрогеологічного модуля соляного карстоутворення...',
      'Розрахунок балансу водопритоку прісних вод до затоплених виробок...',
      'Моделювання кінетики розчинення галіту (v_diss, мм/добу)...',
      'Оцінка стійкості соляних ціликів під гідростатичним тиском...',
      'Формування Гідрогеологічного Звіту вилуговування масиву...'
    ],
    loadingStepsEn: [
      'Initializing hydrogeological salt karst dissolution engine...',
      'Computing freshwater inflow mass balance into flooded chambers...',
      'Simulating halite dissolution kinetics (v_diss, mm/day)...',
      'Evaluating salt pillar stability under hydrostatic pore pressure...',
      'Generating Hydrogeological Dissolution & Inflow Report...'
    ],
    presetsUk: [
      {
        title: 'Кінетика вилуговування соляного купола',
        type: 'Гідрогеологічний баланс та швидкість розчинення галіту (v_diss)',
        region: 'Затоплені виробки шахт №8, №9 Солотвино',
        timeframe: '2024 – 2026 рр.',
        sources: 'Гідрорежим шахтних вод + метеодані опадів + Sentinel-2',
        context: 'Оцінка впливу фільтрації прісних вод на руйнування міжкамерних соляних ціликів та ризики гідропрориву.'
      }
    ]
  },
  'tisza-transboundary-eco-monitor': {
    titleUk: 'Інспектор Транскордонного Еко-Ризику Басейну р. Тиса (EU WFD)',
    titleEn: 'Tisza River Transboundary Brine Spill & Eco-Risk Monitor',
    crewName: 'Інспектор Транскордонного Еко-Ризику Басейну р. Тиса (EU WFD)',
    icon: ShieldAlert,
    loadingStepsUk: [
      'Ініціалізація супутникового гідроекологічного модуля (Sentinel-2)...',
      'Спектральний аналіз індексів каламутності та солоності вод р. Тиса...',
      'Моделювання зон затоплення при прориві захисних відстійників розсолу...',
      'Оцінка транскордонного впливу на Румунію за Конвенцією Еспо...',
      'Формування Міжнародного Екологічного Бюлетеня...'
    ],
    loadingStepsEn: [
      'Initializing Sentinel-2 hydro-ecological satellite engine...',
      'Spectral analysis of water salinity and turbidity indices in Tisza river...',
      'Simulating brine flood inundation upon settling basin breach...',
      'Evaluating transboundary impact on Romania under Espoo Convention...',
      'Generating International Transboundary Eco-Risk Bulletin...'
    ],
    presetsUk: [
      {
        title: 'Транскордонний моніторинг басейну р. Тиса',
        type: 'Контроль мінералізації та ризиків прориву розсолів (EU WFD 2000/60/EC)',
        region: 'Басейн р. Тиса (ділянка Солотвино — Сігету-Мармацієй, Румунія)',
        timeframe: '2025 – 2026 рр.',
        sources: 'Sentinel-2 MSI (водні індекси) + топомоделювання ухилів',
        context: 'Оцінка ризику потрапляння високоминералізованих розсолів у прикордонну річку Тиса та транскордонного комплаєнсу з ЄС.'
      }
    ]
  },
  'agrisat-monitor': {
    titleUk: 'Агроекологічний & Лісовий Супутниковий Аналітик',
    titleEn: 'AgriSat & Deforestation Monitor',
    crewName: 'Агроекологічний та Лісовий Супутниковий Аналітик',
    icon: Sprout,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля агро-супутникового моніторингу...',
      'Отримання даних Sentinel-2 MSI / Landsat 8-9...',
      'Розрахунок вегетаційних індексів (NDVI, EVI, NDWI)...',
      'Оцінка вологості ґрунтів, посух та картографування вирубок...',
      'Формування підсумкового Агро-Супутникового Звіту...'
    ],
    loadingStepsEn: [
      'Initializing AgriSat AI Multi-Agent crew...',
      'Fetching Sentinel-2 / Landsat spectral bands...',
      'Computing NDVI, EVI & NDWI vegetation indices...',
      'Evaluating soil moisture, drought stress & deforestation...',
      'Generating final AgriSat & Deforestation Audit Report...'
    ],
    presetsUk: [
      {
        title: 'Карпатські лісові масиви',
        type: 'Моніторинг вирубок та біомаси (NDVI / EVI)',
        region: 'Карпатський НПП (Яремче — Ворохта — Рахів)',
        timeframe: '2023 – 2026 рр.',
        sources: 'Sentinel-2 MSI (10m) + Landsat 9',
        context: 'Виявлення суцільних та вибіркових вирубок лісу, оцінка втрати біомаси та ризиків зсувів.'
      },
      {
        title: 'Полісся: Агроугіддя та лісгоспи',
        type: 'Вологість ґрунту та стрес рослинності (NDWI)',
        region: 'Житомирська та Рівненська області',
        timeframe: 'Сезон 2025 – 2026 рр.',
        sources: 'Sentinel-2 + MODIS Soil Moisture',
        context: 'Оцінка стану вологозабезпеченості кукурудзи та сої, детекція посушливих ділянок.'
      },
      {
        title: 'Південні агрохолдинги',
        type: 'Прогнозування врожайності та посухи',
        region: 'Одеська та Миколаївська області (Степова зона)',
        timeframe: 'Весняно-літній період 2026 р.',
        sources: 'Sentinel-2 (ESA) + Earth Engine',
        context: 'Моніторинг стану озимих культур, індекс падіння біомаси через температурний стрес.'
      }
    ]
  },
  'urban-sat-analyst': {
    titleUk: 'Аналітик Урбаністики & Інфраструктури (ДЗЗ)',
    titleEn: 'Urban & Infrastructure Sat Analyst',
    crewName: 'Аналітик Урбаністики та Інфраструктури (ДЗЗ)',
    icon: Building,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля супутникової урбаністики...',
      'Отримання фотознімків Change Detection високої роздільності...',
      'Аналіз змін контурів забудови та транспортних артерій...',
      'Перевірка відповідності містобудівним нормам та оцінка пошкоджень...',
      'Формування підсумкового Супутникового Урбан-Звіту...'
    ],
    loadingStepsEn: [
      'Initializing Urban Satellite AI crew...',
      'Running optical Change Detection algorithm...',
      'Analyzing building footprint growth & road corridors...',
      'Auditing construction compliance & damage assessment...',
      'Generating final Urban & Infrastructure Sat Report...'
    ],
    presetsUk: [
      {
        title: 'Київська агломерація та Окружна',
        type: 'Моніторинг розширення забудови (Change Detection)',
        region: 'м. Київ, Софіївська Борщагівка — Ірпінь',
        timeframe: '2024 – 2026 рр.',
        sources: 'Sentinel-2 + PlanetScope (3m)',
        context: 'Виявлення нових плям забудови, перевірка червоних ліній та інфраструктурних мереж.'
      },
      {
        title: 'Зона відбудови Ірпінь — Буча',
        type: 'Оцінка темпів відбудови житлового фонду',
        region: 'Бучанський район Київської області',
        timeframe: '2023 – 2026 рр. (Динаміка відновлення)',
        sources: 'Sentinel-2 + WorldView-3',
        context: 'Картографування відновлених покрівель, об’єктів соціальної інфраструктури та нових споруд.'
      }
    ]
  },
  'maritime-sat-analyst': {
    titleUk: 'Морський & Портовий Супутниковий Аналітик',
    titleEn: 'Maritime & Port Sat Monitor',
    crewName: 'Морський та Портовий Супутниковий Аналітик',
    icon: Anchor,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля морської космічної розвідки...',
      'Зіставлення радарних даних SAR (Sentinel-1) та треків AIS...',
      'Виявлення неопізнаних суден (Dark Vessels) та плям нафти...',
      'Аналіз завантаженості портових терміналів...',
      'Формування підсумкового Морського Супутникового Звіту...'
    ],
    loadingStepsEn: [
      'Initializing Maritime Satellite AI crew...',
      'Cross-matching Sentinel-1 SAR imagery with AIS vessel feeds...',
      'Detecting dark vessels and offshore oil slicks...',
      'Analyzing container port congestion metrics...',
      'Generating final Maritime & Port Sat Audit Report...'
    ],
    presetsUk: [
      {
        title: 'Одеський морський торговельний порт',
        type: 'Моніторинг завантаженості терміналів та реду',
        region: 'Одеська затока, 46.48° N, 30.74° E',
        timeframe: 'Останні 30 днів (2026 р.)',
        sources: 'Sentinel-1 SAR + Sentinel-2 Optical + AIS',
        context: 'Оцінка кількості балковозів, контейнеровозів на зовнішньому рейді та інтенсивності перевалки.'
      },
      {
        title: 'Чорноморська акваторія',
        type: 'Детекція тіньового флоту та нафтових розливів',
        region: 'Виключна морська економічна зона України',
        timeframe: 'Поточний моніторинг 2026 р.',
        sources: 'Sentinel-1 SAR (VV/VH) + ICEYE',
        context: 'Виявлення суден із вимкненим AIS та нафтових плям після Ship-to-Ship перевантажень.'
      }
    ]
  },
  'disaster-sat-response': {
    titleUk: 'Супутниковий Аналітик Реагування на НС',
    titleEn: 'Disaster & Emergency Sat Response',
    crewName: 'Супутниковий Аналітик Реагування на НС',
    icon: Siren,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля екстреного космічного реагування...',
      'Завантаження супутникових знімків зони катастрофи (Copernicus EMS)...',
      'Розрахунок індексів затоплення (MNDWI) та згарищ (dNBR)...',
      'Аналіз заблокованих доріг та гуманітарних коридорів...',
      'Формування Оперативного Звіту Реагування на НС...'
    ],
    loadingStepsEn: [
      'Initializing Emergency Satellite Response AI crew...',
      'Fetching Copernicus EMS high-res disaster feeds...',
      'Computing inundation (MNDWI) & burn severity (dNBR)...',
      'Evaluating blocked road networks & access corridors...',
      'Generating final Disaster & Emergency Sat Briefing...'
    ],
    presetsUk: [
      {
        title: 'Басейн річки Тиса (Закарпаття)',
        type: 'Картографування зон затоплення (MNDWI)',
        region: 'Рахівський та Хустський райони Закарпаття',
        timeframe: 'Паводок 2026 р.',
        sources: 'Sentinel-1 SAR + Sentinel-2',
        context: 'Розрахунок площ підтоплених сільгоспугідь, заблокованих мостів та житлових будинків.'
      },
      {
        title: 'Чорнобильський біосферний заповідник',
        type: 'Аналіз лісових пожеж та площ згарищ (dNBR)',
        region: 'Чорнобильська зона відчуження',
        timeframe: 'Літній сезон 2025 – 2026 рр.',
        sources: 'Sentinel-2 SWIR + MODIS Active Fires',
        context: 'Контур поширення вогню, виявлення локальних осередків тління та деградації рослинності.'
      }
    ]
  },
  'geoint-defense-analyst': {
    titleUk: 'Аналітик Геопросторової Розвідки (GEOINT)',
    titleEn: 'GEOINT Defense Reconnaissance Analyst',
    crewName: 'Аналітик Геопросторової Розвідки (GEOINT)',
    icon: ShieldAlert,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля геопросторової розвідки (GEOINT)...',
      'Завантаження супутникових даних високого дозволу та SAR...',
      'Детекція змін об’єктів, техніки, складових та фортифікацій...',
      'Оцінка пошкоджень інфраструктурних вузлів (BDA)...',
      'Формування підсумкового Аналітичного GEOINT Звіту...'
    ],
    loadingStepsEn: [
      'Initializing GEOINT Defense Satellite AI crew...',
      'Processing high-resolution optical & SAR reconnaissance streams...',
      'Detecting change footprints, equipment & fortification lines...',
      'Executing Battlefield Damage Assessment (BDA)...',
      'Generating final GEOINT Reconnaissance Audit Briefing...'
    ],
    presetsUk: [
      {
        title: 'Прикордонна та фортифікаційна зона',
        type: 'Картографування укріплень та Change Detection',
        region: 'Східний та Південний сектори',
        timeframe: 'Останні 14 діб (2026 р.)',
        sources: 'Optical sub-meter + Sentinel-1 SAR',
        context: 'Детекція нових ліній фортифікацій, інженерних загороджень та руху транспортних колон.'
      },
      {
        title: 'Оцінка уражень інфраструктури (BDA)',
        type: 'Battlefield Damage Assessment об’єкта',
        region: 'Промислово-логістичний вузол',
        timeframe: 'До та після вогневого впливу',
        sources: 'WorldView / PlanetScope sub-meter',
        context: 'Визначення ступеня руйнування мостів, складських приміщень та колійного господарства.'
      }
    ]
  },
  'sar-radar-analyst': {
    titleUk: 'Аналітик Радарних Даних SAR & Нічної Оптики',
    titleEn: 'SAR Radar & Night Optics Analyst',
    crewName: 'Аналітик Радарних Даних SAR та Нічної Оптики',
    icon: Radio,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля всепогодного радарного моніторингу Sentinel-1 (C-SAR) / ICEYE...',
      'Завантаження орбітальних ефемерид POEORB та радіометричне калібрування до Sigma0 (дБ)...',
      'Приглушення спекл-шуму фільтром Лі (Lee 3x3) та Range-Doppler корекція за ЦМР SRTM 1Sec...',
      'Побудова XML-графа Graph Builder для порогової бінаризації водної гладі (Sigma0 < -21.5 дБ)...',
      'Формування підсумкового Звіту Радарного ДЗЗ та масок водності за ДСТУ 4163:2020...'
    ],
    loadingStepsEn: [
      'Initializing All-Weather Sentinel-1 (C-SAR) / ICEYE Radar & Night Optics AI crew...',
      'Loading precise orbit files (POEORB) and executing radiometric calibration to Sigma0 (dB)...',
      'Applying Lee 3x3 adaptive speckle filter and Range-Doppler Terrain Correction with SRTM 1Sec DEM...',
      'Building Graph Builder XML pipeline for threshold water binarization (Sigma0 < -21.5 dB)...',
      'Generating final SAR Earth Observation Audit Report & Water Masks compliant with DSTU 4163:2020...'
    ],
    presetsUk: [
      {
        title: 'Моніторинг вирубки лісів: Random Forest у SNAP (Change Detection 2017 vs 2023)',
        type: 'Машинне навчання та детекція дефорестації (Sentinel-1 C-SAR)',
        region: 'Українське Полісся / Карпати / Київська область',
        timeframe: '2017 р. vs 2023–2026 рр. (мультитемпоральний стек літо-літо)',
        sources: 'Sentinel-1 C-SAR IW GRD + Create Stack + Random Forest у SNAP',
        context: 'Фізика об’ємного розсіювання крон у крос-поляризації Sigma0_VH (-11 дБ цілісний ліс проти -19 дБ вирубка). Побудова тренувальних полігонів дефорестації та класифікація Random Forest з оцінкою OOB помилки.'
      },
      {
        title: 'Каховська катастрофа: Картографування затоплень та осушення (Sentinel-1 SAR)',
        type: 'Всепогодна детекція надзвичайних гідроекологічних ситуацій',
        region: 'Херсонська та Запорізька області, Каховське водосховище та пониззя Дніпра',
        timeframe: 'Червень 2023 р. (порівняно з травнем 2023 та 2026 рр.)',
        sources: 'Sentinel-1 C-SAR IW GRD + Copernicus Browser + ESA SNAP',
        context: 'Всепогодне картографування прориву греблі Каховської ГЕС, виділення затоплених площ за наявності хмарності, порогове виділення води (Sigma0 < -18 дБ).'
      },
      {
        title: 'Весняне водопілля р. Десна (Чернігівщина: Graph Builder)',
        type: 'Автоматизоване конвеєрне картографування паводків',
        region: 'Чернігівська та Київська області, басейн річки Десна',
        timeframe: 'Квітень – Травень 2023 р. (порівняно з меженним рівнем)',
        sources: 'Sentinel-1 C-SAR IW GRD + Graph Builder XML конвеєр (SNAP)',
        context: 'Автоматизоване виявлення меж повені за бімодальною гістограмою зворотного розсіювання, розрахунок площі затоплених угідь у гектарах.'
      },
      {
        title: 'Дністровське вдсх. (Бакота): Синергія Оптики (Sentinel-2) та Радара (Sentinel-1)',
        type: 'Мультисенсорний всепогодний моніторинг обміління водойм',
        region: 'Хмельницька обл., Бакотська затока Дністровського водосховища (48.603° N, 26.978° E)',
        timeframe: 'Вересень 2026 р. (порівняно з вереснем 2018 р.)',
        sources: 'Sentinel-2 L2A (MNDWI) + Sentinel-1 C-SAR (Sigma0_VH) + GUM budget',
        context: 'Подолання 85% осінньої хмарності над каньйоном, оцінка скорочення водної гладі на 311 га з розширеною невизначеністю U = ±0.22 кв. км.'
      },
      {
        title: 'Радарне спостереження крізь хмарність',
        type: 'Всепогодний аналіз ґрунтів та споруд (VV/VH)',
        region: 'Карпатський гірський хребет',
        timeframe: 'Період суцільної хмарності 2026 р.',
        sources: 'Sentinel-1 C-band SAR + ICEYE X-band',
        context: 'Виявлення зміщень ґрунту, зволоження та металевих об’єктів незважаючи на щільний туман.'
      },
      {
        title: 'Нічний енергомоніторинг міст',
        type: 'Оцінка енергопостачання за світловою активністю',
        region: 'Київська, Харківська та Дніпропетровська області',
        timeframe: 'Нічні знімки 2026 р.',
        sources: 'VIIRS Day-Night Band (Suomi NPP / JPSS)',
        context: 'Оцінка відновлення електромереж та інтенсивності нічного освітлення міських кварталів.'
      }
    ],
    presetsEn: [
      {
        title: 'Kakhovka Disaster: Flood Inundation & Dehydration Mapping (Sentinel-1 SAR)',
        type: 'All-Weather Disaster & Emergency Hydro-Ecological Detection',
        region: 'Kherson and Zaporizhzhia Oblasts, Kakhovka Reservoir and Lower Dnieper',
        timeframe: 'June 2023 (vs. May 2023 & 2026)',
        sources: 'Sentinel-1 C-SAR IW GRD + Copernicus Browser + ESA SNAP',
        context: 'All-weather mapping of the Kakhovka HPP dam breach, extraction of inundated zones despite heavy cloud cover, threshold water segmentation (Sigma0 < -18 dB).'
      },
      {
        title: 'Desna River Spring Flooding (Chernihiv: Graph Builder)',
        type: 'Automated Pipeline Flood Plain Mapping',
        region: 'Chernihiv and Kyiv Oblasts, Desna River Basin',
        timeframe: 'April – May 2023 (vs. baseline low-water level)',
        sources: 'Sentinel-1 C-SAR IW GRD + Graph Builder XML pipeline (SNAP)',
        context: 'Automated flood extent delineation based on bimodal backscatter histogram analysis, inundated meadow surface computation in hectares.'
      },
      {
        title: 'Dniester Reservoir (Bakota): Multi-Sensor Optical (Sentinel-2) + Radar (Sentinel-1) Synergy',
        type: 'Multi-Sensor All-Weather Reservoir Depletion Auditing',
        region: 'Khmelnytskyi Oblast, Bakota Bay, Dniester Reservoir (48.603° N, 26.978° E)',
        timeframe: 'September 2026 (vs. September 2018)',
        sources: 'Sentinel-2 L2A (MNDWI) + Sentinel-1 C-SAR (Sigma0_VH) + GUM budget',
        context: 'Overcoming 85% autumn cloud cover over the river canyon, evaluating 311 ha water surface shrinkage with expanded uncertainty U = ±0.22 sq.km.'
      },
      {
        title: 'All-Weather Cloud-Penetrating Radar Surveillance',
        type: 'Ground and infrastructure analysis (VV/VH)',
        region: 'Carpathian Mountain Range',
        timeframe: 'Continuous cloud cover season 2026',
        sources: 'Sentinel-1 C-band SAR + ICEYE X-band',
        context: 'Detection of ground subsidence, soil moisture, and metal structures through dense cloud and fog.'
      },
      {
        title: 'Urban Nighttime Energy & Grid Monitoring',
        type: 'Power supply assessment via light emissions',
        region: 'Kyiv, Kharkiv, and Dnipropetrovsk Oblasts',
        timeframe: 'Nighttime satellite passes 2026',
        sources: 'VIIRS Day-Night Band (Suomi NPP / JPSS)',
        context: 'Auditing electrical grid restoration and nighttime urban quarter illumination intensity.'
      }
    ]
  },
  'space-spectrum-consultant': {
    titleUk: 'Консультант Орбітально-Частотного Ресурсу',
    titleEn: 'Space Spectrum & Orbit Consultant',
    crewName: 'Консультант Орбітально-Частотного Ресурсу',
    icon: Satellite,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля орбітально-частотного ресурсу...',
      'Розрахунок Link Budget та параметрів радіолінії...',
      'Перевірка вимог МСЕ (ITU Radio Regulations) та НКЕК...',
      'Оцінка ризиків космічного сміття (Debris Mitigation Plan)...',
      'Формування підсумкового Звіту Супутникового Комплаєнсу...'
    ],
    loadingStepsEn: [
      'Initializing Orbit & Spectrum Compliance AI crew...',
      'Computing Satellite Link Budget & RF coverage...',
      'Auditing ITU Radio Regulations & NKEK filing rules...',
      'Evaluating Space Debris Mitigation Plan (ISO 24113)...',
      'Generating final Space Spectrum & Orbit Audit Report...'
    ],
    presetsUk: [
      {
        title: 'CubeSat LEO супутникове угруповання',
        type: 'ITU Filing та розрахунок частотного покриття',
        region: 'Низька навколоземна орбіта (LEO 550 км, 53° inclination)',
        timeframe: 'Планований запуск 2026 – 2027 рр.',
        sources: 'ITU Frequency Registry + NORAD TLE Data',
        context: 'Розрахунок Link Budget у Ka/Ku діапазонах, запобігання інтерференції та дотримання ISO 24113.'
      }
    ]
  },
  'newspace-investment-analyst': {
    titleUk: 'Аналітик Інвестицій & Риночків NewSpace',
    titleEn: 'NewSpace Investment & Market Analyst',
    crewName: 'Аналітик Інвестицій та Риночків NewSpace',
    icon: TrendingUp,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля інвестиційного NewSpace аналізу...',
      'Аналіз сегментів ринку Earth Observation та SmallSat...',
      'Розрахунок юніт-економіки запуска ($/кг на LEO)...',
      'Складання чернетки грантової заявки ESA / BRAVE1...',
      'Формування підсумкового NewSpace Інвестиційного Меморандуму...'
    ],
    loadingStepsEn: [
      'Initializing NewSpace Investment & Market AI crew...',
      'Sizing Earth Observation & SmallSat market (TAM/SAM/SOM)...',
      'Calculating payload launch unit economics ($/kg to LEO)...',
      'Drafting ESA & BRAVE1 grant proposal structures...',
      'Generating final NewSpace Investment Memorandum...'
    ],
    presetsUk: [
      {
        title: 'Earth Observation as a Service (EOaaS)',
        type: 'Маркетинговий аналіз та оцінка бізнес-моделі',
        region: 'Глобальний ринок / ЄС та Україна',
        timeframe: 'Прогноз розвитку 2026 – 2030 рр.',
        sources: 'Euroconsult + BryceTech + Space Capital',
        context: 'Розрахунок TAM/SAM/SOM для надання аналітики супутникових знімків агросектору та обороні.'
      }
    ]
  },
  'satellite-payload-engineer': {
    titleUk: 'Інженерний Помічник Проєктування Супутників',
    titleEn: 'Spacecraft & Payload Design Assistant',
    crewName: 'Інженерний Помічник Проєктування Супутників',
    icon: Cpu,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля супутникового інжинірингу...',
      'Розрахунок масо-габаритного бюджету CubeSat (SWaP)...',
      'Підбір оптичних / гіперспектральних сенсорів...',
      'Розрахунок сонячних панелей, акумуляторів та тривалості орбіти...',
      'Формування підсумкового ЕСПК Технічного Паспорта Супутника...'
    ],
    loadingStepsEn: [
      'Initializing Spacecraft Engineering & SWaP AI crew...',
      'Computing Mass, Volume & Power budgets (SWaP)...',
      'Selecting optical / hyperspectral payload sensors...',
      'Sizing solar panels, EPS batteries & orbital lifetime...',
      'Generating final Spacecraft Payload Technical Design Spec...'
    ],
    presetsUk: [
      {
        title: 'CubeSat 3U ДЗЗ із оптико-електронною камерою',
        type: 'Розрахунок SWaP (Mass, Power, Volume)',
        region: 'Сонячно-синхронна орбіта (SSO 500 км)',
        timeframe: 'Проєктний цикл 2026 р.',
        sources: 'CalPoly CubeSat Standard + ESA Payload Spec',
        context: 'Маса 4.2 кг, споживання 12 Вт, сонячні панелі 20 Вт, камери роздільністю 3.5 м.'
      }
    ]
  },
  'geological-sat-explorer': {
    titleUk: 'Супутниковий Геолог & Аналітик Надр',
    titleEn: 'Mineral & Geological Remote Explorer',
    crewName: 'Супутниковий Геолог та Аналітик Надр',
    icon: Pickaxe,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля супутникової геології...',
      'Обробка спектральних каналів SWIR/TIR Sentinel-2 & Landsat...',
      'Виявлення мінеральних індексів глинистих та залізистих порід...',
      'Моніторинг обсягів виробок кар’єрів та хвостосховищ...',
      'Формування підсумкового Геологічного Звіту Розвідки Надр...'
    ],
    loadingStepsEn: [
      'Initializing Remote Geological Exploration AI crew...',
      'Processing SWIR/TIR spectral bands (Sentinel-2 & Landsat)...',
      'Mapping clay, iron & carbonate mineral alteration zones...',
      'Auditing open-pit quarry volumes & tailings ponds...',
      'Generating final Geological Satellite Exploration Report...'
    ],
    presetsUk: [
      {
        title: 'Житомирський гранітний кряж та ільменіти',
        type: 'Спектральний аналіз корисних копалин (SWIR)',
        region: 'Житомирська область (Володарсько-Волинський масив)',
        timeframe: 'Геологічний моніторинг 2026 р.',
        sources: 'Sentinel-2 SWIR + ASTER GDEM',
        context: 'Картографування зон гідротермальних змін, відвалів порід та рідкісноземельних мінералів.'
      }
    ]
  },
  'ghg-sat-tracker': {
    titleUk: 'Супутниковий Трекер Парникових Газів & Methane',
    titleEn: 'GHG & Carbon Emissions Satellite Tracker',
    crewName: 'Супутниковий Трекер Парникових Газів та Methane',
    icon: Wind,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля супутникового клімат-контролю...',
      'Отримання даних спектрометра TROPOMI (Sentinel-5P)...',
      'Детекція витоків метану (CH4) та факельних викидів CO2...',
      'Розрахунок поглинання вуглецю лісами для Carbon Credits...',
      'Формування підсумкового Звіту Викидів Парникових Газів...'
    ],
    loadingStepsEn: [
      'Initializing GHG & Methane Tracking Satellite AI crew...',
      'Fetching Sentinel-5P TROPOMI atmospheric spectrometer feeds...',
      'Detecting methane leak plumes (CH4) & industrial CO2 flares...',
      'Auditing forest carbon sequestration for Carbon Credits...',
      'Generating final Greenhouse Gas & Climate Satellite Report...'
    ],
    presetsUk: [
      {
        title: 'Магістральні газопроводи та Сміттєзвалища',
        type: 'Детекція витоків метану (CH4 Plume Tracking)',
        region: 'Газотранспортна система та Полігони ТПВ',
        timeframe: 'Атмосферний моніторинг 2026 р.',
        sources: 'Sentinel-5P (TROPOMI) + GHGSat',
        context: 'Фіксація локальних концентрацій метану у ppm, виявлення протікань на компресорних станціях.'
      }
    ]
  },
  'ncuvkz-mission-control': {
    titleUk: 'Оператор ЦУП & Телеметрії Космічних Апаратів',
    titleEn: 'Satellite Mission Control & Telemetry Operator',
    crewName: 'Оператор ЦУП та Телеметрії Космічних Апаратів',
    icon: Cpu,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля ЦУП та телеметрії...',
      'Дешифрування логів бортових систем (EPS, ADCS, Thermal)...',
      'Детекція аномальних режимів та фазових відхилень...',
      'Розрахунок орбітальних маневрів та імпульсу двигунів...',
      'Формування підсумкового Польотного Звіту ЦУП...'
    ],
    loadingStepsEn: [
      'Initializing Satellite Mission Control AI crew...',
      'Parsing onboard subsystem telemetry logs...',
      'Detecting anomalies & bus phase shifts...',
      'Calculating orbital maneuver impulse vectors...',
      'Generating final Mission Operations Flight Report...'
    ],
    presetsUk: [
      {
        title: 'Бортова телеметрія супутника Січ-2-30 / LEO CubeSat',
        type: 'Аналіз здоров’я систем EPS, ADCS та Thermal',
        region: 'Низька навколоземна орбіта (LEO 525 км)',
        timeframe: 'Сеанси зв’язку 2026 р.',
        sources: 'Командно-вимірювальний комплекс НЦУВКЗ (Дунаївці / Львів)',
        context: 'Перевірка профілю розряду акумуляторів, термобалансу та стабілізації сонячних батарей супутника Січ-2-30.'
      }
    ]
  },
  'ncuvkz-sda-debris-tracker': {
    titleUk: 'Аналітик Контролю Космічного Простору & Сміття',
    titleEn: 'Space Domain Awareness & Debris Tracker',
    crewName: 'Аналітик Контролю Космічного Простору та Сміття',
    icon: Radio,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля моніторингу космічного простору (SDA)...',
      'Опрацювання каталогів орбітальних об’єктів TLE (NORAD / Спецкаталоги)...',
      'Розрахунок небезпечних зближень та ймовірності зіткнення (PoC)...',
      'Детекція аномальних маневрів військових супутників...',
      'Формування підсумкового SDA Звіту Безпеки Орбіт...'
    ],
    loadingStepsEn: [
      'Initializing Space Domain Awareness (SDA) AI crew...',
      'Parsing TLE orbital target catalogs...',
      'Computing collision probability (PoC) & Miss Distance...',
      'Detecting anomalous military orbit maneuvers...',
      'Generating final Space Domain Awareness Audit Report...'
    ],
    presetsUk: [
      {
        title: 'Моніторинг LEO угруповання супутників та фрагментів сміття',
        type: 'Оцінка небезпечних зближень (Conjunction Assessment & Miss Distance < 800м)',
        region: 'Супутник Січ-2-30 (NORAD 51053, LEO 525 км)',
        timeframe: 'Прогноз на 72 години (2026 р.)',
        sources: 'Space-Track TLE + Оптико-електронні станції НЦУВКЗ',
        context: 'Розрахунок ймовірності зіткнення супутника Січ-2-30 із фрагментами космічного сміття Cosmos-1408 (Miss Distance < 800м) та розрахунок імпульсу маневру ухилення CAM.'
      }
    ]
  },
  'ncuvkz-space-weather-monitor': {
    titleUk: 'Аналітик Космічної Погоди & Сонця',
    titleEn: 'Space Weather & Solar Flare Monitor',
    crewName: 'Аналітик Космічної Погоди та Сонця',
    icon: Sun,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля служби космічної погоди...',
      'Аналіз даних про сонячні спалахи M/X класу та корональні викиди (CME)...',
      'Прогнозування Kp-індексу геомагнітних бур...',
      'Оцінка радіаційної обстановки на орбітах...',
      'Формування Офіційного Бюлетеня Космічної Погоди...'
    ],
    loadingStepsEn: [
      'Initializing Space Weather & Heliophysics AI crew...',
      'Parsing solar flare (M/X class) & CME telemetry...',
      'Forecasting geomagnetic Kp-index storm scales...',
      'Auditing spacecraft radiation dose & HF interference...',
      'Generating final Space Weather & Geomagnetic Bulletin...'
    ],
    presetsUk: [
      {
        title: 'Сонячний спалах класу X та прогноз геомагнітної бурі',
        type: 'Прогноз Kp-індексу та радіаційного навантаження (Kp = 7+)',
        region: 'Навколоземний космічний простір (LEO / GEO)',
        timeframe: 'Поточний сонячний цикл 2026 р.',
        sources: 'Служба космічної погоди НЦУВКЗ + SOHO/SDO/NOAA SWPC',
        context: 'Оцінка загрози збоїв короткохвильового радіозв’язку, збурень іоносфери та радіаційного впливу на сонячні батареї супутників.'
      }
    ]
  },
  'ncuvkz-gnss-geodesy-auditor': {
    titleUk: 'Аналітик Систем GNSS & Геодезії',
    titleEn: 'GNSS & Geodetic Auditor',
    crewName: 'Аналітик Систем GNSS та Геодезії',
    icon: Compass,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля геодезії та супутникової навігації...',
      'Обробка RINEX файлів базових станцій GNSS (GPS/Galileo)...',
      'Детекція та локалізація хибної навігації (Spoofing / Jamming)...',
      'Високоточний розрахунок зміщень земної кори...',
      'Формування підсумкового Звіту Оцінки Навігаційного Поля...'
    ],
    loadingStepsEn: [
      'Initializing Differential GNSS & Geodetic AI crew...',
      'Parsing RINEX ground station network files...',
      'Detecting GPS/Galileo spoofing & jamming anomalies...',
      'Computing high-precision geodetic crustal shifts...',
      'Generating final GNSS Integrity & Geodetic Audit Report...'
    ],
    presetsUk: [
      {
        title: 'Мережа диференційних станцій GNSS (CORS України)',
        type: 'Детекція GPS спуфінгу та геодезичний контроль зміщень',
        region: 'Національна мережа CORS станцій України',
        timeframe: 'Моніторинг 2026 р.',
        sources: 'Базові станції GNSS (GPS L1/L2/L5, Galileo E1/E5a, BeiDou-3)',
        context: 'Фіксація аномальних зсувів координат, перевірка багатошляховості сигналів та виявлення локальних генераторів джамінгу.'
      }
    ]
  },
  'ncuvkz-spacecraft-testing-engineer': {
    titleUk: 'Інженер Випробувань & Сертифікації Космічних Засобів',
    titleEn: 'Spacecraft Environmental Testing Engineer',
    crewName: 'Інженер Випробувань та Сертифікації Космічних Засобів',
    icon: CheckSquare,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля випробувального центру...',
      'Складання програми термовакуумних та вібраційних випробувань...',
      'Аудит відповідності космічним стандартам ECSS / ISO 14303...',
      'Оцінка профілів динамічних навантажень виведення...',
      'Формування Сертифікаційного Акта готовності апарата до пуску...'
    ],
    loadingStepsEn: [
      'Initializing Spacecraft Qualification & Testing AI crew...',
      'Drafting thermal-vacuum & vibration test programs...',
      'Auditing compliance against ECSS / ISO 14303 space standards...',
      'Evaluating launch dynamic acoustic & shock load profiles...',
      'Generating final Flight Qualification Certificate...'
    ],
    presetsUk: [
      {
        title: 'Термовакуумні та вібродинамічні випробування CubeSat 6U',
        type: 'Кваліфікаційний аудит ECSS-E-ST-10-03C & ISO 14303',
        region: 'Випробувальний комплекс космічних апаратів НЦУВКЗ',
        timeframe: 'Передпускові випробування 2026 р.',
        sources: 'Термовакуумна камера ТВК-2 та вібростенд LDS V875',
        context: 'Перевірка працездатності підсистем апарата у діапазоні температур -40°C...+80°C (10^-5 Па) та під час вібронавантажень виведення ракети-носія.'
      }
    ]
  },
  'gcsk-seismic-auditor': {
    titleUk: 'Аналітик Сейсмічного Моніторингу & Ядерного Контролю (CTBTO)',
    titleEn: 'Seismic & CTBTO Nuclear Test Auditor',
    crewName: 'Аналітик Сейсмічного Моніторингу та Ядерного Контролю (CTBTO)',
    icon: Activity,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля сейсмічного моніторингу та CTBTO...',
      'Дешифрування сейсмограм (P та S хвиль) станцій моніторингу...',
      'Розрізнення природних землетрусів від індустріальних та ядерних вибухів...',
      'Розрахунок епіцентру, глибини гіпоцентру та магнітуди Mb/Ms...',
      'Формування підсумкового Сейсмологічного Бюлетеня ОДВЗЯВ...'
    ],
    loadingStepsEn: [
      'Initializing CTBTO seismic monitoring AI crew...',
      'Parsing P & S wave seismograms from station network...',
      'Discriminating earthquakes from industrial & nuclear explosions...',
      'Computing epicenter coordinates, depth & Mb/Ms magnitudes...',
      'Generating final CTBTO National Data Center Seismic Bulletin...'
    ],
    presetsUk: [
      {
        title: 'Моніторинг сейсмічних поштовхів та вибухів',
        type: 'Класифікація події та верифікація норм ОДВЗЯВ (CTBTO)',
        region: 'Національна мережа сейсмостанцій',
        timeframe: 'Чергування 2026 р.',
        sources: 'Сейсмометричні датчики + CTBTO NDC',
        context: 'Автоматичне розрізнення техногенного вибуху кар’єру від підземного сейсмічного поштовху та ядерного тесту.'
      }
    ]
  },
  'gcsk-acoustic-infrasound-analyst': {
    titleUk: 'Аналітик Інфразвукового & Акустичного Спецконтролю',
    titleEn: 'Infrasound & Acoustic Tracking Analyst',
    crewName: 'Аналітик Інфразвукового та Акустичного Спецконтролю',
    icon: Volume2,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля інфразвукового спецконтролю...',
      'Опрацювання мікробарограм та детекція низькочастотних хвиль...',
      'Пеленгація азимуту ударної хвилі та пусків ракет...',
      'Триангуляція графічних координат джерела вибуху...',
      'Формування підсумкового Акустичного Звіту...'
    ],
    loadingStepsEn: [
      'Initializing infrasound tracking AI crew...',
      'Parsing microbarograph feeds & detecting low-frequency waves...',
      'Calculating shockwave azimuth & missile launch signatures...',
      'Triangulating blast origin geographical coordinates...',
      'Generating final Atmospheric Infrasound Audit Report...'
    ],
    presetsUk: [
      {
        title: 'Пеленгація пусків ракет та атмосферних вибухів',
        type: 'Інфразвуковий аналіз хвиль та триангуляція',
        region: 'Повітряний простір України та прилеглі зони',
        timeframe: 'Оперативний моніторинг 2026 р.',
        sources: 'Мережа мікробарографів та акустичних датчиків',
        context: 'Фіксація азимуту інфразвукового фронту та розрахунок тротилового еквіваленту вибуху.'
      }
    ]
  },
  'gcsk-radiation-chemical-monitor': {
    titleUk: 'Аналітик Радіаційного & Екологічного Спецконтролю',
    titleEn: 'Radiation & Environmental Fallout Auditor',
    crewName: 'Аналітик Радіаційного та Екологічного Спецконтролю',
    icon: ShieldAlert,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля радіаційної безпеки...',
      'Моніторинг гамма-фону на постах спостереження...',
      'Спектрометрія радіонуклідів у повітрі (Cs-137, I-131, Xe-133)...',
      'Моделювання перенесення радіоактивної хмари...',
      'Формування підсумкового Радіаційного Акта...'
    ],
    loadingStepsEn: [
      'Initializing radiation safety AI crew...',
      'Monitoring gamma dose rates across ground observation posts...',
      'Analyzing airborne radionuclide spectra (Cs-137, I-131, Xe-133)...',
      'Simulating atmospheric radioactive fallout dispersion plume...',
      'Generating final Nuclear Radiation Safety Incident Report...'
    ],
    presetsUk: [
      {
        title: 'Безперервний моніторинг гамма-фону та АЕС',
        type: 'Радіоізотопний аналіз та симуляція радіоактивної хмари',
        region: 'Зона спостереження АЕС України / Наземні пости',
        timeframe: 'Моніторинг 2026 р.',
        sources: 'Дозиметричні та спектрометричні комплекси',
        context: 'Оцінка відхилень від природного радіаційного фону та виявлення техногенного Йоду-131 чи Цезію-137.'
      }
    ]
  },
  'gcsk-geomagnetic-seismoacoustic-analyst': {
    titleUk: 'Аналітик Магнітометрії & Геофізичного Моніторингу',
    titleEn: 'Geomagnetic & Geophysical Explorer',
    crewName: 'Аналітик Магнітометрії та Геофізичного Моніторингу',
    icon: Compass,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля геофізичного напрямку...',
      'Обробка магнітограм варіаційних станцій та аномалій...',
      'Аналіз даних антарктичної станції "Академік Вернадський"...',
      'Детекція електромагнітних провісників землетрусів...',
      'Формування підсумкового Геофізичного Звіту...'
    ],
    loadingStepsEn: [
      'Initializing geophysical & geomagnetic AI crew...',
      'Processing station magnetograms & field anomaly shifts...',
      'Analyzing Academician Vernadsky Antarctic Station telemetry...',
      'Detecting electromagnetic earthquake precursor signatures...',
      'Generating final Geophysical & Magnetometry Audit Report...'
    ],
    presetsUk: [
      {
        title: 'Геомагнітні варіації та дані ст. "Академік Вернадський"',
        type: 'Аудит геофізичних аномалій та провісників землетрусів',
        region: 'Україна / Антарктична станція "Академік Вернадський"',
        timeframe: 'Спостереження 2026 р.',
        sources: 'Магнітометричні та геофізичні станції спостереження',
        context: 'Пошук літосферно-іоносферних кореляцій та електромагнітних пульсацій перед сейсмічними подіями.'
      }
    ]
  },
  'gcsk-emergency-situation-notifier': {
    titleUk: 'Аналітик Оперативного Сповіщення про Техногенні & Природні НС',
    titleEn: 'Emergency Incident Warning & State Executive Briefing Analyst',
    crewName: 'Аналітик Оперативного Сповіщення про Техногенні та Природні НС',
    icon: AlertTriangle,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля оперативного реагування...',
      'Класифікація зареєстрованих сейсмічних, акустичних та радіаційних НС...',
      'Розрахунок радіуса враження та масштабів руйнувань...',
      'Формування термінових рекомендацій для ДСНС та ЗСУ...',
      'Складання підсумкової Екстреної Довідки для ДСНС та РНБО...'
    ],
    loadingStepsEn: [
      'Initializing rapid emergency notification AI crew...',
      'Classifying registered seismic, acoustic & radiation emergencies...',
      'Estimating impact blast radius & infrastructural damage scales...',
      'Formulating actionable alerts for SESU & Armed Forces...',
      'Drafting final Urgent Executive Briefing for President Office & NSDC...'
    ],
    presetsUk: [
      {
        title: 'Екстрена оперативна довідка про геофізичну НС',
        type: 'Класифікація події та бюлетень для керівництва держави',
        region: 'Зона виникнення надзвичайної події',
        timeframe: 'Негайне інформування (2026 р.)',
        sources: 'Оперативно-черговий центр сповіщення',
        context: 'Формування оперативно-інформаційного документа для Офісу Президента, ДСНС, РНБО та Генерального штабу ЗСУ.'
      }
    ]
  },
  'knp-gnss-integrity-auditor': {
    titleUk: 'Аналітик Цілісності Навігаційних Полів (GPS / Galileo / BeiDou)',
    titleEn: 'GNSS Navigation Field Integrity Auditor',
    crewName: 'Аналітик Цілісності Навігаційних Полів (GPS / Galileo / BeiDou)',
    icon: Navigation,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля аудиту цілісності GNSS...',
      'Аналіз сигналу сузір’їв GPS, Galileo та BeiDou...',
      'Обчислення похибок ефемерид та дрейфу бортових атомних годинників...',
      'Розрахунок еквівалентної похибки дальності (UERE / RAIM)...',
      'Формування підсумкового Звіту Цілісності Навігаційного Поля...'
    ],
    loadingStepsEn: [
      'Initializing GNSS integrity auditing AI crew...',
      'Parsing GPS, Galileo & BeiDou multi-constellation telemetry...',
      'Computing ephemeris error & satellite atomic clock offsets...',
      'Calculating User Equivalent Range Error (UERE) & RAIM metrics...',
      'Generating final GNSS Navigation Field Integrity Report...'
    ],
    presetsUk: [
      {
        title: 'Аудит цілісності сузір’їв GPS, Galileo та BeiDou-3',
        type: 'Моніторинг похибки UERE та цілісності RAIM',
        region: 'Повітряний простір України (Національний рівень)',
        timeframe: 'Цілодобове чергування 2026 р.',
        sources: 'Контрольно-вимірювальний комплекс КНП (Київ / Дніпро)',
        context: 'Перевірка стабільності орбітальних ефемерид, дрейфу атомних годинників та завчасне виявлення збоїв супутників GNSS.'
      }
    ]
  },
  'knp-rtk-diff-correction-operator': {
    titleUk: 'Оператор Мережі Станцій RTK & Диференційної Корекції',
    titleEn: 'RTK Station Network & Differential Correction Operator',
    crewName: 'Оператор Мережі Станцій RTK та Диференційної Корекції',
    icon: MapPin,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля мережі диференційної корекції...',
      'Моніторинг працездатності референцних станцій ККС...',
      'Аудит затримок NTRIP/RTK потоків для сантиметрової точності...',
      'Валідація та генерація RINEX файлів післясесійного аналізу...',
      'Формування підсумкового Технічного Звіту Мережі ККС...'
    ],
    loadingStepsEn: [
      'Initializing reference station network AI crew...',
      'Tracking reference station (ККС) operational readiness...',
      'Auditing NTRIP/RTK stream latency & centimetric positioning...',
      'Validating & building post-processing RINEX datasets...',
      'Generating final Reference Station Network Technical Report...'
    ],
    presetsUk: [
      {
        title: 'Мережа референцних станцій ККС (NTRIP/RTK)',
        type: 'NTRIP/RTK диференційна корекція сантиметрової точності',
        region: 'Національна мережа базових станцій ККС',
        timeframe: 'Моніторинг 2026 р.',
        sources: 'Базові станції ККС (NTRIP Caster, RTCM 3.3)',
        context: 'Забезпечення сантиметрової точності позиціонування для кадастру, агромоніторингу та інженерно-геодезичних вишукувань.'
      }
    ]
  },
  'knp-time-frequency-metrologist': {
    titleUk: 'Спеціаліст Метрології Єдиного Часу & Частоти UTC(UA)',
    titleEn: 'National Time & Frequency Metrology Specialist UTC(UA)',
    crewName: 'Спеціаліст Метрології Єдиного Часу та Частоти UTC(UA)',
    icon: Clock,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля метрології часу та частоти...',
      'Синхронізація шкали часу UTC(UA) за атомними еталонами...',
      'Вимірювання варіації Алана та добового дрейфу частоти...',
      'Аудит часового розсинхрону для ЗСУ, НБУ та зв’язку...',
      'Формування Офіційного Метрологічного Бюлетеня Часу...'
    ],
    loadingStepsEn: [
      'Initializing time & frequency metrology AI crew...',
      'Synchronizing national UTC(UA) time scale via atomic standards...',
      'Measuring Allan deviation & daily frequency drift...',
      'Auditing clock offsets for defense, banking & telecom networks...',
      'Generating final National Time & Frequency Metrology Bulletin...'
    ],
    presetsUk: [
      {
        title: 'Державний еталонний комплекс часу та частоти UTC(UA)',
        type: 'Синхронізація шкали часу та контроль варіації Алана',
        region: 'Державний первинний еталон одиниць часу і частоти (ННЦ "Інститут метрології")',
        timeframe: 'Безперервний метрологічний контроль 2026 р.',
        sources: 'Водневі мазери та цезієві генератори часу UTC(UA)',
        context: 'Аудит наносекундної розсинхронізації часових серверів NTP/PTP для критичної банківської інфраструктури, енергомереж та ЗСУ.'
      }
    ]
  },
  'knp-antijamming-antispoofing-analyst': {
    titleUk: 'Аналітик Радіонавігаційного Захисту & Спуфінгу/Джамінгу',
    titleEn: 'Navigation Anti-Jamming & Anti-Spoofing Operations Analyst',
    crewName: 'Аналітик Радіонавігаційного Захисту та Спуфінгу/Джамінгу',
    icon: ShieldAlert,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля радіонавігаційного кіберзахисту...',
      'Детекція сигнатур GNSS спуфінгу та блокування частот...',
      'Пеленгація координат комплексів РЕБ противника...',
      'Побудова карти завадової обстановки навігаційного поля...',
      'Формування Звіту Завадостійкості та Захисту Інфраструктури...'
    ],
    loadingStepsEn: [
      'Initializing navigation anti-jamming cyber defense AI crew...',
      'Detecting GNSS spoofing signatures & frequency jamming...',
      'Geolocating hostile EW jammer transmitter coordinates...',
      'Mapping navigation signal degradation & blind spots...',
      'Generating final Navigation Cyber Defense & Resilience Report...'
    ],
    presetsUk: [
      {
        title: 'Детекція сигнатур РЕБ спуфінгу та джамінгу навігаційного поля',
        type: 'Пеленгація передавачів завад та розрахунок зон придушення',
        region: 'Зони збурень GNSS та сектори відповідальності Сил Оборони',
        timeframe: 'Оперативний аналіз 2026 р.',
        sources: 'Спектроаналізатори, станції радіомоніторингу UCRF та приймачі з CRPA антенами',
        context: 'Виявлення генераторів хибних координатних полів, перехоплення фазових зсувів L1/L2 та розрахунок завадозахищених частот.'
      }
    ]
  },
  'knp-defense-navigation-briefing-analyst': {
    titleUk: 'Аналітик Навігаційного Забезпечення Сил Оборони',
    titleEn: 'Military Navigation Field Readiness & Precision Targeting Analyst',
    crewName: 'Аналітик Навігаційного Забезпечення Сил Оборони',
    icon: Crosshair,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля навігаційного забезпечення Сил Оборони...',
      'Розрахунок геометричних факторів GDOP, PDOP, HDOP...',
      'Прогнозування часових вікон найвищої точності позиціонування...',
      'Формування рекомендацій для високоточного озброєння та БПЛА...',
      'Складання підсумкового Навігаційного Бюлетеня для ЗСУ...'
    ],
    loadingStepsEn: [
      'Initializing military navigation readiness AI crew...',
      'Computing spatial-temporal GDOP, PDOP & HDOP error maps...',
      'Forecasting precision targeting window availability...',
      'Building operational guidance for UAV & strike weapon systems...',
      'Drafting final Operational Navigation Readiness Briefing for Armed Forces...'
    ],
    presetsUk: [
      {
        title: 'Розрахунок GDOP/PDOP та вікон точності для ударних засобів та БПЛА',
        type: 'Оперативний навігаційний бюлетень для Генерального Штабу ЗСУ',
        region: 'Операційні райони виконання завдань (Східний та Південний сектори)',
        timeframe: 'Добовий прогноз вікон супутникової доступності 2026 р.',
        sources: 'Аналітичний центр навігаційного забезпечення КНП + Сузір’я GPS/Galileo',
        context: 'Визначення часових інтервалів мінімального геометричного фактора згасання точності (PDOP < 2.0) для застосування високоточної зброї та безпілотників.'
      }
    ]
  },
  'war-damage-sat-auditor': {
    titleUk: 'Оцінка Збитків від Воєнних Дій в Україні (ДЗЗ)',
    titleEn: 'War Damage & Destruction Remote Sensing Auditor',
    crewName: 'Супутниковий моніторинг та оцінка збитків від воєнних дій',
    icon: ShieldAlert,
    loadingStepsUk: [
      'Ініціалізація аналітичного модуля воєнно-космічного моніторингу...',
      'Обробка довоєнних та поточних знімків Sentinel-1 SAR / Sentinel-2 / PlanetScope...',
      'Проведення Change Detection аналізу та детекції кратерів/руйнувань...',
      'Розрахунок матеріальних збитків в UAH/USD за ПКМУ № 326 та RDNA3 Світового банку...',
      'Формування юридичного доказового пакета для Реєстру Збитків у Гаазі (RD4U)...'
    ],
    loadingStepsEn: [
      'Initializing war damage remote sensing AI crew...',
      'Processing pre-war vs post-war Sentinel-1 SAR & PlanetScope satellite imagery...',
      'Performing spatial Change Detection & impact crater analysis...',
      'Calculating economic loss in UAH/USD per CMU No. 326 & World Bank RDNA3 standards...',
      'Building evidence package for the Hague Register of Damage for Ukraine (RD4U)...'
    ],
    presetsUk: [
      {
        title: 'Оцінка руйнувань житлового масиву / енергооб\'єкта',
        type: 'Супутниковий Change Detection та оцінка збитків майна',
        region: 'м. Харків / Запоріжжя / Донецька область',
        timeframe: 'Довоєнний базис 2021 р. vs Поточний проліт 2026 р.',
        sources: 'Sentinel-1 SAR + Sentinel-2 + PlanetScope (3m)',
        context: 'Оцінка площі руйнувань, визначення рівня деструкції (Level 1-4), розрахунок збитків за ПКМУ № 326 та відбудови в USD для Реєстру Гааги.'
      },
      {
        title: 'Аналіз ураження агроугідь та розмінування ґрунтів',
        type: 'Моніторинг воєнних кратерів та деградації сільгоспземель',
        region: 'Херсонська / Миколаївська область',
        timeframe: 'Період бойових дій (2022-2026 рр.)',
        sources: 'Sentinel-2 MSI + ICEYE High-Res SAR',
        context: 'Детекція вибухових аномалій, оцінка втрати родючого шару, вартості рекультивації та розмінування.'
      }
    ]
  },
  'sat-image-harvester': {
    titleUk: 'Агент-Завантажувач & Архіватор Знімків (EO Data Harvester)',
    titleEn: 'EO Data Harvester & Downloader Agent',
    crewName: 'Агент-Завантажувач та Архіватор Супутникових Знімків',
    icon: Download,
    loadingStepsUk: [
      'Ініціалізація ШІ-агента автоматичного завантаження та архівації ДЗЗ...',
      'Підключення до безкоштовних (Sentinel/Landsat) та комерційних (UP42/Maxar/Planet) STAC каталогів...',
      'Фільтрація та викачування кадрів за вказаним BBOX, датами та хмарністю...',
      'Розрахунок вартості (для платних знімків з націнкою 33% + Monobank)...',
      'Формування пакету завантажених знімків та Офіційного Акту-Бюлетеня за ДСТУ 4163:2020...'
    ],
    loadingStepsEn: [
      'Initializing EO Data Harvester & Archiver AI Agent...',
      'Querying free (Sentinel/Landsat) and paid (UP42/Maxar/Planet) STAC endpoints...',
      'Downloading satellite frames matching target BBOX, dates & cloud threshold...',
      'Computing Monobank sell rate & 33% margin pricing for commercial imagery...',
      'Packaging downloaded assets and generating official DSTU 4163:2020 Audit Brief...'
    ],
    presetsUk: [
      {
        title: 'Безкоштовний архів Sentinel-2 & Landsat 9 (Київська обл.)',
        type: 'Викачування безкоштовних відкритих даних (0 грн)',
        region: 'м. Київ, 50.45° N, 30.52° E',
        timeframe: '2026-01-01 – 2026-08-01',
        sources: 'Sentinel-2 MSI (10m) + Landsat 9 OLI',
        context: 'Автоматичний пошук, викачування та локальна архівація відкритих 10m мультиспектральних знімків.'
      },
      {
        title: 'Комерційні знімки надвисокої роздільності Maxar / SkySat',
        type: 'Викачування платного VHR архіву (з націнкою 33% та курсом Monobank)',
        region: 'Одеський порт / Одеська затока',
        timeframe: '2026-06-01 – 2026-08-01',
        sources: 'WorldView-3 / GeoEye (0.3m) + SkySat (0.5m)',
        context: 'Пошук у каталозі UP42, розрахунок кошторису в доларах та гривнях, завантаження VHR кадрів.'
      }
    ]
  },
  'sat-thematic-processor': {
    titleUk: 'Агент Тематичної Обробки та Дешифрування Знімків',
    titleEn: 'Satellite Image Thematic Processing & Deciphering Agent',
    icon: Layers,
    badgeUk: 'Gemini 3.7 Flash & Vision • GeoTIFF / PNG / JPG • ДСТУ 4163:2020',
    badgeEn: 'Gemini 3.7 Flash & Vision • GeoTIFF / PNG / JPG • DSTU 4163:2020',
    descUk: 'ШІ-дешифрувальник супутникових та аерофотознімків із ПК чи смартфона. Дозволяє завантажувати знімок з комп’ютера, обирати вид тематичного аналізу (NDVI, NBR, NDBI, RDNA3, SAR) та будувати дешифрувальний звіт із завантаженням Word-файла за ДСТУ 4163:2020.',
    descEn: 'AI decipherer for local satellite & aerial photo upload. Allows choosing thematic analysis types (NDVI, NBR, NDBI, RDNA3, SAR) and exporting DSTU 4163:2020 Word reports.',
    crewName: 'satellite_thematic_processor_crew',
    presetsUk: [
      {
        title: 'Комплексне дешифрування знімка з пристрою',
        type: 'Комплексна авто-обробка',
        region: 'Локальний файл зображення',
        timeframe: 'Поточна зйомка',
        sources: 'GeoTIFF / PNG / JPG / WebP',
        context: 'дешифрування супутникових кадрів та побудова акту за ДСТУ 4163:2020.'
      }
    ],
    presetsEn: [
      {
        title: 'Local Satellite Image Thematic Audit',
        type: 'Complex Auto Processing',
        region: 'Local Image File',
        timeframe: 'Current Acquisition',
        sources: 'GeoTIFF / PNG / JPG / WebP',
        context: 'AI satellite image deciphering and DSTU 4163:2020 reporting.'
      }
    ],
    loadingStepsUk: [
      'Завантаження супутникового знімка та матеріалів користувача...',
      'Зчитування метаданих та передача зображення у мультимодальне ядро Gemini 3.7 Flash & Vision...',
      'Опрацювання супровідних нормативно-технічних документів та гео-даних...',
      'Проведення тематичного аналізу (NDVI, NBR, NDBI, RDNA3, SAR)...',
      'Формування підсумкового геоінформаційного звіту за ДСТУ 4163:2020...'
    ],
    loadingStepsEn: [
      'Uploading satellite image and user materials...',
      'Parsing metadata & passing image to Gemini 3.7 Flash & Vision multimodal engine...',
      'Extracting text from supporting technical documents & geo-data...',
      'Performing thematic processing (NDVI, NBR, NDBI, RDNA3, SAR)...',
      'Building final geospatial analysis report per DSTU 4163:2020...'
    ]
  },
  'sat-drought-water-stress-auditor': {
    titleUk: 'Аналітик Супутникового Водного Стресу та Ризиків Посухи (NDMI + SPEI-3)',
    titleEn: 'Satellite Drought & Water Stress Auditor (NDMI + SPEI-3)',
    icon: Droplets,
    badgeUk: 'Sentinel-2 L2A (B08A/B11) • TerraClimate (4km) • ДСТУ 4163:2020',
    badgeEn: 'Sentinel-2 L2A (B08A/B11) • TerraClimate (4km) • DSTU 4163:2020',
    descUk: 'Комплексний супутниково-кліматичний моніторинг водно-екологічного стресу, розрахунок індексів NDMI та SPEI-3, 2-шарова модель ґрунтової посухи Палмера (PDSI) та прогнозування маловоддя річок з генерацією офіційного звіту .docx за ДСТУ 4163:2020.',
    descEn: 'Comprehensive satellite-climatic monitoring of water stress, computation of NDMI and SPEI-3 indices, 2-layer Palmer drought model (PDSI), and riverbed dehydration forecasting with DSTU 4163:2020 Word report.',
    crewName: 'satellite_drought_water_stress_crew',
    presetsUk: [
      {
        title: 'Дністровське водосховище (Бакота: 5 та 20 км)',
        type: 'Моніторинг обміління та динаміки водної поверхні (NDWI/MNDWI)',
        region: 'Хмельницька обл., Кам\'янець-Подільський р-н, Бакотська затока (48.603° N, 26.978° E)',
        timeframe: 'Вересень 2026 р. (порівняно з вереснем 2018 р.)',
        sources: 'Sentinel-2 L2A (B03, B08, B11, B12: 12-8-4) + Copernicus Academy методики',
        context: 'Критичне обміління водойми, дефіцит водності Дністра (11% від норми), відступ водної лінії на 311 га у радіусі 5 км та на 8,95 км² у зоні 20 км, вплив скидів Дністровської ГЕС.'
      },
      {
        title: 'Басейн річки Рось (м. Біла Церква)',
        type: 'Оперативний моніторинг посухи та дефіциту вологи',
        region: 'Київська обл., м. Біла Церква, басейн р. Рось (49.79° N, 30.11° E)',
        timeframe: 'Червень – Серпень 2024 р. (порівняно з 2021 р.)',
        sources: 'Sentinel-2 L2A (B08A, B11) + TerraClimate (SPEI-3, PDSI)',
        context: 'Оцінка ризиків зневоднення екосистеми, падіння тургору біомаси та загрози дефіциту питного водопостачання міста.'
      }
    ],
    presetsEn: [
      {
        title: 'Dniester Reservoir (Bakota: 5 & 20 km)',
        type: 'Reservoir Shrinkage & Water Line Dynamics (NDWI/MNDWI)',
        region: 'Khmelnytskyi Oblast, Bakota Bay, Dniester Reservoir (48.603° N, 26.978° E)',
        timeframe: 'September 2026 (vs. September 2018)',
        sources: 'Sentinel-2 L2A (B03, B08, B11, B12: 12-8-4) + Copernicus Academy methodology',
        context: 'Critical reservoir depletion, Dniester inflow drop to 11% norm, water line retreat by 311 ha (5 km) and 8.95 sq.km (20 km), Dniester HPP hydrological discharge regulation impact.'
      },
      {
        title: 'Ros River Basin (Bila Tserkva)',
        type: 'Operational Drought & Water Stress Monitoring',
        region: 'Kyiv Oblast, Bila Tserkva, Ros River Basin (49.79° N, 30.11° E)',
        timeframe: 'June – August 2024 (vs. 2021)',
        sources: 'Sentinel-2 L2A (B08A, B11) + TerraClimate (SPEI-3, PDSI)',
        context: 'Assessment of ecosystem dehydration risks, vegetation turgor collapse, and municipal drinking water supply deficit.'
      }
    ],
    loadingStepsUk: [
      'Запит супутникових знімків Sentinel-2 L2A (BOA) через STAC API...',
      'Вилучення каналів B08A (865 нм) та B11 SWIR (1610 нм) для розрахунку NDMI...',
      'Агрегація кліматичного реаналізу TerraClimate (4 км) та розрахунок SPEI-3...',
      'Оцінка за 2-шаровою гідрологічною моделлю Палмера (PDSI)...',
      'Генерація аудиторського висновку та офіційного Акта за ДСТУ 4163:2020...'
    ],
    loadingStepsEn: [
      'Querying Sentinel-2 L2A BOA satellite scenes via STAC API...',
      'Extracting B08A (865 nm) and B11 SWIR (1610 nm) bands for NDMI...',
      'Aggregating TerraClimate (4 km) climate reanalysis and calculating SPEI-3...',
      'Evaluating 2-layer Palmer hydrological drought severity model (PDSI)...',
      'Generating audit summary and official DSTU 4163:2020 Word report...'
    ]
  }
};

function getSatelliteSourceOptions(agentId: string) {
  const gnssAgents = [
    'ncuvkz-gnss-geodesy-auditor',
    'knp-gnss-integrity-auditor',
    'knp-rtk-diff-correction-operator',
    'knp-time-frequency-metrologist',
    'knp-antijamming-antispoofing-analyst',
    'knp-defense-navigation-briefing-analyst'
  ];

  const seismicAgents = [
    'gcsk-seismic-auditor',
    'gcsk-acoustic-infrasound-analyst',
    'gcsk-radiation-chemical-monitor',
    'gcsk-geomagnetic-seismoacoustic-analyst',
    'gcsk-emergency-situation-notifier'
  ];

  const radioSpectrumAgents = [
    'space-spectrum-consultant',
    'ncuvkz-space-weather-monitor'
  ];

  const sdaMissionAgents = [
    'ncuvkz-mission-control',
    'ncuvkz-sda-debris-tracker',
    'ncuvkz-spacecraft-testing-engineer',
    'satellite-payload-engineer',
    'newspace-investment-analyst'
  ];

  if (gnssAgents.includes(agentId)) {
    return [
      { labelUk: '📍 Мережа ДГНСС / GPS (L1/L2/L5 + C/A)', labelEn: '📍 DGNSS / GPS Network (L1/L2/L5)', value: 'Мережа ДГНСС / GPS (L1/L2/L5)' },
      { labelUk: '📍 Мережа Galileo (E1/E5a/E5b High Accuracy Service)', labelEn: '📍 Galileo Constellation (E1/E5a/E5b HAS)', value: 'Мережа Galileo (E1/E5a/E5b)' },
      { labelUk: '📍 Сузір’я BeiDou-3 (B1I/B2I/B3I)', labelEn: '📍 BeiDou-3 Constellation (B1I/B2I/B3I)', value: 'Сузір’я BeiDou-3' },
      { labelUk: '📍 Мережа RTK / CORS базових станцій України', labelEn: '📍 National RTK / CORS Base Station Network', value: 'Мережа RTK / CORS станцій' },
      { labelUk: '⏱️ Еталони частоти та часу UTC(UA)', labelEn: '⏱️ UTC(UA) Time & Frequency Standards', value: 'Еталони частоти та часу UTC(UA)' }
    ];
  }

  if (seismicAgents.includes(agentId)) {
    return [
      { labelUk: '🌋 Сейсмічна мережа станцій ГЦКМ / НАН України', labelEn: '🌋 National Seismic Network (NCSK / NASU)', value: 'Сейсмічна мережа ГЦКМ' },
      { labelUk: '🌍 Міжнародна моніторингова система CTBTO (IMS Seismic)', labelEn: '🌍 CTBTO International Monitoring System (IMS)', value: 'Станції CTBTO IMS' },
      { labelUk: '🎤 Інфразвукові станції виявлення аномальних вибухів', labelEn: '🎤 Infrasound Atmospheric Monitoring Stations', value: 'Інфразвукові станції' },
      { labelUk: '☢️ Автоматизовані пости радіаційного контролю (АСКРО)', labelEn: '☢️ Automated Radiation Control System (ASKRO)', value: 'Пости радіаційного моніторингу АСКРО' },
      { labelUk: '🧲 Геомагнітні обсерваторії', labelEn: '🧲 Geomagnetic Observatories', value: 'Геомагнітні обсерваторії' }
    ];
  }

  if (radioSpectrumAgents.includes(agentId)) {
    return [
      { labelUk: '📡 Центр космічного зв’язку та радіоастрономії (РТ-32 Золочів)', labelEn: '📡 Radio Astronomy Center (RT-32 Zolochiv)', value: 'РТ-32 Золочів (C/X/Ku-band)' },
      { labelUk: '📡 Центр далекого космічного зв’язку (РТ-70 Євпаторія)', labelEn: '📡 Deep Space Center (RT-70 Yevpatoria)', value: 'РТ-70 Євпаторія (Дециметровий/Сантиметровий діапазон)' },
      { labelUk: '☀️ Сонячні спектрометри та коронографи (SOHO, SDO, GOES)', labelEn: '☀️ Space Weather Solar Spectrometers (SOHO, SDO, GOES)', value: 'Сонячні спектрометри SOHO/SDO/GOES' },
      { labelUk: '📡 Станції радіомоніторингу космічних частот UCRF', labelEn: '📡 Space Spectrum Monitoring Stations (UCRF)', value: 'Станції радіомоніторингу UCRF' }
    ];
  }

  if (sdaMissionAgents.includes(agentId)) {
    return [
      { labelUk: '🛸 Орбітальні каталоги TLE / Space-Track / CSpOC', labelEn: '🛸 Orbit Catalogs TLE / Space-Track / CSpOC', value: 'Каталоги TLE Space-Track' },
      { labelUk: '🔭 Оптичні телескопи та радарні комплекси SDA', labelEn: '🔭 Optical Telescopes & Radar SDA Complexes', value: 'Комплекси SDA моніторингу космосу' },
      { labelUk: '📡 Наземні станції командно-вимірювальних комплексів (КВК)', labelEn: '📡 Ground Station TT&C Complexes', value: 'Бортова телеметрія & КВК' },
      { labelUk: '🛰️ Бортові сенсори LEO/GEO космічних апаратів', labelEn: '🛰️ Spacecraft LEO/GEO Onboard Sensors', value: 'Бортові сенсори КА' }
    ];
  }

  // DEFAULT FOR ALL EARTH OBSERVATION IMAGERY & HARVESTER AGENTS:
  return [
    { labelUk: '🛰️ Sentinel-2 MSI (10m Мультиспектр, Безкоштовно ESA)', labelEn: '🛰️ Sentinel-2 MSI (10m Multispectral, Free ESA)', value: 'Sentinel-2 MSI (10m Мультиспектр)' },
    { labelUk: '🛰️ Sentinel-1 SAR (10m Всепогодна Радіолокація, Безкоштовно ESA)', labelEn: '🛰️ Sentinel-1 SAR (10m All-weather Radar, Free ESA)', value: 'Sentinel-1 SAR (10m Радіолокація)' },
    { labelUk: '🛰️ Landsat 8/9 OLI (15m-30m Мультиспектр, Безкоштовно NASA/USGS)', labelEn: '🛰️ Landsat 8/9 OLI (15m-30m, Free NASA/USGS)', value: 'Landsat 8/9 OLI (15m-30m NASA)' },
    { labelUk: '💎 Maxar WorldView-3 / GeoEye-1 (0.3m Надвисока роздільна здатність, Платний VHR)', labelEn: '💎 Maxar WorldView-3 / GeoEye-1 (0.3m High Res, Paid VHR)', value: 'WorldView-3 / GeoEye (0.3m VHR)' },
    { labelUk: '💎 SkySat / Airbus Pléiades Neo (0.3m-0.5m Оптична зйомка, Платний VHR)', labelEn: '💎 SkySat / Airbus Pléiades Neo (0.3m-0.5m High Res, Paid VHR)', value: 'SkySat / Pléiades Neo (0.3m-0.5m VHR)' },
    { labelUk: '💎 PlanetScope SuperDove (3m Щоденне супутникове покриття, Платний)', labelEn: '💎 PlanetScope SuperDove (3m Daily Constellation, Paid)', value: 'PlanetScope SuperDove (3m Daily)' },
    { labelUk: '💎 ICEYE / Capella Space High-Res SAR (0.5m Радіолокація, Платний)', labelEn: '💎 ICEYE / Capella Space High-Res SAR (0.5m Radar, Paid)', value: 'ICEYE / Capella High-Res SAR (0.5m)' }
  ];
}

function getMonitoringTypeOptions(agentId: string) {
  const gnssAgents = [
    'ncuvkz-gnss-geodesy-auditor',
    'knp-gnss-integrity-auditor',
    'knp-rtk-diff-correction-operator',
    'knp-time-frequency-metrologist',
    'knp-antijamming-antispoofing-analyst',
    'knp-defense-navigation-briefing-analyst'
  ];

  const seismicAgents = [
    'gcsk-seismic-auditor',
    'gcsk-acoustic-infrasound-analyst',
    'gcsk-radiation-chemical-monitor',
    'gcsk-geomagnetic-seismoacoustic-analyst',
    'gcsk-emergency-situation-notifier'
  ];

  const spaceTechAgents = [
    'space-spectrum-consultant',
    'ncuvkz-space-weather-monitor',
    'ncuvkz-mission-control',
    'ncuvkz-sda-debris-tracker',
    'ncuvkz-spacecraft-testing-engineer',
    'satellite-payload-engineer',
    'newspace-investment-analyst'
  ];

  if (gnssAgents.includes(agentId)) {
    return [
      { labelUk: '📍 Контроль завад GNSS, Спуфінг & Джамінг (РЕБ)', labelEn: '📍 DGNSS Interference, Spoofing & Jamming Audit', value: 'Контроль завад GNSS, Спуфінг & Джамінг (РЕБ)' },
      { labelUk: '⏱️ Метрологія єдиного часу UTC(UA) & Синхронізація', labelEn: '⏱️ UTC(UA) Time & Frequency Metrology Audit', value: 'Метрологія єдиного часу UTC(UA) & Синхронізація' },
      { labelUk: '📡 Перевірка цілісності ефемерид та похибки UERE/RAIM', labelEn: '📡 Ephemeris Integrity & UERE/RAIM Verification', value: 'Перевірка цілісності ефемерид та похибки UERE/RAIM' },
      { labelUk: '🛰️ Моніторинг геодезичних зсувів земної кори', labelEn: '🛰️ Geodetic Crustal Displacement Monitoring', value: 'Моніторинг геодезичних зсувів земної кори' }
    ];
  }

  if (seismicAgents.includes(agentId)) {
    return [
      { labelUk: '🌋 Сейсмічна вибірка P/S хвиль & Вибухи (CTBTO)', labelEn: '🌋 Seismic P/S Wave Discrimination (CTBTO)', value: 'Сейсмічна вибірка P/S хвиль & Вибухи (CTBTO)' },
      { labelUk: '🎤 Інфразвуковий аналіз атмосфери та аномальних сигнатур', labelEn: '🎤 Infrasound Atmospheric Signature Analysis', value: 'Інфразвуковий аналіз атмосфери' },
      { labelUk: '☢️ Моніторинг гамма-фону та радіонуклідного випаду', labelEn: '☢️ Gamma Background & Radionuclide Fall-out Monitoring', value: 'Моніторинг гамма-фону та радіонуклідного випаду' }
    ];
  }

  if (spaceTechAgents.includes(agentId)) {
    return [
      { labelUk: '🛸 Орбітальний контроль & Зближення (SDA, TLE, PoC)', labelEn: '🛸 Orbit Assessment & Collision Avoidance (SDA, TLE)', value: 'Орбітальний контроль & Зближення (SDA, TLE, PoC)' },
      { labelUk: '☀️ Аналіз сонячних спалахів, Kp-індексу та геомагнітних бурь', labelEn: '☀️ Solar Flare, Kp-index & Geomagnetic Storm Analysis', value: 'Моніторинг сонячної спалахової активності' },
      { labelUk: '📡 Аудит радіочастотного спектра та завад C/X/Ku-band', labelEn: '📡 Radio Spectrum & C/X/Ku-band Interference Audit', value: 'Аудит радіочастотного спектра C/X/Ku-band' }
    ];
  }

  // DEFAULT FOR EARTH OBSERVATION / IMAGERY AGENTS:
  return [
    { labelUk: '🌿 Сільськогосподарський аналіз та індекси (NDVI, EVI, NDRE)', labelEn: '🌿 Vegetation & Crop Health (NDVI, EVI, NDRE)', value: 'Агромоніторинг & Індекси росту (NDVI, EVI, NDRE)' },
    { labelUk: '💧 Водний баланс & Гідрологія (MNDWI, NDWI)', labelEn: '💧 Water Resources & Hydrology (MNDWI, NDWI)', value: 'Водний баланс & Гідрологія (MNDWI, NDWI)' },
    { labelUk: '🔥 Виявлення пожеж & Гару (NBR, dNBR, Thermal)', labelEn: '🔥 Wildfire & Burned Area Detection (NBR, dNBR)', value: 'Виявлення пожеж & Гару (NBR, dNBR, Thermal)' },
    { labelUk: '🏗️ Урбаністичний розвиток & Зсуви (InSAR, NDBI)', labelEn: '🏗️ Urban Infrastructure & Subsidence (InSAR, NDBI)', value: 'Урбаністичний розвиток & Зсуви (InSAR, NDBI)' },
    { labelUk: '🌊 Морський моніторинг & Судноплавство (AIS + SAR)', labelEn: '🌊 Maritime Domain & Vessel Tracking (AIS + SAR)', value: 'Морський моніторинг & Судноплавство (AIS + SAR)' },
    { labelUk: '🛡️ Військова розвідка GEOINT & Change Detection', labelEn: '🛡️ Defense GEOINT & Target Change Detection', value: 'Військова розвідка GEOINT & Захист об’єктів' },
    { labelUk: '🌐 Архівація відкритих та комерційних ДЗЗ даних', labelEn: '🌐 Open & Commercial EO Data Archiving', value: 'Архівація відкритих та комерційних ДЗЗ даних' }
  ];
}

function getRegionOptions(agentId: string) {
  const gnssAgents = ['ncuvkz-gnss-geodesy-auditor', 'knp-gnss-integrity-auditor', 'knp-rtk-diff-correction-operator', 'knp-time-frequency-metrologist', 'knp-antijamming-antispoofing-analyst', 'knp-defense-navigation-briefing-analyst'];
  const seismicAgents = ['gcsk-seismic-auditor', 'gcsk-acoustic-infrasound-analyst', 'gcsk-radiation-chemical-monitor', 'gcsk-geomagnetic-seismoacoustic-analyst', 'gcsk-emergency-situation-notifier'];
  const spaceTechAgents = ['space-spectrum-consultant', 'ncuvkz-space-weather-monitor', 'ncuvkz-mission-control', 'ncuvkz-sda-debris-tracker', 'ncuvkz-spacecraft-testing-engineer', 'satellite-payload-engineer', 'newspace-investment-analyst'];

  if (gnssAgents.includes(agentId)) {
    return [
      { labelUk: '📍 Національна мережа GNSS України (CORS)', labelEn: '📍 National GNSS Network of Ukraine (CORS)', value: 'Національна мережа GNSS України' },
      { labelUk: '🏙️ Київська область & Північний операційний район', labelEn: '🏙️ Kyiv Region & Northern Sector', value: 'Київська область & Північний район' },
      { labelUk: '🌊 Чорноморсько-Азовський морський басейн', labelEn: '🌊 Black Sea & Azov Maritime Basin', value: 'Чорноморсько-Азовський басейн' }
    ];
  }

  if (seismicAgents.includes(agentId)) {
    return [
      { labelUk: '🌋 Сейсмоактивна зона Вранча (Карпатський регіон)', labelEn: '🌋 Vrancea Seismic Zone (Carpathian Region)', value: 'Сейсмоактивна зона Вранча (Карпати)' },
      { labelUk: '☢️ Чорнобильська зона відчуження & АЕС України', labelEn: '☢️ Chornobyl Exclusion Zone & NPPs of Ukraine', value: 'Чорнобильська зона & АЕС України' },
      { labelUk: '🏛️ Головний центр спеціального контролю (м. Городок)', labelEn: '🏛️ Main Center of Special Control (Horodok)', value: 'ГЦКМ Городок, Хмельницька обл.' }
    ];
  }

  if (spaceTechAgents.includes(agentId)) {
    return [
      { labelUk: '🚀 Низька навколоземна орбіта (LEO 500–600 км)', labelEn: '🚀 Low Earth Orbit (LEO 500–600 km)', value: 'Низька навколоземна орбіта (LEO 500–600 км)' },
      { labelUk: '🛰️ Геостаціонарна орбіта (GEO 35 786 км)', labelEn: '🛰️ Geostationary Orbit (GEO 35 786 km)', value: 'Геостаціонарна орбіта (GEO 36 000 км)' },
      { labelUk: '📡 Центр космічних досліджень (Золочів, Львівська обл.)', labelEn: '📡 Space Research Center (Zolochiv)', value: 'Золочів, Львівська обл. (РТ-32)' }
    ];
  }

  if (agentId === 'sat-drought-water-stress-auditor') {
    return [
      { labelUk: '🔴 Басейн р. Рось (м. Біла Церква, Київська обл.) — Епіцентр посухи', labelEn: '🔴 Ros River Basin (Bila Tserkva) — Drought Epicenter', value: 'Київська обл., м. Біла Церква, басейн р. Рось (49.79° N, 30.11° E)' },
      { labelUk: '🔴 Басейн р. Дніпро (Середнє та Нижнє Подніпров\'я, Каскад ГЕС)', labelEn: '🔴 Dnipro River Basin (Middle & Lower)', value: 'Дніпропетровська/Запорізька обл., басейн р. Дніпро (48.46° N, 35.04° E)' },
      { labelUk: '🟠 Басейн р. Дністер (Дністровський комплексний гідровузол)', labelEn: '🟠 Dnister River Basin (Podillia)', value: 'Вінницька/Хмельницька обл., басейн р. Дністер (48.45° N, 27.78° E)' },
      { labelUk: '🟢 Басейн р. Тиса (Закарпаття, Карпатський водозбір)', labelEn: '🟢 Tisza River Basin (Carpathians)', value: 'Закарпатська обл., басейн р. Тиса (48.17° N, 23.30° E)' },
      { labelUk: '🔴 Басейн Південного Бугу (Миколаївщина, Олександрівка)', labelEn: '🔴 Southern Buh River Basin', value: 'Миколаївська обл., басейн Південного Бугу (48.04° N, 30.85° E)' },
      { labelUk: '🔴 Басейн Сіверського Дінця (Харківщина, Донбас)', labelEn: '🔴 Siverskyi Donets River Basin', value: 'Харківська/Донецька обл., басейн Сіверського Дінця (49.20° N, 37.28° E)' }
    ];
  }

  return [
    { labelUk: '🇺🇦 Вся територія України (Національний рівень)', labelEn: '🇺🇦 Territory of Ukraine (National Level)', value: 'Вся територія України (Національний рівень)' },
    { labelUk: '🌾 Аграрний сектор (Центральні та Південні області)', labelEn: '🌾 Agriculture Sector (Central & South Ukraine)', value: 'Аграрний сектор (Центральні та Південні області)' },
    { labelUk: '🌲 Карпатський лісовий масив & Заповідні зони', labelEn: '🌲 Carpathian Forests & Protected Zones', value: 'Карпатський лісовий масив & Заповідні зони' },
    { labelUk: '🌊 Акваторія Чорного та Азовського морів', labelEn: '🌊 Black Sea & Sea of Azov Maritime Zone', value: 'Акваторія Чорного та Азовського морів' },
    { labelUk: '🏙️ Київська агломерація & Промислові зони', labelEn: '🏙️ Kyiv Agglomeration & Industrial Hubs', value: 'Київська агломерація & Промислові зони' }
  ];
}

const TIMEFRAME_OPTIONS = [
  { labelUk: '⚡ Оперативний моніторинг (Останні 24 години)', labelEn: '⚡ Real-time Operational (Last 24 Hours)', value: 'Оперативний моніторинг (Останні 24 години)' },
  { labelUk: '📅 Добовий та тижневий зріз (Останні 7 днів)', labelEn: '📅 Weekly Monitoring (Last 7 Days)', value: 'Добовий та тижневий зріз (Останні 7 днів)' },
  { labelUk: '🗓️ Місячний динамічний просеріал (Останні 30 днів)', labelEn: '🗓️ Monthly Dynamic Series (Last 30 Days)', value: 'Місячний динамічний просеріал (Останні 30 днів)' },
  { labelUk: '🌿 Сезонний порівняльний аналіз (2025 – 2026 рр.)', labelEn: '🌿 Seasonal Comparative Analysis (2025–2026)', value: 'Сезонний порівняльний аналіз (2025 – 2026 рр.)' },
  { labelUk: '📜 Багаторічний ретроспективний архів (2020 – 2026 рр.)', labelEn: '📜 Multi-year Retrospective Archive (2020–2026)', value: 'Багаторічний ретроспективний архів (2020 – 2026 рр.)' },
];

export const isEarthObservationAgent = (id: string): boolean => {
  return [
    'solotvyno-geomonitor-orchestrator',
    'insar-subsidence-timeseries-analyst',
    'gnss-sat-fusion-geodesist',
    'karst-sinkhole-optical-morphometrist',
    'subsidence-trough-geomechanical-predictor',
    'mine-infrastructure-structural-auditor',
    'hydro-karst-salt-dissolution-modeler',
    'tisza-transboundary-eco-monitor',
    'agrisat-monitor',
    'urban-sat-analyst',
    'maritime-sat-analyst',
    'disaster-sat-response',
    'geoint-defense-analyst',
    'sar-radar-analyst',
    'geological-sat-explorer',
    'ghg-sat-tracker',
    'war-damage-sat-auditor',
    'sat-image-harvester',
    'sat-thematic-processor',
    'sat-drought-water-stress-auditor',
    'satellite-eco-analyst'
  ].includes(id);
};

export type SpaceDomainCategory = 'eo' | 'sda' | 'gnss' | 'seismic';

export const getAgentDomainCategory = (id: string): SpaceDomainCategory => {
  if (isEarthObservationAgent(id)) return 'eo';
  if ([
    'ncuvkz-gnss-geodesy-auditor',
    'knp-gnss-integrity-auditor',
    'knp-rtk-diff-correction-operator',
    'knp-time-frequency-metrologist',
    'knp-antijamming-antispoofing-analyst',
    'knp-defense-navigation-briefing-analyst'
  ].includes(id)) return 'gnss';
  if ([
    'gcsk-seismic-auditor',
    'gcsk-acoustic-infrasound-analyst',
    'gcsk-radiation-chemical-monitor',
    'gcsk-geomagnetic-seismoacoustic-analyst',
    'gcsk-emergency-situation-notifier'
  ].includes(id)) return 'seismic';
  return 'sda'; // Default for ncuvkz-sda-debris-tracker, mission-control, space-weather, payload, spectrum, spacecraft testing
};

function getTimeframeOptions(agentId: string) {
  const cat = getAgentDomainCategory(agentId);
  if (cat === 'sda') {
    return [
      { labelUk: '⚡ Оперативний розрахунок на 24 години (TLE Епоха 0)', labelEn: '⚡ Real-time 24h Orbit Propagation (TLE Epoch 0)', value: 'Оперативний прогноз на 24 години (TLE Епоха)' },
      { labelUk: '🛸 Розрахунок зближень на 72 години (Conjunction Horizon)', labelEn: '🛸 72-Hour Conjunction Risk Window', value: 'Прогноз на 72 години (2026 р.)' },
      { labelUk: '🗓️ Орбітальний прогноз на 7 діб (Weekly Ephemeris)', labelEn: '🗓️ 7-Day Orbital Drift & Ephemeris Horizon', value: 'Орбітальний прогноз на 7 діб' },
      { labelUk: '📜 Довгостроковий аналіз деорбітації (2025–2030 рр.)', labelEn: '📜 Long-term De-orbit & Decay Assessment (2025–2030)', value: 'Довгостроковий аналіз деорбітації (2025–2030 рр.)' },
    ];
  }
  if (cat === 'gnss') {
    return [
      { labelUk: '⚡ Оперативний моніторинг (Останні 24 години)', labelEn: '⚡ Real-time Operational (Last 24 Hours)', value: 'Оперативний моніторинг (Останні 24 години)' },
      { labelUk: '📅 Добовий та тижневий RINEX зріз (Останні 7 днів)', labelEn: '📅 Weekly RINEX & Integrity Window (7 Days)', value: 'Добовий та тижневий зріз (Останні 7 днів)' },
      { labelUk: '🗓️ Місячний навігаційний аналіз (Останні 30 днів)', labelEn: '🗓️ Monthly GNSS Performance (30 Days)', value: 'Місячний динамічний просеріал (Останні 30 днів)' },
    ];
  }
  if (cat === 'seismic') {
    return [
      { labelUk: '⚡ Оперативне чергування (Останні 24 години)', labelEn: '⚡ Real-time Watch (Last 24 Hours)', value: 'Оперативний моніторинг (Останні 24 години)' },
      { labelUk: '📅 Тижневий сейсмологічний бюлетень (7 днів)', labelEn: '📅 Weekly CTBTO Seismic Bulletin (7 Days)', value: 'Добовий та тижневий зріз (Останні 7 днів)' },
      { labelUk: '🗓️ Місячний геофізичний зріз (Останні 30 днів)', labelEn: '🗓️ Monthly Geophysical Report (30 Days)', value: 'Місячний динамічний просеріал (Останні 30 днів)' },
    ];
  }
  return TIMEFRAME_OPTIONS;
}

const isPaidSourceSelected = (sourceStr: string): boolean => {
  if (!sourceStr) return false;
  const lower = sourceStr.toLowerCase();
  return (
    lower.includes('0.3m') ||
    lower.includes('0.5m') ||
    lower.includes('3m') ||
    lower.includes('worldview') ||
    lower.includes('geoeye') ||
    lower.includes('skysat') ||
    lower.includes('planetscope') ||
    lower.includes('iceye') ||
    lower.includes('capella') ||
    lower.includes('pléiades') ||
    lower.includes('pleiades') ||
    lower.includes('platny') ||
    lower.includes('платн') ||
    lower.includes('vhr')
  ) && !lower.includes('sentinel') && !lower.includes('landsat');
};

export default function SpaceSatAgentView({ 
  agentId, 
  language, 
  onLanguageChange,
  onBack, 
  onGoHome,
  useEnterpriseContext, 
  enterpriseName,
  currency = 'UAH'
}: SpaceSatAgentViewProps) {
  const isUk = language === 'uk';
  const isPl = language === 'pl';
  const isEn = language === 'en';
  const isEO = isEarthObservationAgent(agentId);
  const domainCategory = getAgentDomainCategory(agentId);
  const meta = SPACE_AGENTS_META[agentId] || SPACE_AGENTS_META['agrisat-monitor'];
  const IconComponent = meta.icon || Globe;

  const firstPreset = meta.presetsUk[0] || {
    title: 'Тестовий регіон',
    type: 'Супутниковий аналіз',
    region: 'Україна, 50.45° N, 30.52° E',
    timeframe: '2025 – 2026 рр.',
    sources: 'Sentinel-2 + Landsat 9',
    context: 'Детальний супутниковий аналіз території.'
  };

  const [regionName, setRegionName] = useState(firstPreset.region);
  const [monitoringType, setMonitoringType] = useState(firstPreset.type);
  const [timeFrame, setTimeFrame] = useState(firstPreset.timeframe);
  const [satelliteSources, setSatelliteSources] = useState(firstPreset.sources);
  const [additionalContext, setAdditionalContext] = useState(
    useEnterpriseContext ? (isUk ? 'Враховувати гео-об’єкти з бази знань підприємства.' : 'Include enterprise geo-data from knowledge base.') : ''
  );

  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  const [copiedReport, setCopiedReport] = useState(false);

  useEffect(() => {
    if (report && typeof report === 'string' && report.trim().length > 0 && !report.startsWith('⚠️ ПОМИЛКА')) {
      autoDownloadReport(report, meta.titleUk || 'Супутниковий Звіт', agentId);
    }
  }, [report]);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  const [isDownloadingDocx, setIsDownloadingDocx] = useState(false);
  const [pricingInfo, setPricingInfo] = useState<{ price: number; limit: number; durationDays: number; singlePrice: number } | null>(null);

  // Execution Mode: 'harvest' (Live STAC) | 'upload' (Vision AI) | 'simulate' (AI Modeling)
  const [executionMode, setExecutionMode] = useState<'harvest' | 'upload' | 'simulate'>(() => {
    if (agentId === 'sat-thematic-processor') return 'upload';
    if (!isEarthObservationAgent(agentId)) return 'simulate';
    return 'harvest';
  });

  // Local Image Upload & Thematic Processing States
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [supportFiles, setSupportFiles] = useState<File[]>([]);
  const [thematicType, setThematicType] = useState<string>('auto_general');
  const [isUploadingThematic, setIsUploadingThematic] = useState<boolean>(false);
  const [satRenderUrl, setSatRenderUrl] = useState<string | null>(null);
  const [satRenderUrls, setSatRenderUrls] = useState<string[]>([]);
  const [processedUploadImages, setProcessedUploadImages] = useState<Array<{ name: string; url: string; size: number }>>([]);
  const [satDocxUrl, setSatDocxUrl] = useState<string | null>(null);

  // Direct Device Download & Harvester States
  const [harvestedItems, setHarvestedItems] = useState<any[]>([]);
  const [autoDownloadDevice, setAutoDownloadDevice] = useState<boolean>(true);
  const [isZipping, setIsZipping] = useState<boolean>(false);
  const [activeLightboxImage, setActiveLightboxImage] = useState<{ src: string; alt: string } | null>(null);

  const getDefaultThematicType = (id: string): string => {
    if (['solotvyno-geomonitor-orchestrator', 'subsidence-trough-geomechanical-predictor', 'mine-infrastructure-structural-auditor', 'urban-sat-analyst', 'ncuvkz-gnss-geodesy-auditor'].includes(id)) return 'urban_infrastructure';
    if (['insar-subsidence-timeseries-analyst', 'gnss-sat-fusion-geodesist', 'sar-radar-analyst', 'knp-antijamming-antispoofing-analyst'].includes(id)) return 'sar_radar';
    if (['karst-sinkhole-optical-morphometrist', 'hydro-karst-salt-dissolution-modeler', 'geological-sat-explorer'].includes(id)) return 'geology_mining';
    if (['tisza-transboundary-eco-monitor', 'maritime-sat-analyst'].includes(id)) return 'hydro_water';
    return 'auto_general';
  };

  React.useEffect(() => {
    const currentMeta = SPACE_AGENTS_META[agentId] || SPACE_AGENTS_META['agrisat-monitor'];
    const p = currentMeta.presetsUk[0] || {
      title: 'Тестовий регіон',
      type: 'Супутниковий аналіз',
      region: 'Україна, 50.45° N, 30.52° E',
      timeframe: '2025 – 2026 рр.',
      sources: 'Sentinel-2 + Landsat 9',
      context: 'Детальний супутниковий аналіз території.'
    };
    setRegionName(p.region);
    setMonitoringType(p.type);
    setTimeFrame(p.timeframe);
    setSatelliteSources(p.sources);
    setThematicType(getDefaultThematicType(agentId));
    if (agentId === 'sat-thematic-processor') {
      setExecutionMode('upload');
    } else if (isEarthObservationAgent(agentId)) {
      setExecutionMode('harvest');
    } else {
      setExecutionMode('simulate');
    }
    setError(null);
    setReport(null);
    setHarvestedItems([]);
    setSelectedFiles([]);
    setSupportFiles([]);
    setSatRenderUrl(null);
    setSatRenderUrls([]);
    setProcessedUploadImages([]);
    setSatDocxUrl(null);
  }, [agentId]);

  React.useEffect(() => {
    const fetchPricing = async () => {
      try {
        const res = await fetch('/api/subscription/pricing');
        if (res.ok) {
          const data = await res.json();
          if (data[agentId]) {
            setPricingInfo(data[agentId]);
          } else {
            setPricingInfo({ price: 490, limit: 30, durationDays: 30, singlePrice: 25 });
          }
        }
      } catch (err) {
        setPricingInfo({ price: 490, limit: 30, durationDays: 30, singlePrice: 25 });
      }
    };
    fetchPricing();
  }, [agentId]);

  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const defaultSteps = isUk 
        ? ['Аналіз параметрів...', 'Обробка Gemini Vision...', 'Формування звіту ДСТУ...']
        : ['Analyzing parameters...', 'Gemini Vision processing...', 'Building report...'];
      const steps = (isUk ? meta?.loadingStepsUk : meta?.loadingStepsEn) || defaultSteps;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < (steps.length || 1) - 1 ? prev + 1 : prev));
      }, 6000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk, meta]);

  const handleApplyPreset = (preset: typeof meta.presetsUk[0]) => {
    setRegionName(preset.region);
    setMonitoringType(preset.type);
    setTimeFrame(preset.timeframe);
    setSatelliteSources(preset.sources);
    setAdditionalContext(preset.context);
  };

  const handleRunThematicUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsAuditing(true);
    setIsUploadingThematic(true);
    setError(null);
    setReport(null);
    setSatRenderUrl(null);
    setSatRenderUrls([]);
    setProcessedUploadImages([]);
    setSatDocxUrl(null);

    try {
      const formData = new FormData();
      selectedFiles.forEach((f) => {
        formData.append('files', f);
      });
      formData.append('thematicType', thematicType);
      formData.append('agentType', agentId);
      formData.append('targetDescription', additionalContext || regionName || 'Тематична обробка знімків');

      if (supportFiles && supportFiles.length > 0) {
        supportFiles.forEach((f) => {
          formData.append('supportFiles', f);
        });
      }

      const res = await fetch('/api/satellite/upload-thematic-analysis', {
        method: 'POST',
        body: formData
      });

      const text = await res.text();
      let data: any = {};
      try {
        data = JSON.parse(text);
      } catch (jsonErr) {
        throw new Error(text || `Помилка сервера (HTTP ${res.status}): ${res.statusText}`);
      }

      if (res.ok && data.success) {
        setReport(data.reportMarkdown);
        if (data.renderUrls && Array.isArray(data.renderUrls)) {
          setSatRenderUrls(data.renderUrls);
        }
        if (data.renderUrl) setSatRenderUrl(data.renderUrl);
        if (data.processedImages && Array.isArray(data.processedImages)) {
          setProcessedUploadImages(data.processedImages);
        }
        if (data.docxUrl) setSatDocxUrl(data.docxUrl);
      } else {
        setError(data.error || `Помилка тематичної обробки знімка (HTTP ${res.status})`);
      }
    } catch (err: any) {
      setError(err?.message || 'Помилка мережі при завантаженні файлу');
    } finally {
      setIsAuditing(false);
      setIsUploadingThematic(false);
    }
  };

  const triggerDeviceDownload = (fileUrl: string, filename: string) => {
    const directUrl = `/api/satellite/download-direct?url=${encodeURIComponent(fileUrl)}&filename=${encodeURIComponent(filename)}`;
    const a = document.createElement('a');
    a.href = directUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const handleDownloadZipBundle = async (itemsToZip?: any[]) => {
    const list = itemsToZip || harvestedItems;
    if (!list || list.length === 0) return;
    setIsZipping(true);
    try {
      const urls = list.map(item => item.assetUrl).filter(Boolean);
      const res = await fetch('/api/satellite/download-zip-bundle', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ urls })
      });
      const data = await res.json();
      if (res.ok && data.zipUrl) {
        triggerDeviceDownload(data.zipUrl, data.zipFilename || `Satellite_Images_${(regionName || 'Bundle').replace(/[^a-zA-Z0-9]/g, '_')}.zip`);
      }
    } catch (err: any) {
      console.error('Failed to download ZIP bundle:', err);
    } finally {
      setIsZipping(false);
    }
  };

  const handleRunHarvest = async () => {
    if (!regionName.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);
    setHarvestedItems([]);
    setSatRenderUrl(null);
    setSatDocxUrl(null);

    try {
      const res = await fetch('/api/satellite/harvest-imagery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId: agentId,
          satelliteSources: satelliteSources,
          monitoringType: monitoringType,
          targetDescription: `${monitoringType} - ${additionalContext || regionName}`,
          regionName: regionName,
          region: regionName,
          additionalContext: additionalContext,
          coordinates: regionName,
          startDate: timeFrame.includes('202') ? (timeFrame.split(/[-–—]/)[0]?.trim() || '2026-01-01') : '2026-01-01',
          endDate: timeFrame.includes('202') ? (timeFrame.split(/[-–—]/)[1]?.trim() || '2026-08-01') : '2026-08-01',
          maxCloudCover: '20%',
          collections: satelliteSources.toLowerCase().includes('sar') || satelliteSources.toLowerCase().includes('sentinel-1')
            ? ['sentinel-1', 'sentinel-2']
            : satelliteSources.toLowerCase().includes('landsat')
            ? ['landsat-9', 'sentinel-2']
            : satelliteSources.toLowerCase().includes('maxar')
            ? ['maxar-wv3', 'sentinel-2']
            : ['sentinel-2', 'landsat-9']
        })
      });

      const text = await res.text();
      let data: any = {};
      try {
        data = JSON.parse(text);
      } catch (jsonErr) {
        throw new Error(
          res.status === 502 || res.status === 504
            ? (isUk ? 'Сервер оновлював з\'єднання (HTTP 502). Будь ласка, натисніть кнопку ще раз.' : 'Server was reconnecting (HTTP 502). Please retry in a few seconds.')
            : (text.substring(0, 100) || `Помилка сервера (HTTP ${res.status}): ${res.statusText}`)
        );
      }

      if (res.ok && data.success) {
        setReport(data.reportMarkdown);
        if (data.docxUrl) setSatDocxUrl(data.docxUrl);
        if (Array.isArray(data.harvestedItems)) {
          setHarvestedItems(data.harvestedItems);
          
          // AUTO DOWNLOAD SINGLE ZIP BUNDLE TO PREVENT BROWSER MULTI-FILE DOWNLOAD BLOCKING
          if (autoDownloadDevice && data.harvestedItems.length > 0) {
            setTimeout(() => {
              handleDownloadZipBundle(data.harvestedItems);
            }, 500);
          }
        }
      } else {
        if (data.error === 'SUBSCRIPTION_REQUIRED' || res.status === 403) {
          setError(data.message || (isUk 
            ? '🎁 Ви вже використали безкоштовний тестовий запуск цього інструмента. Поповніть баланс або придбайте доступ для продовження.' 
            : 'You have used your free demo run for this agent. Please top up your balance or subscribe to continue.'));
        } else {
          setError(data.error || data.message || (isUk ? 'Помилка викачування супутникових знімків.' : 'Failed to harvest satellite images.'));
        }
      }
    } catch (err: any) {
      setError(err.message || (isUk ? 'Помилка мережевого запиту.' : 'Network error.'));
    } finally {
      setIsAuditing(false);
    }
  };

  const fetchSimulationSatelliteScene = async (currentReport: string) => {
    try {
      const res = await fetch('/api/satellite/harvest-imagery', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          agentId,
          satelliteSources,
          monitoringType,
          targetDescription: `${monitoringType} - ${additionalContext || regionName}`,
          regionName,
          region: regionName,
          coordinates: regionName,
          startDate: timeFrame.includes('202') ? (timeFrame.split(/[-–—]/)[0]?.trim() || '2026-01-01') : '2026-01-01',
          endDate: timeFrame.includes('202') ? (timeFrame.split(/[-–—]/)[1]?.trim() || '2026-08-01') : '2026-08-01',
          maxCloudCover: '20%',
          collections: satelliteSources.toLowerCase().includes('sar') || satelliteSources.toLowerCase().includes('sentinel-1')
            ? ['sentinel-1', 'sentinel-2']
            : ['sentinel-2', 'landsat-9']
        })
      });
      if (res.ok) {
        const data = await res.json();
        if (data.harvestedItems && Array.isArray(data.harvestedItems) && data.harvestedItems.length > 0) {
          setHarvestedItems(data.harvestedItems);
          if (data.docxUrl) setSatDocxUrl(data.docxUrl);
        }
      }
    } catch (e) {
      console.warn('Simulation satellite scene auto-fetch error:', e);
    }
  };

  const handleRunSimulation = async () => {
    if (!regionName.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);
    setHarvestedItems([]);
    setSatRenderUrl(null);
    setSatDocxUrl(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: meta.crewName,
          agentId,
          inputs: {
            region_name: regionName,
            monitoring_type: monitoringType,
            time_frame: timeFrame,
            satellite_sources: satelliteSources,
            additional_context: additionalContext || (isUk ? "Стандартний супутниковий аналіз місії" : "Standard satellite mission audit")
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        if (data.error === 'SUBSCRIPTION_REQUIRED' || response.status === 403) {
          throw new Error(isUk 
            ? '🎁 Ви вже використали безкоштовний тестовий запуск цього інструмента. Поповніть баланс або придбайте доступ для продовження.' 
            : 'You have used your free demo run for this agent. Please top up your balance or subscribe to continue.');
        }
        throw new Error(data.error || data.message || (isUk ? 'Помилка виконання супутникового аналізу.' : 'Failed to run satellite audit.'));
      }

      const data = await response.json();

      if (data.jobId) {
        const pollJob = async () => {
          const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
          if (!statusRes.ok) throw new Error(isUk ? 'Помилка перевірки статусу.' : 'Failed to check status.');
          const statusData = await statusRes.json();

          if (statusData.status === 'completed') {
            setReport(statusData.report);
            setIsAuditing(false);
            if (isEarthObservationAgent(agentId)) {
              fetchSimulationSatelliteScene(statusData.report);
            }
          } else if (statusData.status === 'failed') {
            setError(statusData.error || (isUk ? 'Помилка під час виконання аналізу.' : 'Audit execution failed.'));
            setIsAuditing(false);
          } else {
            setTimeout(pollJob, 3000);
          }
        };
        setTimeout(pollJob, 3000);
      } else if (data.report) {
        setReport(data.report);
        setIsAuditing(false);
        if (isEarthObservationAgent(agentId)) {
          fetchSimulationSatelliteScene(data.report);
        }
      } else {
        throw new Error(isUk ? 'Невідомий формат відповіді сервера.' : 'Invalid server response.');
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка при виконання аналізу.' : 'An error occurred during execution.'));
      setIsAuditing(false);
    }
  };

  const handleRun = async () => {
    if (executionMode === 'upload') {
      return handleRunThematicUpload();
    }
    if (executionMode === 'harvest') {
      return handleRunHarvest();
    }
    return handleRunSimulation();
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  const handleDownloadDocx = async () => {
    if (!report) return;
    setIsDownloadingDocx(true);

    try {
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text: report,
          fileName: `${agentId}_Report_${regionName.replace(/[^a-zA-Z0-9абвгґдеєжзиіїйклмнопрстуфхцчшщьюяАБВГҐДЕЄЖЗИІЇЙКЛМНОПРСТУФХЦЧШЩЬЮЯ_-]/g, '_').substring(0, 30)}`
        })
      });

      if (!response.ok) {
        throw new Error('Помилка генерації файлу Word (.docx).');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${agentId}_Report.docx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      alert(err.message || 'Не вдалося завантажити Word документ.');
    } finally {
      setIsDownloadingDocx(false);
    }
  };

  const renderFormattedReport = (rawMarkdown: string) => {
    if (!rawMarkdown) return null;
    const lines = rawMarkdown.split('\n');
    const elements: React.ReactNode[] = [];
    let i = 0;

    const renderInline = (text: string) => {
      const parts = text.split(/(\*\*.*?\*\*|\*.*?\*|`.*?`|\[.*?\]\(.*?\))/g);
      return parts.map((part, pIdx) => {
        if (!part) return null;
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={pIdx} className="font-bold text-white">{part.slice(2, -2)}</strong>;
        }
        if (part.startsWith('*') && part.endsWith('*')) {
          return <em key={pIdx} className="italic text-emerald-300">{part.slice(1, -1)}</em>;
        }
        if (part.startsWith('`') && part.endsWith('`')) {
          return <code key={pIdx} className="px-1.5 py-0.5 rounded bg-slate-800 text-emerald-400 font-mono text-xs">{part.slice(1, -1)}</code>;
        }
        const linkMatch = part.match(/^\[(.*?)\]\((.*?)\)$/);
        if (linkMatch) {
          return (
            <a
              key={pIdx}
              href={linkMatch[2]}
              target="_blank"
              rel="noopener noreferrer"
              className="text-emerald-400 hover:text-emerald-300 underline font-semibold transition-colors"
            >
              {linkMatch[1]}
            </a>
          );
        }
        return part;
      });
    };

    while (i < lines.length) {
      const line = lines[i];
      const trimmed = line.trim();

      // 1. Markdown Image: ![alt](src)
      const imgMatch = trimmed.match(/^!\[(.*?)\]\((.*?)\)$/);
      if (imgMatch) {
        const altText = imgMatch[1] || 'Космічний знімок';
        const imgSrc = imgMatch[2];
        
        const extractFullDateBadge = (text: string) => {
          if (!text) return null;
          const dateMatch = text.match(/(\d{4}-\d{2}-\d{2}(?:\s+\d{2}:\d{2}(?::\d{2})?\s*UTC)?|\d{1,2}\.\d{1,2}\.\d{4}(?:\s+\d{2}:\d{2}(?::\d{2})?\s*UTC)?|\d{1,2}\s+(?:січня|лютого|березня|квітня|травня|червня|липня|серпня|вересня|жовтня|листопада|грудня)\s+\d{4}(?:\s+року)?)/i);
          if (dateMatch) return dateMatch[0];
          const afterDateKeyword = text.split(/Дата зйомки:|Дата фіксації:|Дата:|Date:/i)[1];
          if (afterDateKeyword) return afterDateKeyword.replace(/[()\]]/g, '').trim();
          return null;
        };

        const displayDate = extractFullDateBadge(altText) || '2026-04-15 10:30:00 UTC (15 квітня 2026 р.)';

        elements.push(
          <div key={`img-${i}`} className="my-6 bg-slate-950 border border-slate-800 hover:border-emerald-500/40 rounded-2xl p-4 space-y-3 shadow-xl transition-all">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <span className="text-xs font-mono font-bold text-emerald-400 flex items-center gap-1.5 truncate">
                <ImageIcon className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{altText}</span>
              </span>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => triggerDeviceDownload(imgSrc, `Sat_Frame_${Date.now()}.jpg`)}
                  className="px-2.5 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-mono rounded-lg border border-emerald-500/30 flex items-center gap-1 cursor-pointer transition-colors"
                  title="Завантажити знімок на пристрій"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>JPG</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLightboxImage({ src: imgSrc, alt: altText })}
                  className="px-2.5 py-1 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-mono rounded-lg border border-slate-700 flex items-center gap-1 cursor-pointer transition-colors"
                  title="Збільшити на весь екран"
                >
                  <Maximize2 className="w-3.5 h-3.5 text-emerald-400" />
                  <span>{isUk ? 'Збільшити' : 'Zoom'}</span>
                </button>
              </div>
            </div>
            <div 
              onClick={() => setActiveLightboxImage({ src: imgSrc, alt: altText })}
              className="relative overflow-hidden rounded-xl cursor-pointer border border-slate-800 bg-black/60 group max-h-[500px]"
            >
              <img
                src={imgSrc}
                alt={altText}
                className="w-full h-auto object-contain mx-auto group-hover:scale-[1.01] transition-transform duration-300"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-slate-950/0 group-hover:bg-slate-950/30 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                <span className="px-3.5 py-1.5 bg-slate-900/90 border border-emerald-500/50 text-emerald-300 text-xs font-bold rounded-xl shadow-2xl backdrop-blur-md flex items-center gap-1.5">
                  <Maximize2 className="w-3.5 h-3.5" />
                  <span>{isUk ? 'Натисніть для перегляду' : 'Click to preview'}</span>
                </span>
              </div>
            </div>
            <div className="flex items-center justify-between gap-2 flex-wrap text-[11px] text-slate-400 font-mono pt-1.5 border-t border-slate-900">
              <span className="italic text-slate-300 font-medium">{altText.startsWith('Рис') ? altText : `Рис. — ${altText}`}</span>
              <span className="px-2.5 py-1 rounded-lg bg-emerald-950/80 border border-emerald-500/40 text-emerald-300 font-bold flex items-center gap-1.5 shadow-sm text-xs">
                <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                <span>📅 Дата зйомки: {displayDate}</span>
              </span>
            </div>
          </div>
        );
        i++;
        continue;
      }

      // 2. Headings
      if (trimmed.startsWith('# ')) {
        elements.push(
          <h1 key={`h1-${i}`} className="text-xl sm:text-2xl font-black text-white uppercase tracking-wide mt-8 mb-4 border-b border-emerald-500/30 pb-2 flex items-center gap-2">
            <Satellite className="w-5 h-5 text-emerald-400" />
            <span>{trimmed.replace(/^#\s+/, '')}</span>
          </h1>
        );
        i++;
        continue;
      }
      if (trimmed.startsWith('## ')) {
        elements.push(
          <h2 key={`h2-${i}`} className="text-base sm:text-lg font-bold text-emerald-400 uppercase tracking-wide mt-6 mb-3 border-b border-slate-800 pb-1.5">
            {trimmed.replace(/^##\s+/, '')}
          </h2>
        );
        i++;
        continue;
      }
      if (trimmed.startsWith('### ')) {
        elements.push(
          <h3 key={`h3-${i}`} className="text-sm sm:text-base font-bold text-slate-100 mt-4 mb-2">
            {trimmed.replace(/^###\s+/, '')}
          </h3>
        );
        i++;
        continue;
      }

      // 3. Markdown Tables
      if (trimmed.startsWith('|') && trimmed.endsWith('|')) {
        const tableLines: string[] = [];
        while (i < lines.length && lines[i].trim().startsWith('|') && lines[i].trim().endsWith('|')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        const cleanRows = tableLines
          .filter(l => !l.includes('---|'))
          .map(l => l.split('|').slice(1, -1).map(c => c.trim()));

        if (cleanRows.length > 0) {
          const header = cleanRows[0];
          const body = cleanRows.slice(1);
          elements.push(
            <div key={`table-${i}`} className="my-5 overflow-x-auto rounded-xl border border-slate-800 bg-slate-950/90 shadow-xl">
              <table className="w-full text-xs text-left text-slate-300 font-mono">
                <thead className="bg-slate-900 text-emerald-400 font-bold border-b border-slate-800 uppercase tracking-wider">
                  <tr>
                    {header.map((col, cIdx) => (
                      <th key={cIdx} className="px-4 py-3 border-r border-slate-800 last:border-r-0">
                        {renderInline(col)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {body.map((row, rIdx) => (
                    <tr key={rIdx} className="hover:bg-slate-900/50 transition-colors">
                      {row.map((cell, cIdx) => (
                        <td key={cIdx} className="px-4 py-2.5 border-r border-slate-800/60 last:border-r-0">
                          {renderInline(cell)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          );
        }
        continue;
      }

      // 4. Bullet lists
      if (trimmed.startsWith('* ') || trimmed.startsWith('- ')) {
        const bulletText = trimmed.replace(/^[\*\-]\s+/, '');
        elements.push(
          <div key={`bullet-${i}`} className="flex items-start gap-2.5 my-1.5 ml-2 text-xs sm:text-sm text-slate-300 leading-relaxed font-mono">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mt-2 shrink-0" />
            <div className="flex-1">{renderInline(bulletText)}</div>
          </div>
        );
        i++;
        continue;
      }

      // 5. Dividers
      if (trimmed === '---' || trimmed === '***') {
        elements.push(<hr key={`hr-${i}`} className="border-slate-800 my-6" />);
        i++;
        continue;
      }

      // 6. Regular paragraphs
      if (trimmed) {
        elements.push(
          <p key={`p-${i}`} className="text-xs sm:text-sm text-slate-300 leading-relaxed my-2.5 font-mono">
            {renderInline(trimmed)}
          </p>
        );
      }

      i++;
    }

    return elements;
  };

  const defaultStepsList = isUk 
    ? ['Аналіз параметрів...', 'Обробка у Gemini 3.7 Flash & Vision...', 'Формування звіту ДСТУ 4163:2020...']
    : ['Analyzing parameters...', 'Processing with Gemini 3.7 Flash & Vision...', 'Building DSTU report...'];
  const stepsList = (isPl ? (meta?.loadingStepsPl || meta?.loadingStepsEn || meta?.loadingStepsUk) : isUk ? meta?.loadingStepsUk : meta?.loadingStepsEn) || defaultStepsList;
  const agentTitle = isPl ? (meta?.titlePl || meta?.titleEn || meta?.titleUk) : isUk ? meta?.titleUk : meta?.titleEn;

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Top Banner & Header */}
      <div className="flex items-center justify-between flex-wrap gap-4 bg-slate-900/80 backdrop-blur border border-slate-800 rounded-2xl p-5 shadow-xl">
        <div className="flex items-center gap-3 flex-wrap">
          <button 
            onClick={onGoHome || onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            🏠 {isPl ? "Strona Główna" : isUk ? "На головну" : "Home"}
          </button>
          <button 
            onClick={onBack}
            className="px-3.5 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase rounded-lg transition-colors flex items-center gap-2 cursor-pointer border border-slate-700 font-mono"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>{isPl ? "← Powrót do Centrum Kosmicznego" : isUk ? "← До Космічного Центру" : "← Back to Space Hub"}</span>
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl text-emerald-400">
            <IconComponent className="w-7 h-7" />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-white">{agentTitle}</h1>
              <span className="px-2.5 py-0.5 text-xs font-semibold bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 rounded-full">
                CrewAI Multi-Agent (Gemini 3.7 Flash)
              </span>
            </div>
            <p className="text-xs text-slate-400 mt-1">
              {isPl
                ? "Autonomiczny system wieloagentowy CrewAI do teledetekcji i analizy satelitarnej Ziemi"
                : isUk 
                ? "Автономна CrewAI мультиагентна система дистанційного зондування Землі та космічного аналізу"
                : "Autonomous CrewAI Earth Observation & Space Intelligence Multi-Agent Engine"}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          {onLanguageChange && (
            <div className="flex items-center bg-slate-950 p-1 rounded-xl border border-slate-800 text-[10px] font-mono font-bold">
              <button
                type="button"
                onClick={() => onLanguageChange('uk')}
                className={`px-2 py-1 rounded-lg transition-all cursor-pointer ${
                  language === 'uk' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                🇺🇦 UA
              </button>
              <button
                type="button"
                onClick={() => onLanguageChange('en')}
                className={`px-2 py-1 rounded-lg transition-all cursor-pointer ${
                  language === 'en' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                🇬🇧 EN
              </button>
              <button
                type="button"
                onClick={() => onLanguageChange('pl')}
                className={`px-2 py-1 rounded-lg transition-all cursor-pointer ${
                  language === 'pl' ? 'bg-emerald-600 text-white font-black shadow-xs' : 'text-slate-400 hover:text-slate-200'
                }`}
              >
                🇵🇱 PL
              </button>
            </div>
          )}
          {useEnterpriseContext && (
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-xl text-blue-400 text-xs">
              <Sparkles className="w-4 h-4" />
              <span>{isPl ? `Kontekst: ${enterpriseName || 'Przedsiębiorstwo'}` : isUk ? `Контекст: ${enterpriseName || 'Підприємство'}` : `Context: ${enterpriseName || 'Enterprise'}`}</span>
            </div>
          )}
        </div>
      </div>

      {/* Agent Pricing & Tariffs Info Card */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-4 sm:p-5 text-white flex flex-col md:flex-row items-start md:items-center justify-between gap-4 font-mono shadow-xl">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-blue-500/20 text-blue-300 border border-blue-400/30 rounded">
              💳 {isPl ? 'TARYFY I CENNIK MODUŁU' : isUk ? 'ТАРИФ ТА ВАРТІСТЬ ПОСЛУГ' : 'AGENT PRICING & TARIFFS'}
            </span>
            <span className="px-2 py-0.5 text-[9px] font-extrabold uppercase bg-purple-500/20 text-purple-300 border border-purple-400/30 rounded">
              ✨ Gemini 3.7 Flash
            </span>
          </div>
          <h4 className="text-sm font-extrabold uppercase text-white tracking-wide">
            {isPl ? 'Plany taryfowe i koszt uruchomienia modułu' : isUk ? 'Тарифні плани та вартість запуску модуля' : 'Flexible Access & Rate Plans'}
          </h4>
          <p className="text-xs text-slate-300">
            {isPl
              ? '1. start ZA DARMO. Dostępne są korzystne pakiety subskrypcyjne lub płatność jednorazowa.'
              : isUk
              ? '1-й запуск БЕЗКОШТОВНО. Далі доступні вигідні підписки на пакет запитів або разовий запуск.'
              : 'First run FREE. Flexible pay-per-use and monthly subscription rates apply thereafter.'}
          </p>
        </div>

        <div className="flex items-center gap-3 flex-wrap shrink-0">
          <div className="px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-slate-400 uppercase">{isUk ? 'Підписка (30 дн)' : isPl ? 'Subskrypcja (30 dni)' : 'Subscription (30d)'}</span>
            <span className="text-sm font-extrabold text-blue-400">
              {formatPriceWithCurrency(pricingInfo?.price || 490, currency)} <span className="text-[10px] text-slate-400">/ {pricingInfo?.limit || 30} {isUk ? 'зап.' : language === 'pl' ? 'zap.' : 'req.'}</span>
            </span>
          </div>

          <div className="px-3.5 py-2 bg-slate-950 border border-slate-800 rounded-xl text-center">
            <span className="block text-[10px] font-bold text-slate-400 uppercase">{isUk ? 'Разовий запуск' : language === 'pl' ? 'Pojedynczo' : 'Single Run'}</span>
            <span className="text-sm font-extrabold text-emerald-400">
              {formatPriceWithCurrency(pricingInfo?.singlePrice || 25, currency)} <span className="text-[10px] text-slate-400">/ {isUk ? '1 зап.' : language === 'pl' ? '1 zap.' : '1 req.'}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Free Demo Callout */}
      <div className="bg-emerald-950/40 border border-emerald-500/30 rounded-2xl p-4 flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500/20 text-emerald-400 rounded-lg">
            <Sparkles className="w-5 h-5 animate-pulse" />
          </div>
          <div>
            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wider">
              {isUk ? '🎁 1-й запуск БЕЗКОШТОВНО (100% FREE)' : '🎁 1st Run 100% FREE'}
            </span>
            <p className="text-xs text-slate-300">
              {isUk
                ? (isEO
                    ? 'Спробуйте аналіз супутникових знімків прямо зараз на демо-прикладі в 1 клік без привʼязки платіжної картки.'
                    : domainCategory === 'sda'
                    ? 'Спробуйте розрахунок орбітальних зближень та аналіз космічного простору (SDA) на демо-прикладі в 1 клік без привʼязки картки.'
                    : domainCategory === 'gnss'
                    ? 'Спробуйте аудит навігаційного поля та захисту від РЕБ на демо-прикладі в 1 клік без привʼязки картки.'
                    : 'Спробуйте сейсмічний та геофізичний спецконтроль CTBTO на демо-прикладі в 1 клік без привʼязки картки.')
                : isPl
                ? (isEO
                    ? 'Wypróbuj analizę zdjęć satelitarnych w 1 kliknięcie na przykładzie demo bez podpinania karty.'
                    : 'Wypróbuj analizę orbity i bezpieczeństwa kosmicznego na danych demo za 1 kliknięciem.')
                : (isEO
                    ? 'Try satellite imagery analysis right now using 1-click demo data with zero card commitments.'
                    : 'Try Space Domain Awareness and orbital conjunction modeling with 1-click demo data.')}
            </p>
          </div>
        </div>
        <button
          onClick={() => handleApplyPreset(firstPreset)}
          className="w-full sm:w-auto px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold text-xs rounded-xl shadow-lg shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 whitespace-nowrap"
        >
          <Sparkles className="w-4 h-4" />
          <span>{isUk ? '⚡ Спробувати на демо-прикладі в 1 клік' : '⚡ Try 1-Click Demo Preset'}</span>
        </button>
      </div>

      {/* Presets */}
      {meta.presetsUk && meta.presetsUk.length > 0 && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-400">
            {isUk 
              ? (isEO ? "Готові пресети та прецеденти моніторингу:" : "Готові галузеві пресети та орбітальні прецеденти:") 
              : "Pre-configured domain presets:"}
          </label>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {meta.presetsUk.map((preset, idx) => (
              <button
                key={idx}
                onClick={() => handleApplyPreset(preset)}
                className="text-left p-3.5 bg-slate-900/60 hover:bg-slate-800/80 border border-slate-800 hover:border-emerald-500/40 rounded-xl transition-all group"
              >
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold text-white group-hover:text-emerald-400 transition-colors">
                    {preset.title}
                  </span>
                  <Sparkles className="w-3.5 h-3.5 text-slate-500 group-hover:text-emerald-400" />
                </div>
                <p className="text-[11px] text-emerald-400/80 mt-1 font-mono">{preset.type}</p>
                <p className="text-[11px] text-slate-400 mt-1 line-clamp-2">{preset.context}</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Inputs Form */}
      <div className="bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-xl space-y-5">
        
        {/* 3-MODE EXECUTION SWITCHER (High Contrast & Intuitive) */}
        <div className="space-y-1.5">
          <label className="block text-xs font-bold text-slate-300 font-mono flex items-center justify-between">
            <span>
              {isUk 
                ? (isEO ? '🌐 ОБЕРІТЬ ДЖЕРЕЛО ТА РЕЖИМ СУПУТНИКОВОГО АНАЛІЗУ:' : '🌐 ОБЕРІТЬ РЕЖИМ РОЗРАХУНКУ ТА ДЖЕРЕЛО ДАНИХ:') 
                : isPl 
                ? '🌐 WYBIERZ ŹRÓDŁO I TRYB ANALIZY:' 
                : '🌐 SELECT DATA SOURCE & OPERATIONAL MODE:'}
            </span>
            <span className="text-[10px] text-emerald-400 font-mono">
              {executionMode === 'harvest' 
                ? (isEO ? '🛰️ STAC Live' : '🚀 Fast Solver') 
                : executionMode === 'upload' 
                ? (isEO ? '📸 Vision AI' : '📁 Data Logs') 
                : '🧠 AI Simulation'}
            </span>
          </label>

          <div className="bg-slate-950 p-1.5 border border-slate-800 rounded-2xl grid grid-cols-1 sm:grid-cols-3 gap-1.5">
            <button
              type="button"
              onClick={() => setExecutionMode('harvest')}
              className={`py-2.5 px-3 rounded-xl text-xs font-bold font-mono flex items-center justify-center gap-2 transition-all ${
                executionMode === 'harvest'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20 border border-emerald-400/40'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900/60'
              }`}
            >
              <Radio className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="truncate">
                {isUk 
                  ? (isEO ? '1. 🛰️ Живий збір з супутників' : '1. 🚀 Оперативний ШІ-Розрахунок') 
                  : isPl 
                  ? (isEO ? '1. 🛰️ Pobieranie z satelitów' : '1. 🚀 Szybkie obliczenia AI') 
                  : (isEO ? '1. 🛰️ Live STAC Harvester' : '1. 🚀 Fast AI Orbit Solver')}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setExecutionMode('upload')}
              className={`py-2.5 px-3 rounded-xl text-xs font-bold font-mono flex items-center justify-center gap-2 transition-all ${
                executionMode === 'upload'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20 border border-emerald-400/40'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900/60'
              }`}
            >
              <Upload className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="truncate">
                {isUk 
                  ? (isEO 
                      ? '2. 📸 Завантажити знімок' 
                      : domainCategory === 'sda' 
                      ? '2. 📁 Завантажити TLE / Логи' 
                      : domainCategory === 'gnss' 
                      ? '2. 📁 Завантажити RINEX / Логи' 
                      : '2. 📁 Завантажити Сейсмограми') 
                  : isPl 
                  ? (isEO ? '2. 📸 Prześlij zdjęcie' : '2. 📁 Prześlij pliki / logi') 
                  : (isEO ? '2. 📸 Upload Own Image' : '2. 📁 Upload TLE / Logs')}
              </span>
            </button>

            <button
              type="button"
              onClick={() => setExecutionMode('simulate')}
              className={`py-2.5 px-3 rounded-xl text-xs font-bold font-mono flex items-center justify-center gap-2 transition-all ${
                executionMode === 'simulate'
                  ? 'bg-gradient-to-r from-emerald-600 to-teal-600 text-white shadow-lg shadow-emerald-500/20 border border-emerald-400/40'
                  : 'text-slate-400 hover:text-white hover:bg-slate-900/60'
              }`}
            >
              <Sparkles className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="truncate">
                {isUk 
                  ? (isEO ? '3. 🧠 ШІ-Моделювання' : '3. 🧠 Глибоке Моделювання') 
                  : isPl 
                  ? '3. 🧠 Modelowanie AI' 
                  : '3. 🧠 Deep AI Simulation'}
              </span>
            </button>
          </div>
        </div>

        {/* MODE 2: LOCAL IMAGE UPLOAD & VISION AI OR DATA LOGS */}
        {executionMode === 'upload' && (
          <div className="bg-slate-950/80 border border-emerald-500/30 rounded-xl p-5 space-y-4 shadow-md">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 border-b border-slate-800 pb-2.5">
              <h3 className="text-xs font-bold text-emerald-400 font-mono flex items-center gap-2">
                <Upload className="w-4 h-4 text-emerald-400" />
                <span>
                  {isUk 
                    ? (isEO
                        ? '📸 ЗАВАНТАЖЕННЯ ВЛАСНИХ ЗНІМКІВ (Gemini 3.7 Vision AI & ДСТУ 4163:2020)'
                        : domainCategory === 'sda'
                        ? '📁 ЗАВАНТАЖЕННЯ ДАНИХ TLE, CDM, ЕФЕМЕРИД АБО БОРТОВОЇ ТЕЛЕМЕТРІЇ (Gemini 3.7 & ДСТУ 4163:2020)'
                        : domainCategory === 'gnss'
                        ? '📁 ЗАВАНТАЖЕННЯ RINEX, NMEA ТА НАВІГАЦІЙНИХ ЛОГІВ (Gemini 3.7 & ДСТУ 4163:2020)'
                        : '📁 ЗАВАНТАЖЕННЯ СЕЙСМОГРАМ, МІКРОБАРОГРАМ ТА ДАНИХ CTBTO (Gemini 3.7 & ДСТУ 4163:2020)')
                    : isPl 
                    ? (isEO ? '📸 PRZESYŁANIE WŁASNYCH ZDJĘĆ (Gemini Vision)' : '📁 PRZESYŁANIE PLIKÓW TLE / LOGÓW (Gemini)') 
                    : (isEO ? '📸 DIRECT SATELLITE IMAGES UPLOAD (Gemini 3.7 Vision)' : '📁 DIRECT ORBITAL DATA / TLE / LOGS UPLOAD (Gemini 3.7)')}
                </span>
              </h3>
              <span className="px-2 py-0.5 bg-emerald-500/10 text-emerald-300 text-[10px] font-mono border border-emerald-500/20 rounded">
                {isEO 
                  ? 'GeoTIFF • PNG • JPG • WebP • PDF • Мульти-знімки' 
                  : domainCategory === 'sda' 
                  ? 'TLE • CDM • OEM • OMM • CSV • TXT • PDF • DOCX' 
                  : domainCategory === 'gnss' 
                  ? 'RINEX (RNX/OBS/NAV) • NMEA • TXT • LOG • PDF' 
                  : 'SEED • MiniSEED • SAC • TXT • DAT • PDF'}
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* 1. Main Satellite Images / Map Files / Log Files Input */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-slate-300 font-mono flex items-center justify-between">
                  <span>
                    {isUk 
                      ? (isEO 
                          ? '1. 📸 Супутникові знімки / карти (GeoTIFF, PNG, JPG, WebP, PDF):' 
                          : domainCategory === 'sda'
                          ? '1. 📁 Файли TLE, CDM повідомлень або телеметрії (TXT, TLE, CDM, CSV, PDF):'
                          : domainCategory === 'gnss'
                          ? '1. 📁 Файли RINEX спостережень або навігаційних логів (RNX, OBS, NAV, TXT):'
                          : '1. 📁 Файли сейсмограм або мікробарограм (SEED, SAC, TXT, DAT):')
                      : isPl 
                      ? (isEO ? '1. 📸 Zdjęcia satelitarne / mapy (GeoTIFF, PNG, JPG, WebP):' : '1. 📁 Pliki danych / logów (TLE, RINEX, CSV, TXT):') 
                      : (isEO ? '1. 📸 Satellite images / maps (GeoTIFF, PNG, JPG, WebP, PDF):' : '1. 📁 Data files / logs (TLE, CDM, RINEX, CSV, TXT):')}
                  </span>
                  <span className="text-[10px] text-emerald-400 font-mono">можна обрати кілька</span>
                </label>
                <input
                  type="file"
                  multiple
                  accept={isEO 
                    ? "image/png,image/jpeg,image/webp,image/tiff,image/tif,application/pdf"
                    : ".txt,.tle,.cdm,.oem,.omm,.rnx,.obs,.nav,.nmea,.log,.seed,.mseed,.sac,.csv,.json,.pdf,.docx,.xlsx"}
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      const newFiles = Array.from(e.target.files);
                      setSelectedFiles((prev) => [...prev, ...newFiles]);
                    }
                  }}
                  className="w-full text-xs text-slate-300 font-mono file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-emerald-500/20 file:text-emerald-300 hover:file:bg-emerald-500/30 file:cursor-pointer cursor-pointer bg-slate-900 border border-slate-800 rounded-xl p-1"
                />

                {selectedFiles.length > 0 && (
                  <div className="space-y-1.5 mt-2 max-h-48 overflow-y-auto pr-1">
                    <div className="flex items-center justify-between text-[10px] font-mono text-slate-400 border-b border-slate-800 pb-1">
                      <span className="text-emerald-400 font-semibold">
                        ✓ Обрано файлів: {selectedFiles.length} (сумарно {(selectedFiles.reduce((acc, f) => acc + f.size, 0) / 1024 / 1024).toFixed(2)} MB)
                      </span>
                      <button
                        type="button"
                        onClick={() => setSelectedFiles([])}
                        className="text-rose-400 hover:underline cursor-pointer"
                      >
                        {isUk ? 'Очистити всі' : isPl ? 'Wyczyść wszystkie' : 'Clear all'}
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-1.5">
                      {selectedFiles.map((file, idx) => (
                        <div
                          key={idx}
                          className="flex items-center justify-between px-2.5 py-1.5 bg-slate-950/80 border border-slate-800 rounded-lg text-xs font-mono"
                        >
                          <div className="flex items-center gap-2 truncate">
                            <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 text-[10px] font-bold">
                              #{idx + 1}
                            </span>
                            <span className="truncate text-slate-200" title={file.name}>
                              {file.name}
                            </span>
                            <span className="text-[10px] text-slate-400 whitespace-nowrap">
                              ({(file.size / 1024 / 1024).toFixed(2)} MB)
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => setSelectedFiles((prev) => prev.filter((_, i) => i !== idx))}
                            className="text-slate-500 hover:text-rose-400 text-xs px-1 cursor-pointer"
                            title="Видалити цей файл"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* 2. Thematic Type Selector */}
              <div className="space-y-1.5">
                <label className="block text-[11px] font-bold text-slate-300 font-mono">
                  {isUk ? '2. 🔍 Профіль аналізу / алгоритм обробки:' : '2. 🔍 Processing Profile & Analysis Algorithm:'}
                </label>
                <select
                  value={thematicType}
                  onChange={(e) => setThematicType(e.target.value)}
                  className="w-full px-3 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-slate-200 text-xs font-mono focus:outline-none focus:border-emerald-500/50 cursor-pointer"
                >
                  <option value="auto_general">{isUk ? '🤖 Комплексний автоматичний аналіз даних' : '🤖 Complex Auto Processing'}</option>
                  {isEO && (
                    <>
                      <option value="war_damage">{isUk ? '🛡️ Військово-оціночна (GEOINT & RDNA3 руйнування, вирви, техніка)' : '🛡️ GEOINT & War Damage Assessment (RDNA3)'}</option>
                      <option value="sar_radar">{isUk ? '📡 Радарна SAR (всепогодний аналіз, InSAR деформації)' : '📡 SAR Radar (All-weather, InSAR Displacement)'}</option>
                      <option value="agri_ndvi">{isUk ? '🌾 Сільськогосподарська (NDVI, EVI, SAVI, стрес посівів)' : '🌾 Agricultural (NDVI, EVI, Crop Stress)'}</option>
                      <option value="eco_burn">{isUk ? '🌲 Екологічна (NBR, пожежі, згарища, дефорестація, повені)' : '🌲 Ecological (NBR, Wildfires, Deforestation, Floods)'}</option>
                      <option value="urban_infrastructure">{isUk ? '🏗️ Містобудівна (NDBI, забудова, дороги, LST тепло)' : '🏗️ Urban & Infrastructure (NDBI, Heat Islands)'}</option>
                      <option value="geology_mining">{isUk ? '⛏️ Геологічна (кар’єри, видобуток породи, мінеральні аномалії)' : '⛏️ Geological & Mining (Open-pit, Minerals)'}</option>
                      <option value="hydro_water">{isUk ? '🌊 Гідрологічна (MNDWI, розливи річок, затоплення, акваторії)' : '🌊 Hydrological (MNDWI, Water Inundation)'}</option>
                    </>
                  )}
                  {domainCategory === 'sda' && (
                    <>
                      <option value="conjunction_assessment">{isUk ? '🛸 Оцінка зближень та ризику зіткнення (Conjunction & PoC)' : '🛸 Conjunction & Collision Risk Assessment'}</option>
                      <option value="cam_maneuver">{isUk ? '🚀 Розрахунок імпульсу маневру ухилення (CAM & ΔV)' : '🚀 Collision Avoidance Maneuver (CAM & ΔV)'}</option>
                      <option value="telemetry_health">{isUk ? '🔋 Аудит підсистем КА за телеметрією (EPS, ADCS, Thermal)' : '🔋 Subsystem Health Audit via Telemetry'}</option>
                      <option value="solar_weather">{isUk ? '☀️ Аналіз сонячних спалахів та геомагнітної обстановки' : '☀️ Solar Flare & Geomagnetic Environment Analysis'}</option>
                    </>
                  )}
                  {domainCategory === 'gnss' && (
                    <>
                      <option value="gnss_jamming">{isUk ? '📡 Детекція завад РЕБ, джамінгу та спуфінгу навігації' : '📡 EW Jamming & Spoofing Detection'}</option>
                      <option value="rinex_differential">{isUk ? '📍 Диференційна обробка RINEX та сантиметрова точність' : '📍 RINEX Differential Post-Processing'}</option>
                      <option value="time_metrology">{isUk ? '⏱️ Метрологічний аудит шкали часу UTC(UA) та частоти' : '⏱️ UTC(UA) Time Scale & Frequency Audit'}</option>
                    </>
                  )}
                  {domainCategory === 'seismic' && (
                    <>
                      <option value="seismic_ctbto">{isUk ? '🌋 Сейсмічна класифікація P/S хвиль та вибухів (CTBTO)' : '🌋 Seismic Wave Discrimination (CTBTO)'}</option>
                      <option value="infrasound_blast">{isUk ? '🎤 Інфразвукова пеленгація вибухів та пусків' : '🎤 Infrasound Blast Triangulation'}</option>
                      <option value="radiation_gamma">{isUk ? '☢️ Дозиметричний моніторинг гамма-фону та радіонуклідів' : '☢️ Gamma Background & Fallout Audit'}</option>
                    </>
                  )}
                </select>
              </div>
            </div>

            {/* 3. Detailed Custom Task Description Input */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-300 font-mono flex items-center justify-between">
                <span>{isUk ? '3. ✍️ Опис завдання / Додаткові вказівки для аналітика:' : '3. ✍️ Custom Task Description & Analyst Instructions:'}</span>
                <span className="text-[10px] text-slate-400 font-sans">{isUk ? '(необов’язково)' : '(optional)'}</span>
              </label>
              <textarea
                rows={3}
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                placeholder={isUk 
                  ? (isEO
                      ? "Опишіть, що саме необхідно виявити або оцінити (наприклад: 'Виявити нові фортифікації, порівняти зміни до/після, оцінити ступінь пошкоджень об’єкта за BDA, обчислити NDVI для ділянки...')."
                      : domainCategory === 'sda'
                      ? "Опишіть орбітальне завдання (наприклад: 'Оцінити ризик зіткнення супутника Січ-2-30 із уламками Cosmos-1408 за завантаженим TLE, розрахувати оптимальний час маневру CAM')."
                      : domainCategory === 'gnss'
                      ? "Опишіть навігаційне завдання (наприклад: 'Виявити аномальні фазові зсуви в RINEX файлі та локалізувати джерело джамінгу GPS L1')."
                      : "Опишіть сейсмологічне завдання (наприклад: 'Перевірити спектр події на відповідність ядерному випробуванню за нормами CTBTO').")
                  : "Describe what needs to be detected or measured..."}
                className="w-full px-3.5 py-2.5 bg-slate-900 border border-slate-800 rounded-xl text-slate-100 text-xs font-mono focus:outline-none focus:border-emerald-500/50 resize-y"
              />
            </div>

            {/* 4. Supporting Documents Multi-File Upload Input */}
            <div className="space-y-1.5">
              <label className="block text-[11px] font-bold text-slate-300 font-mono flex items-center justify-between">
                <span>{isUk ? '4. 📑 Підгрузка додаткових документів та матеріалів (PDF, DOCX, XLSX, TXT, GeoJSON, CSV):' : '4. 📑 Upload supporting documents & materials (PDF, DOCX, XLSX, TXT, GeoJSON, CSV):'}</span>
                <span className="text-[10px] text-emerald-400 font-mono">до 5 файлів</span>
              </label>
              <input
                type="file"
                multiple
                accept=".pdf,.docx,.xlsx,.txt,.json,.geojson,.csv,.kml,.tle,.rnx"
                onChange={(e) => {
                  if (e.target.files) {
                    setSupportFiles(Array.from(e.target.files));
                  }
                }}
                className="w-full text-xs text-slate-300 font-mono file:mr-3 file:py-2 file:px-4 file:rounded-xl file:border-0 file:text-xs file:font-semibold file:bg-slate-800 file:text-slate-200 hover:file:bg-slate-700 file:cursor-pointer cursor-pointer bg-slate-900 border border-slate-800 rounded-xl p-1"
              />
              {supportFiles.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {supportFiles.map((file, idx) => (
                    <span key={idx} className="px-2.5 py-1 bg-slate-900 border border-slate-700 text-emerald-300 text-[10px] font-mono rounded-lg flex items-center gap-1.5">
                      <span>📄 {file.name}</span>
                      <span className="text-slate-400">({(file.size / 1024).toFixed(0)} KB)</span>
                    </span>
                  ))}
                  <button
                    type="button"
                    onClick={() => setSupportFiles([])}
                    className="text-[10px] text-rose-400 hover:underline font-mono ml-1"
                  >
                    {isUk ? 'Очистити документи' : 'Clear docs'}
                  </button>
                </div>
              )}
            </div>

            <button
              type="button"
              onClick={handleRunThematicUpload}
              disabled={isAuditing || selectedFiles.length === 0}
              className={`w-full py-3.5 rounded-xl font-bold text-xs shadow-lg flex items-center justify-center gap-2 transition-all font-mono uppercase ${
                isAuditing || selectedFiles.length === 0
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                  : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-emerald-600/20 cursor-pointer border border-emerald-400/30'
              }`}
            >
              {isUploadingThematic ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>
                    {isUk 
                      ? `Триває комплексний аналіз ${selectedFiles.length > 1 ? `${selectedFiles.length} файлів` : 'файлу'} у Gemini...` 
                      : `Analyzing ${selectedFiles.length} file(s) with Gemini...`}
                  </span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>
                    {isUk 
                      ? `🚀 Обробити завантажені дані ${selectedFiles.length > 0 ? `(${selectedFiles.length} шт.)` : ''} через Gemini 3.7 та згенерувати звіт ДСТУ` 
                      : isPl 
                      ? `🚀 Przetwórz przesłane dane ${selectedFiles.length > 0 ? `(${selectedFiles.length} szt.)` : ''} przez Gemini` 
                      : `🚀 Process Uploaded Data ${selectedFiles.length > 0 ? `(${selectedFiles.length})` : ''} with Gemini 3.7`}
                  </span>
                </>
              )}
            </button>
          </div>
        )}

        {/* MODE 1 & MODE 3: HARVEST OR SIMULATE FORMS */}
        {executionMode !== 'upload' && (
          <>
            {agentId === 'sat-drought-water-stress-auditor' && (
              <div className="mb-5 p-4 rounded-2xl bg-gradient-to-br from-slate-950 via-cyan-950/20 to-slate-900 border border-cyan-500/30 shadow-xl">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-3 pb-3 border-b border-cyan-500/20">
                  <div className="flex items-center gap-2.5">
                    <span className="p-2 rounded-xl bg-cyan-500/10 border border-cyan-500/30 text-cyan-400">
                      <Droplets className="w-5 h-5 animate-pulse" />
                    </span>
                    <div>
                      <h4 className="text-sm font-bold text-slate-100 flex items-center gap-2">
                        <span>🗺️ Інтерактивний Селектор Басейнів Річок України</span>
                        <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 font-mono border border-cyan-500/40">
                          Sentinel-2 SWIR + TerraClimate
                        </span>
                      </h4>
                      <p className="text-[11px] text-slate-400">
                        Оберіть цільовий гідрографічний басейн в 1 клік для завантаження геоданих та метрик водного стресу
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href="/nis-eco-map.html"
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-xl bg-cyan-600/30 hover:bg-cyan-600/50 text-cyan-200 border border-cyan-500/40 text-xs font-semibold flex items-center gap-1.5 transition-all shadow-sm cursor-pointer"
                    >
                      <Globe className="w-3.5 h-3.5" />
                      <span>Ситуаційна Карта НІС-ЕКО ↗</span>
                    </a>
                  </div>
                </div>

                {/* 6 Basin Cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2.5">
                  {[
                    {
                      id: 'ros',
                      name: 'Басейн річки Рось',
                      loc: 'Біла Церква (Київська/Черкаська обл.)',
                      badge: '🔴 ALERT',
                      badgeColor: 'bg-red-500/20 text-red-300 border-red-500/40',
                      ndmi: '-0.094',
                      spei: '-3.42',
                      pdsi: '-5.00',
                      deficit: '-222 мм',
                      region: 'Київська обл., м. Біла Церква, басейн р. Рось (49.79° N, 30.11° E)',
                      context: 'Оцінка ризиків зневоднення екосистеми, падіння тургору біомаси та загрози дефіциту питного водопостачання міста Біла Церква.'
                    },
                    {
                      id: 'dnipro',
                      name: 'Басейн річки Дніпро',
                      loc: 'Середнє та Нижнє Подніпров\'я',
                      badge: '🔴 ALERT',
                      badgeColor: 'bg-red-500/20 text-red-300 border-red-500/40',
                      ndmi: '-0.037',
                      spei: '-3.50',
                      pdsi: '-5.00',
                      deficit: '-245 мм',
                      region: 'Дніпропетровська/Запорізька обл., басейн р. Дніпро (48.46° N, 35.04° E)',
                      context: 'Моніторинг наслідків зміління після руйнації Каховської ГЕС, зрошення півдня та рівнів води водозаборів.'
                    },
                    {
                      id: 'dnister',
                      name: 'Басейн річки Дністер',
                      loc: 'Подільсько-Карпатський сектор',
                      badge: '🟠 WARNING',
                      badgeColor: 'bg-amber-500/20 text-amber-300 border-amber-500/40',
                      ndmi: '+0.228',
                      spei: '-1.08',
                      pdsi: '-5.00',
                      deficit: '-70 мм',
                      region: 'Вінницька/Хмельницька обл., басейн р. Дністер (48.45° N, 27.78° E)',
                      context: 'Оцінка водності Дністровського комплексного гідровузла, баланс опадів та випаровуваності SPEI-3.'
                    },
                    {
                      id: 'tisza',
                      name: 'Басейн річки Тиса',
                      loc: 'Закарпаття, Карпатський водозбір',
                      badge: '🟢 NORMAL',
                      badgeColor: 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40',
                      ndmi: '+0.355',
                      spei: '+0.31',
                      pdsi: '+3.47',
                      deficit: '+20 мм',
                      region: 'Закарпатська обл., басейн р. Тиса (48.17° N, 23.30° E)',
                      context: 'Оцінка тургору гірських лісів, карпатського стоку та соляного карсту в Солотвино.'
                    },
                    {
                      id: 'pivdennyi_buh',
                      name: 'Басейн Південного Бугу',
                      loc: 'Миколаївщина / ПАЕС',
                      badge: '🔴 ALERT',
                      badgeColor: 'bg-red-500/20 text-red-300 border-red-500/40',
                      ndmi: '-0.107',
                      spei: '-3.50',
                      pdsi: '-5.00',
                      deficit: '-250 мм',
                      region: 'Миколаївська обл., басейн Південного Бугу (48.04° N, 30.85° E)',
                      context: 'Оцінка дефіциту охолоджувальних вод ПАЕС та питного постачання Миколаєва.'
                    },
                    {
                      id: 'siverskyi_donets',
                      name: 'Басейн Сіверського Дінця',
                      loc: 'Харківщина / Східний Донбас',
                      badge: '🔴 ALERT',
                      badgeColor: 'bg-red-500/20 text-red-300 border-red-500/40',
                      ndmi: '-0.185',
                      spei: '-3.50',
                      pdsi: '-5.00',
                      deficit: '-275 мм',
                      region: 'Харківська/Донецька обл., басейн Сіверського Дінця (49.20° N, 37.28° E)',
                      context: 'Аудит стану каналу Сіверський Донець — Донбас та зон пожеж прилеглих лісів.'
                    }
                  ].map((basin) => {
                    const isSelected = regionName.includes(basin.name) || regionName === basin.region;
                    return (
                      <div
                        key={basin.id}
                        onClick={() => {
                          setRegionName(basin.region);
                          setMonitoringType('Оперативний супутниковий аудит водного стресу та дефіциту вологи');
                          setTimeFrame('Червень – Серпень 2024 р. (порівняно з 2021 р.)');
                          setAdditionalContext(basin.context);
                        }}
                        className={`p-3 rounded-xl border transition-all cursor-pointer text-left ${
                          isSelected
                            ? 'bg-cyan-950/80 border-cyan-400 shadow-lg shadow-cyan-900/30 ring-1 ring-cyan-400'
                            : 'bg-slate-900/70 border-slate-800 hover:border-cyan-500/40 hover:bg-slate-850'
                        }`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-bold text-slate-200">{basin.name}</span>
                          <span className={`text-[9px] px-1.5 py-0.5 rounded font-mono font-bold border ${basin.badgeColor}`}>
                            {basin.badge}
                          </span>
                        </div>
                        <div className="text-[10px] text-slate-400 mb-2 truncate">{basin.loc}</div>
                        <div className="grid grid-cols-3 gap-1 pt-1.5 border-t border-slate-800/80 text-[10px] font-mono">
                          <div>
                            <span className="text-slate-500 block">NDMI:</span>
                            <span className={basin.ndmi.startsWith('-') ? 'text-red-400 font-bold' : 'text-emerald-400 font-bold'}>
                              {basin.ndmi}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500 block">SPEI-3:</span>
                            <span className="text-amber-300 font-semibold">{basin.spei}</span>
                          </div>
                          <div>
                            <span className="text-slate-500 block">Дефіцит:</span>
                            <span className="text-cyan-300 font-semibold">{basin.deficit}</span>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {/* Official Damage Act Download Bar */}
                <div className="mt-3.5 pt-3 border-t border-cyan-500/20 flex flex-wrap items-center justify-between gap-2.5">
                  <div className="flex items-center gap-2 text-xs text-slate-300">
                    <span className="px-2 py-0.5 rounded bg-red-500/20 text-red-400 font-bold font-mono text-[10px] border border-red-500/30">
                      ⚖️ НАКАЗ МІНДОВКІЛЛЯ № 252
                    </span>
                    <span className="text-[11px] text-slate-300">
                      Офіційний Розрахунковий Акт екологічних збитків маловоддя та обміління Росі (221 млн грн):
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <a
                      href="/api/satellite/download-water-damage-act?format=docx"
                      download="Акт_Розрахунку_Екологічних_Збитків_Водозабору_Рось_версія1_2026-09-29.docx"
                      className="px-3 py-1.5 rounded-xl bg-gradient-to-r from-red-600 to-rose-700 hover:from-red-500 hover:to-rose-600 text-white text-xs font-bold font-mono flex items-center gap-1.5 transition-all shadow-md shadow-red-950/30 cursor-pointer"
                    >
                      <Download className="w-3.5 h-3.5" />
                      <span>Завантажити Акт (.docx за ДСТУ)</span>
                    </a>
                    <a
                      href="/api/satellite/download-water-damage-act?format=md"
                      download="Акт_Розрахунку_Екологічних_Збитків_Водозабору_Рось_версія1_2026-09-29.md"
                      className="px-2.5 py-1.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-slate-300 hover:text-white border border-slate-700 text-xs font-mono flex items-center gap-1 transition-all cursor-pointer"
                    >
                      <span>.md</span>
                    </a>
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Field 1: Region / Target Object / NORAD ID / Station */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-300 font-mono">
                    {isUk
                      ? (isEO
                          ? "Цільовий регіон / Об'єкт / Координати:"
                          : domainCategory === 'sda'
                          ? "🛰️ Цільовий КА / Об'єкт спостереження / NORAD ID:"
                          : domainCategory === 'gnss'
                          ? "📍 Станція GNSS / Мережа RTK / Сектор навігації:"
                          : "🌋 Сейсмічна станція / Обсерваторія / Гео-сектор:")
                      : isPl
                      ? (isEO
                          ? "Region docelowy / Obiekt / Współrzędne:"
                          : domainCategory === 'sda'
                          ? "🛰️ Satelita / Obiekt / NORAD ID:"
                          : domainCategory === 'gnss'
                          ? "📍 Stacja GNSS / Sieć RTK / Sektor:"
                          : "🌋 Stacja sejsmiczna / Obserwatorium:")
                      : (isEO
                          ? "Target Region / Object / Coordinates:"
                          : domainCategory === 'sda'
                          ? "🛰️ Target Spacecraft / Object / NORAD ID:"
                          : domainCategory === 'gnss'
                          ? "📍 GNSS Station / RTK Network / Nav Sector:"
                          : "🌋 Seismic Station / Observatory / Geo Sector:")}
                  </label>
                  <span className="text-[10px] text-emerald-400 font-mono">📋 {isUk ? 'або оберіть зі списку' : isPl ? 'lub wybierz z listy' : 'or select from list'}</span>
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) setRegionName(e.target.value);
                  }}
                  value={getRegionOptions(agentId).some(o => o.value === regionName) ? regionName : ''}
                  className="w-full mb-2 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-emerald-500/50 cursor-pointer"
                >
                  <option value="">{isUk ? '🔽 -- Швидкий вибір цільового об’єкта / станції --' : isPl ? '🔽 -- Szybki wybór obiektu / stacji --' : '🔽 -- Quick Select Target Object / Station --'}</option>
                  {getRegionOptions(agentId).map((opt, i) => (
                    <option key={i} value={opt.value}>
                      {isUk ? opt.labelUk : opt.labelEn}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={regionName}
                  onChange={(e) => setRegionName(e.target.value)}
                  placeholder={isUk 
                    ? (isEO
                        ? "Введіть назву регіону / об'єкта або координати WGS-84..."
                        : domainCategory === 'sda'
                        ? "Супутник Січ-2-30 (NORAD 51053, LEO 525 км), Starlink-30125, або ISS..."
                        : domainCategory === 'gnss'
                        ? "Базова станція CORS Київ (KYIV00UKR), або сектор навігації..."
                        : "Сейсмостанція Городок (ГЦСК), зона Вранча, або координати...")
                    : isPl 
                    ? "Wpisz nazwę obiektu lub współrzędne..." 
                    : "Type target object, spacecraft or coordinates..."}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500/50 font-mono"
                />

                {/* Hint Badge with Clickable Chips */}
                <div className="mt-2.5 p-2.5 rounded-xl bg-slate-900/90 border border-emerald-500/20 text-[11px] font-mono leading-relaxed">
                  <div className="flex items-center gap-1.5 mb-1.5 text-emerald-300 font-semibold">
                    <span className="px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40 text-[10px] uppercase tracking-wider">
                      💡 {isUk ? 'Підказка' : isPl ? 'Wskazówka' : 'Hint'}
                    </span>
                    <span className="text-slate-300">
                      {isUk 
                        ? (isEO ? 'Клікніть для підстановки регіону чи координат:' : 'Клікніть для швидкої підстановки цільового об’єкта:') 
                        : 'Click to auto-populate target parameters:'}
                    </span>
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5 text-slate-300">
                    {agentId === 'sat-drought-water-stress-auditor' ? (
                      <>
                        <span className="text-cyan-400 font-semibold">💧 Гідробасейни:</span>
                        <button
                          type="button"
                          onClick={() => {
                            setRegionName('Київська обл., м. Біла Церква, басейн р. Рось (49.79° N, 30.11° E)');
                            setMonitoringType('Оперативний супутниковий аудит водного стресу та дефіциту вологи');
                            setTimeFrame('Червень – Серпень 2024 р. (порівняно з 2021 р.)');
                            setAdditionalContext('Оцінка ризиків зневоднення екосистеми, падіння тургору біомаси та загрози дефіциту питного водопостачання міста Біла Церква.');
                          }}
                          className="px-1.5 py-0.5 rounded bg-red-950/80 hover:bg-red-900 text-red-300 border border-red-500/50 transition-colors font-medium cursor-pointer"
                        >
                          🔴 р. Рось
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRegionName('Дніпропетровська/Запорізька обл., басейн р. Дніпро (48.46° N, 35.04° E)');
                            setMonitoringType('Оцінка водного балансу каскаду водосховищ Дніпра');
                            setTimeFrame('Травень – Вересень 2024 р.');
                            setAdditionalContext('Моніторинг наслідків зміління після руйнації Каховської ГЕС, зрошення півдня та рівнів води водозаборів.');
                          }}
                          className="px-1.5 py-0.5 rounded bg-red-950/80 hover:bg-red-900 text-red-300 border border-red-500/50 transition-colors font-medium cursor-pointer"
                        >
                          🔴 р. Дніпро
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRegionName('Вінницька/Хмельницька обл., басейн р. Дністер (48.45° N, 27.78° E)');
                            setMonitoringType('Гідрологічний та кліматичний моніторинг Дністровського басейну');
                            setTimeFrame('Літо 2024 р.');
                            setAdditionalContext('Оцінка водності Дністровського комплексного гідровузла, баланс опадів та випаровуваності SPEI-3.');
                          }}
                          className="px-1.5 py-0.5 rounded bg-amber-950/80 hover:bg-amber-900 text-amber-300 border border-amber-500/50 transition-colors font-medium cursor-pointer"
                        >
                          🟠 р. Дністер
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRegionName('Закарпатська обл., басейн р. Тиса (48.17° N, 23.30° E)');
                            setMonitoringType('Транскордонний екологічний моніторинг водності Тиси');
                            setTimeFrame('Червень – Серпень 2024 р.');
                            setAdditionalContext('Оцінка тургору гірських лісів, карпатського стоку та соляного карсту в Солотвино.');
                          }}
                          className="px-1.5 py-0.5 rounded bg-emerald-950/80 hover:bg-emerald-900 text-emerald-300 border border-emerald-500/50 transition-colors font-medium cursor-pointer"
                        >
                          🟢 р. Тиса
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRegionName('Миколаївська обл., басейн Південного Бугу (48.04° N, 30.85° E)');
                            setMonitoringType('Водний стрес басейну Південного Бугу та Олександрівського водосховища');
                            setTimeFrame('Літо 2024 р.');
                            setAdditionalContext('Оцінка дефіциту охолоджувальних вод ПАЕС та питного постачання Миколаєва.');
                          }}
                          className="px-1.5 py-0.5 rounded bg-red-950/80 hover:bg-red-900 text-red-300 border border-red-500/50 transition-colors font-medium cursor-pointer"
                        >
                          🔴 Пд. Буг
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setRegionName('Харківська/Донецька обл., басейн Сіверського Дінця (49.20° N, 37.28° E)');
                            setMonitoringType('Моніторинг маловоддя та гідротехнічного стресу Сіверського Дінця');
                            setTimeFrame('Літо 2024 р.');
                            setAdditionalContext('Аудит стану каналу Сіверський Донець — Донбас та зон пожеж прилеглих лісів.');
                          }}
                          className="px-1.5 py-0.5 rounded bg-red-950/80 hover:bg-red-900 text-red-300 border border-red-500/50 transition-colors font-medium cursor-pointer"
                        >
                          🔴 Сів. Донець
                        </button>
                        <span className="text-slate-600 mx-0.5">|</span>
                        <a
                          href="/nis-eco-map.html"
                          target="_blank"
                          rel="noreferrer"
                          className="px-2 py-0.5 rounded bg-cyan-950/90 hover:bg-cyan-900 text-cyan-300 border border-cyan-500/50 transition-colors text-xs font-semibold flex items-center gap-1 inline-flex"
                        >
                          🗺️ Ситуаційна Карта НІС-ЕКО ↗
                        </a>
                      </>
                    ) : isEO ? (
                      <>
                        <span className="text-slate-400">🗺️ {isUk ? 'Регіон:' : 'Region:'}</span>
                        <button
                          type="button"
                          onClick={() => setRegionName('Карпатський НПП (Яремче — Ворохта — Рахів)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Карпати
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Київська область, Бучанський район')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Буча / Ірпінь
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Одеський морський порт / Одеська затока')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Одеса
                        </button>
                        <span className="text-slate-600 mx-0.5">|</span>
                        <span className="text-slate-400">🎯 WGS-84:</span>
                        <button
                          type="button"
                          onClick={() => setRegionName('48.3300 N, 24.4500 E')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-cyan-950 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 transition-colors font-mono cursor-pointer"
                        >
                          48.33°N, 24.45°E
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('50.4500, 30.5200')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-cyan-950 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 transition-colors font-mono cursor-pointer"
                        >
                          50.45, 30.52
                        </button>
                      </>
                    ) : domainCategory === 'sda' ? (
                      <>
                        <span className="text-slate-400">🛰️ КА:</span>
                        <button
                          type="button"
                          onClick={() => setRegionName('Супутник Січ-2-30 (NORAD 51053, LEO 525 км)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Січ-2-30 (LEO)
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('МКС / ISS (NORAD ID 25544, LEO 420 км)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          МКС / ISS
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Сузір’я LEO Starlink (550 км, i=53°)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Starlink LEO
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Геостаціонарна орбіта (GEO 35,786 км, 4.8° E)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-cyan-950 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 transition-colors font-mono cursor-pointer"
                        >
                          GEO Пояс (36k км)
                        </button>
                      </>
                    ) : domainCategory === 'gnss' ? (
                      <>
                        <span className="text-slate-400">📍 GNSS:</span>
                        <button
                          type="button"
                          onClick={() => setRegionName('Національна мережа CORS станцій України')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Мережа CORS
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Базова станція KYIV00UKR (Київ)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          KYIV00UKR
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Чорноморсько-Азовський басейн')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-cyan-950 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 transition-colors font-mono cursor-pointer"
                        >
                          Морський басейн
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="text-slate-400">🌋 Гео-пости:</span>
                        <button
                          type="button"
                          onClick={() => setRegionName('Сейсмоактивна зона Вранча (Карпати)')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          Зона Вранча
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('ГЦКМ Городок, Хмельницька обл.')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          ГЦСК Городок
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Чорнобильська зона & АЕС України')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-emerald-950 text-emerald-400 hover:text-emerald-300 border border-slate-700 hover:border-emerald-500/40 transition-colors cursor-pointer"
                        >
                          ЧАЕС / АЕС
                        </button>
                        <button
                          type="button"
                          onClick={() => setRegionName('Антарктична станція "Академік Вернадський"')}
                          className="px-1.5 py-0.5 rounded bg-slate-800 hover:bg-cyan-950 text-cyan-400 hover:text-cyan-300 border border-slate-700 hover:border-cyan-500/40 transition-colors font-mono cursor-pointer"
                        >
                          ст. Вернадський
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Field 2: Monitoring / Analysis / Calculation Type */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-300 font-mono">
                    {isUk
                      ? (isEO
                          ? "Тип супутникового аналізу / Індекси:"
                          : domainCategory === 'sda'
                          ? "🎯 Вид орбітального розрахунку / Завдання безпеки (SDA):"
                          : domainCategory === 'gnss'
                          ? "📡 Тип навігаційного аудиту / Параметри РЕБ:"
                          : "💥 Вид геофізичного аналізу / Спектральні хвилі:")
                      : isPl
                      ? (isEO ? "Typ analizy satelitarnej / Wskaźniki:" : "Typ obliczeń / Zadanie:")
                      : (isEO ? "Satellite Analysis Type / Indices:" : "Calculation Type / Task:")}
                  </label>
                  <span className="text-[10px] text-emerald-400 font-mono">🔍 {isUk ? 'або оберіть зі списку' : 'or select'}</span>
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) setMonitoringType(e.target.value);
                  }}
                  value={getMonitoringTypeOptions(agentId).some(o => o.value === monitoringType) ? monitoringType : ''}
                  className="w-full mb-2 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-emerald-500/50 cursor-pointer"
                >
                  <option value="">{isUk ? '🔽 -- Швидкий вибір типу розрахунку / алгоритму --' : '🔽 -- Quick Select Analysis Type --'}</option>
                  {getMonitoringTypeOptions(agentId).map((opt, i) => (
                    <option key={i} value={opt.value}>
                      {isUk ? opt.labelUk : opt.labelEn}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={monitoringType}
                  onChange={(e) => setMonitoringType(e.target.value)}
                  placeholder={isUk 
                    ? (isEO 
                        ? "Наприклад: NDVI, NBR, MNDWI, Change Detection..." 
                        : domainCategory === 'sda'
                        ? "Наприклад: Conjunction Assessment, PoC розрахунок, CAM маневр..."
                        : domainCategory === 'gnss'
                        ? "Наприклад: Детекція джамінгу L1/L2, похибка UERE/RAIM..."
                        : "Наприклад: Сейсмічна вибірка P/S хвиль, CTBTO...")
                    : "e.g., Analysis Type / Parameters..."}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500/50 font-mono"
                />
              </div>

              {/* Field 3: Timeframe */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-300 font-mono">
                    {isUk
                      ? (isEO
                          ? "Часовий інтервал / Період спостереження:"
                          : domainCategory === 'sda'
                          ? "⏱️ Горизонт орбітального прогнозування / Епоха TLE:"
                          : domainCategory === 'gnss'
                          ? "⏱️ Інтервал моніторингу навігаційного поля:"
                          : "⏱️ Часовий інтервал реєстрації подій:")
                      : isPl
                      ? (isEO ? "Przedział czasowy / Okres obserwacji:" : "Horyzont czasowy / Epoka TLE:")
                      : (isEO ? "Timeframe / Monitoring Period:" : "Propagation Horizon / TLE Epoch:")}
                  </label>
                  <span className="text-[10px] text-emerald-400 font-mono">📅 {isUk ? 'або оберіть період' : 'or select timeframe'}</span>
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) setTimeFrame(e.target.value);
                  }}
                  value={getTimeframeOptions(agentId).some(o => o.value === timeFrame) ? timeFrame : ''}
                  className="w-full mb-2 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-emerald-500/50 cursor-pointer"
                >
                  <option value="">{isUk ? '🔽 -- Швидкий вибір часового інтервалу --' : '🔽 -- Quick Select Timeframe --'}</option>
                  {getTimeframeOptions(agentId).map((opt, i) => (
                    <option key={i} value={opt.value}>
                      {isUk ? opt.labelUk : opt.labelEn}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={timeFrame}
                  onChange={(e) => setTimeFrame(e.target.value)}
                  placeholder={isUk 
                    ? (isEO ? "Наприклад: Динаміка за 2024 - 2026 рр." : "Наприклад: Прогноз на 72 години (2026 р.)") 
                    : "e.g., 2024 - 2026 timeline / 72h horizon"}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500/50 font-mono"
                />
              </div>

              {/* Field 4: Constellations / Data Sources */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-xs font-medium text-slate-300 font-mono">
                    {isUk
                      ? (isEO
                          ? "Супутникові сузір'я / Джерела даних:"
                          : domainCategory === 'sda'
                          ? "🛸 Каталоги орбіт TLE / Джерела спостереження (Space-Track/НЦУВКЗ):"
                          : domainCategory === 'gnss'
                          ? "📡 Сузір’я GNSS / Базові станції (GPS/Galileo/NTRIP):"
                          : "🌋 Мережа сейсмодатчиків / Пости моніторингу CTBTO:")
                      : isPl
                      ? (isEO ? "Konstelacje satelitarne / Źródła danych:" : "Katalogi orbit TLE / Źródła danych:")
                      : (isEO ? "Satellite Constellations / Sources:" : "Orbit Catalogs TLE / Data Sources:")}
                  </label>
                  <span className="text-[10px] text-emerald-400 font-mono">🛰️ {isUk ? 'оберіть джерело' : 'select source'}</span>
                </div>
                <select
                  onChange={(e) => {
                    if (e.target.value) setSatelliteSources(e.target.value);
                  }}
                  value={getSatelliteSourceOptions(agentId).some(o => o.value === satelliteSources) ? satelliteSources : ''}
                  className="w-full mb-2 px-3 py-2 bg-slate-950 border border-slate-800 rounded-xl text-slate-300 text-xs font-mono focus:outline-none focus:border-emerald-500/50 cursor-pointer"
                >
                  <option value="">{isUk ? '🔽 -- Виберіть джерело даних зі списку --' : '🔽 -- Select Data Source --'}</option>
                  {getSatelliteSourceOptions(agentId).map((opt, i) => (
                    <option key={i} value={opt.value}>
                      {isUk ? opt.labelUk : opt.labelEn}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  value={satelliteSources}
                  onChange={(e) => setSatelliteSources(e.target.value)}
                  placeholder={isUk 
                    ? (isEO ? "Sentinel-2 MSI (10m) + Sentinel-1 SAR..." : "Space-Track TLE + Оптико-електронні комплекси...") 
                    : "Data sources & catalogs..."}
                  className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500/50 font-mono"
                />

                {/* Paid Commercial Satellite Warning Banner (Only for EO) */}
                {isEO && isPaidSourceSelected(satelliteSources) && (
                  <div className="mt-2.5 p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-xl space-y-2 text-xs font-mono text-amber-200 animate-fadeIn">
                    <div className="flex items-center gap-2 font-bold text-amber-400">
                      <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
                      <span>{isUk ? '⚠️ УВАГА: ОБРАНО ПЛАТНІ СУПУТНИКОВІ ЗНІМКИ (0.3м – 3м)!' : '⚠️ WARNING: PAID COMMERCIAL SATELLITE IMAGERY SELECTED!'}</span>
                    </div>
                    <p className="text-[11px] text-slate-300 leading-relaxed font-sans">
                      {isUk 
                        ? 'Ви обрали комерційні супутники з надвисокою роздільною здатністю (0.3м – 3м, такі як Maxar WorldView-3, PlanetScope, SkySat, ICEYE SAR, Pléiades Neo). Ці знімки є платно-архівними та надаються за мінімальною площею замовлення (від 25 км²).'
                        : 'You have selected commercial satellites with high resolution (0.3m – 3m, e.g. Maxar WorldView-3, PlanetScope, SkySat, ICEYE SAR, Pléiades Neo). These are paid commercial assets with a minimum billable order area (from 25 sq. km).'}
                    </p>
                    <div className="p-2.5 bg-slate-950/80 rounded-lg border border-slate-800/80 space-y-1.5 text-[11px]">
                      <div className="font-semibold text-amber-300">
                        {isUk ? '💰 Остаточна підсумкова вартість (за курсом Monobank):' : '💰 Final Total Costs (at Monobank sell rate):'}
                      </div>
                      <ul className="space-y-1 text-slate-300 text-[10px]">
                        <li className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-800/60 pb-1 gap-1">
                          <span>• Maxar WorldView-3 / GeoEye (0.3m VHR):</span>
                          <strong className="text-amber-300 font-mono">~$24.00/км² (мін. 25 км² = від ~$600 / ~26 800 грн за знімок)</strong>
                        </li>
                        <li className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-800/60 pb-1 gap-1">
                          <span>• Airbus Pléiades Neo (0.3m VHR):</span>
                          <strong className="text-amber-300 font-mono">~$20.00/км² (мін. 25 км² = від ~$500 / ~22 400 грн за знімок)</strong>
                        </li>
                        <li className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-800/60 pb-1 gap-1">
                          <span>• Planet SkySat (0.5m VHR Оптика):</span>
                          <strong className="text-amber-300 font-mono">~$16.00/км² (мін. 25 км² = від ~$400 / ~17 900 грн за знімок)</strong>
                        </li>
                        <li className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-slate-800/60 pb-1 gap-1">
                          <span>• ICEYE / Capella High-Res SAR (0.5m Радар):</span>
                          <strong className="text-amber-300 font-mono">~$18.60/км² (мін. 25 км² = від ~$465 / ~20 800 грн за знімок)</strong>
                        </li>
                        <li className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                          <span>• PlanetScope SuperDove (3m Щоденний):</span>
                          <strong className="text-amber-300 font-mono">~$4.65/км² (мін. 25 км² = від ~$116 / ~5 200 грн за знімок)</strong>
                        </li>
                      </ul>
                      <p className="text-[10px] text-emerald-400 pt-1 font-sans">
                        {isUk 
                          ? '💡 Порада: Для 100% безкоштовних знімків (0 грн) обирайте Sentinel-2 MSI (10m), Sentinel-1 SAR або Landsat 8/9.'
                          : '💡 Tip: For 100% free satellite images, select Sentinel-2 MSI (10m), Sentinel-1 SAR, or Landsat 8/9.'}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5 font-mono">
                {isUk
                  ? (isEO
                      ? "Додатковий інженерний / технічний контекст:"
                      : domainCategory === 'sda'
                      ? "📝 Вхідні Two-Line Elements (TLE), повідомлення CDM або опис завдання ухилення:"
                      : domainCategory === 'gnss'
                      ? "📝 Вхідні параметри RINEX, логи завад або координати об’єктів:"
                      : "📝 Параметри сейсмограм, показання мікробарографів або опис події:")
                  : isPl
                  ? "Dodatkowy kontekst techniczny / uwagi:"
                  : "Additional Technical / Engineering Context:"}
              </label>
              <textarea
                value={additionalContext}
                onChange={(e) => setAdditionalContext(e.target.value)}
                rows={3}
                placeholder={isUk 
                  ? (isEO
                      ? "Вкажіть особливі вимоги, кадастрові межі чи специфічні індекси для аналізу..."
                      : domainCategory === 'sda'
                      ? "Вставте Two-Line Elements (TLE рядки 1 та 2) або вкажіть параметри зближення (Miss Distance < 500м, CAM імпульс)..."
                      : domainCategory === 'gnss'
                      ? "Вкажіть особливі вимоги до RTK/NTRIP, параметри маски піднесення супутників, або логи завад РЕБ..."
                      : "Вкажіть параметри реєстрації P/S хвиль, глибину гіпоцентру або тип підозрюваної події...")
                  : "Specify custom requirements or parameters..."}
                className="w-full px-3.5 py-2.5 bg-slate-950 border border-slate-800 rounded-xl text-slate-100 text-sm focus:outline-none focus:border-emerald-500/50 font-mono"
              />
            </div>

            {isEO && executionMode === 'harvest' && (
              <div className="flex items-center gap-2 px-1">
                <input
                  type="checkbox"
                  id="autoDownloadCheck"
                  checked={autoDownloadDevice}
                  onChange={(e) => setAutoDownloadDevice(e.target.checked)}
                  className="w-4 h-4 text-emerald-500 bg-slate-950 border-slate-800 rounded focus:ring-emerald-500 cursor-pointer"
                />
                <label htmlFor="autoDownloadCheck" className="text-xs text-slate-300 font-mono cursor-pointer select-none">
                  {isUk ? '📥 Автоматично скачувати всі викачані знімки прямо на мій пристрій (ZIP-архів на ПК / Смартфон)' : '📥 Auto-download all fetched satellite images directly to my device (ZIP to PC / Phone)'}
                </label>
              </div>
            )}

            <button
              onClick={handleRun}
              disabled={isAuditing || !regionName.trim()}
              className={`w-full py-3.5 rounded-xl font-bold text-sm shadow-xl flex items-center justify-center gap-2 transition-all font-mono uppercase ${
                isAuditing || !regionName.trim()
                  ? 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
                  : 'bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white shadow-emerald-500/20 cursor-pointer border border-emerald-400/30'
              }`}
            >
              {isAuditing ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>
                    {isUk
                      ? (isEO && executionMode === 'harvest'
                          ? '🛰️ Триває пошук та викачування знімків з супутників Copernicus STAC...'
                          : domainCategory === 'sda'
                          ? '🛸 Виконується розрахунок орбіт, розмноження ефемерид та аналіз зближень...'
                          : domainCategory === 'gnss'
                          ? '📡 Виконується аудит навігаційного поля, цілісності ефемерид та завад...'
                          : domainCategory === 'seismic'
                          ? '🌋 Виконується дешифрування сейсмограм та аналіз спектрів CTBTO...'
                          : '🧠 Виконується мультиагентне моделювання місії...')
                      : isPl
                      ? (isEO && executionMode === 'harvest'
                          ? 'Pobieranie zdjęć z satelitów STAC...'
                          : 'Wykonywanie symulacji i obliczeń AI...')
                      : (isEO && executionMode === 'harvest'
                          ? 'Harvesting Satellite Imagery from STAC...'
                          : 'Executing AI Mission Simulation...')}
                  </span>
                </>
              ) : (
                <>
                  {isEO && executionMode === 'harvest' ? (
                    <>
                      <Radio className="w-5 h-5" />
                      <span>{isUk ? `🛰️ Викачати знімки з супутників та проаналізувати (${agentTitle})` : `Harvest Satellite Imagery & Analyze (${agentTitle})`}</span>
                    </>
                  ) : domainCategory === 'sda' ? (
                    <>
                      <Radio className="w-5 h-5" />
                      <span>{isUk ? `🚀 Запустити розрахунок орбіт та аналіз безпеки космосу (SDA)` : `Run Space Domain & Orbit Conjunction Analysis`}</span>
                    </>
                  ) : domainCategory === 'gnss' ? (
                    <>
                      <Compass className="w-5 h-5" />
                      <span>{isUk ? `🚀 Виконати навігаційно-геодезичний аудит (GNSS / РЕБ)` : `Run GNSS Navigation & EW Jamming Audit`}</span>
                    </>
                  ) : domainCategory === 'seismic' ? (
                    <>
                      <Activity className="w-5 h-5" />
                      <span>{isUk ? `🚀 Запустити сейсмічний та геофізичний аналіз (CTBTO)` : `Run CTBTO Seismic & Geophysical Audit`}</span>
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-5 h-5" />
                      <span>{isUk ? `🧠 Запустити аналітичне ШІ-моделювання (${agentTitle})` : `Run AI Simulation (${agentTitle})`}</span>
                    </>
                  )}
                </>
              )}
            </button>
          </>
        )}
      </div>

      {/* Loading Progress */}
      {isAuditing && (
        <div className="bg-slate-900/90 border border-emerald-500/30 rounded-2xl p-6 space-y-4 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="w-6 h-6 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin" />
            <h3 className="text-sm font-semibold text-emerald-400">
              {isUk ? 'Триває мультиагентний космічний аналіз...' : 'Satellite Multi-Agent Execution in Progress...'}
            </h3>
          </div>
          <div className="space-y-2">
            {stepsList.map((step, idx) => (
              <div 
                key={idx} 
                className={`flex items-center gap-2.5 text-xs transition-colors ${
                  idx === loadingStepIndex 
                    ? 'text-emerald-400 font-medium' 
                    : idx < loadingStepIndex 
                      ? 'text-slate-400 line-through opacity-70' 
                      : 'text-slate-600'
                }`}
              >
                <div className={`w-1.5 h-1.5 rounded-full ${idx === loadingStepIndex ? 'bg-emerald-400 animate-ping' : idx < loadingStepIndex ? 'bg-slate-400' : 'bg-slate-700'}`} />
                <span>{step}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error state */}
      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-2xl text-red-400 text-sm flex items-start gap-3">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">{isUk ? 'Помилка виконання:' : 'Execution Error:'}</p>
            <p className="mt-0.5 text-xs text-red-300">{error}</p>
          </div>
        </div>
      )}

      {/* Report view */}
      {report && (
        <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 shadow-2xl space-y-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-b border-slate-800 pb-4">
            <div>
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-emerald-400" />
                <span>{isUk ? 'Підсумковий Космічний Аналітичний Звіт' : 'Final Satellite Intelligence Report'}</span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">
                {regionName} • {monitoringType}
              </p>
            </div>

            <div className="flex items-center gap-2 w-full sm:w-auto flex-wrap">
              <TelegramExportButton
                title={meta.titleUk || 'Супутниковий Звіт & ДЗЗ'}
                content={report || ''}
                agentName={meta.titleUk || 'Центр Космічних Агентів'}
                agentId={agentId}
                isUk={isUk}
                variant="emerald"
              />

              <button
                onClick={handleCopyReport}
                className="flex-1 sm:flex-none px-3 py-2 bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs rounded-xl flex items-center justify-center gap-1.5 transition-colors"
              >
                {copiedReport ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано!' : 'Copied!') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>

              <button
                onClick={handleDownloadDocx}
                disabled={isDownloadingDocx}
                className="flex-1 sm:flex-none px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold rounded-xl flex items-center justify-center gap-1.5 shadow-lg shadow-emerald-500/20 transition-all"
              >
                {isDownloadingDocx ? (
                  <div className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                <span>{isUk ? 'Завантажити Word (.docx)' : 'Download Word (.docx)'}</span>
              </button>
            </div>
          </div>

          {/* Processed Satellite Images Display */}
          {satRenderUrls && satRenderUrls.length > 0 && (
            <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-3">
              <div className="flex items-center justify-between border-b border-slate-800 pb-2">
                <h4 className="text-xs font-bold font-mono text-emerald-400 uppercase flex items-center gap-2">
                  <span>📸 {isUk ? `Опрацьовані супутникові знімки (${satRenderUrls.length} шт.):` : isPl ? `Przetworzone zdjęcia satelitarne (${satRenderUrls.length}):` : `Processed Satellite Images (${satRenderUrls.length}):`}</span>
                </h4>
                <span className="text-[10px] text-slate-400 font-mono">
                  {isUk ? 'Клікніть JPG для збереження' : 'Click JPG to download'}
                </span>
              </div>

              <div className={`grid gap-4 ${satRenderUrls.length === 1 ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                {satRenderUrls.map((url, idx) => {
                  const imgMeta = processedUploadImages[idx];
                  const title = imgMeta?.name || (isUk ? `Знімок #${idx + 1}` : `Frame #${idx + 1}`);
                  return (
                    <div key={idx} className="group bg-slate-900/80 border border-slate-800 rounded-xl p-3 space-y-2">
                      <div className="flex items-center justify-between text-xs font-mono text-slate-300">
                        <span className="font-bold truncate text-emerald-300" title={title}>
                          #{idx + 1}: {title}
                        </span>
                        <button
                          type="button"
                          onClick={() => triggerDeviceDownload(url, `Sat_Frame_${idx + 1}_${Date.now()}.jpg`)}
                          className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-[10px] rounded-lg border border-emerald-500/30 flex items-center gap-1 cursor-pointer transition-colors"
                          title="Завантажити знімок на пристрій"
                        >
                          <Download className="w-3 h-3" />
                          <span>JPG</span>
                        </button>
                      </div>
                      <div className="rounded-lg overflow-hidden border border-slate-800 bg-black/40">
                        <img
                          src={url}
                          alt={`Satellite Render #${idx + 1}`}
                          className="max-h-72 w-full object-contain mx-auto transition-transform group-hover:scale-[1.02]"
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {(!satRenderUrls || satRenderUrls.length === 0) && satRenderUrl && (
            <div className="p-4 bg-slate-950 border border-slate-800 rounded-xl space-y-2">
              <h4 className="text-xs font-bold font-mono text-emerald-400 uppercase">
                {isUk ? 'Оброблений супутниковий знімок:' : 'Processed Satellite Render:'}
              </h4>
              <img src={satRenderUrl} alt="Satellite Render" className="max-h-96 rounded-lg object-contain border border-slate-800 mx-auto" />
            </div>
          )}

          {harvestedItems.length > 0 && (
            <div className="p-5 bg-slate-950 border border-emerald-500/30 rounded-2xl space-y-4 shadow-xl">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-800 pb-3">
                <div>
                  <h3 className="text-xs font-bold font-mono text-emerald-400 uppercase flex items-center gap-2">
                    <Download className="w-4 h-4 text-emerald-400" />
                    <span>{isUk ? '🖼️ ВИКАЧАНІ СУПУТНИКОВІ ЗНІМКИ НА ВАШ ПРИСТРІЙ' : '🖼️ HARVESTED SATELLITE IMAGES FOR DEVICE DOWNLOAD'}</span>
                  </h3>
                  <p className="text-[10px] text-slate-400 font-mono mt-0.5">
                    {isUk ? 'Знімки збережено та підготовлено для миттєвого збереження на ПК або Смартфон' : 'Ready for direct download to PC or Phone'}
                  </p>
                  <div className="mt-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-xs font-mono text-emerald-300 flex items-center gap-1.5">
                    <span>📍 {isUk ? 'Локація кадру:' : 'Frame location:'}</span>
                    <strong className="text-white font-bold">{regionName}</strong>
                  </div>
                </div>

                <button
                  onClick={handleDownloadZipBundle}
                  disabled={isZipping}
                  className="px-4 py-2 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-mono text-xs font-bold rounded-xl flex items-center gap-2 shadow-lg transition-all"
                >
                  {isZipping ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <Download className="w-4 h-4" />
                  )}
                  <span>{isUk ? '📦 Завантажити всі ZIP-архівом на ПК' : '📦 Download ALL as ZIP to PC'}</span>
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {harvestedItems.map((item, idx) => {
                  const formatRawDate = (raw?: string) => {
                    if (!raw) return '15.04.2026 10:30 UTC';
                    const clean = raw.replace('Z', '').replace('T', ' ').trim();
                    const m = clean.match(/^(\d{4})-(\d{2})-(\d{2})(?:\s+(\d{2}:\d{2}(?::\d{2})?))?/);
                    if (m) {
                      const [, y, mo, d, t] = m;
                      const timeStr = t ? `${t} UTC` : '10:30 UTC';
                      return `${d}.${mo}.${y} ${timeStr}`;
                    }
                    return clean;
                  };

                  return (
                    <div key={idx} className="bg-slate-900 border border-slate-800 rounded-xl p-3.5 space-y-2">
                      <div className="flex items-center justify-between text-xs font-mono">
                        <span className="font-bold text-white">Кадр №{idx + 1}: {item.provider}</span>
                        {!item.isFree && (
                          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-500/20 text-amber-300">
                            ${item.finalUsdCost} (~{item.finalUahCost} грн)
                          </span>
                        )}
                      </div>
                      <img src={item.assetUrl} alt={`Frame ${idx + 1}`} className="w-full h-40 object-cover rounded-lg border border-slate-800" />
                      <div className="flex items-center justify-between text-[11px] font-mono text-emerald-300 bg-emerald-950/70 border border-emerald-500/30 px-2.5 py-1.5 rounded-lg">
                        <span className="flex items-center gap-1.5 font-bold">
                          <Calendar className="w-3.5 h-3.5 text-emerald-400" />
                          <span>{isUk ? '📅 Дата та час зйомки:' : '📅 Acquisition Date:'}</span>
                        </span>
                        <strong className="font-mono text-white text-xs">
                          {formatRawDate(item.datetime)}
                        </strong>
                      </div>
                      <button
                        onClick={() => triggerDeviceDownload(item.assetUrl, `Sat_Image_${(regionName || 'Location').replace(/[^a-zA-Z0-9]/g, '_')}_${(item.provider || 'Frame').replace(/[^a-zA-Z0-9]/g, '_')}_${idx + 1}.jpg`)}
                        className="w-full py-2 bg-slate-800 hover:bg-slate-700 text-emerald-400 font-mono text-xs font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                      >
                        <Download className="w-3.5 h-3.5" />
                        <span>{isUk ? '📥 Завантажити цей знімок на пристрій' : '📥 Save this Frame to Device'}</span>
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {satDocxUrl && (
            <div className="p-4 bg-emerald-500/10 border border-emerald-500/30 rounded-xl flex items-center justify-between">
              <div className="space-y-0.5 font-mono">
                <p className="text-xs font-bold text-emerald-300">
                  {isUk ? '📄 Офіційний звіт за ДСТУ 4163:2020 сформовано' : '📄 Official DSTU 4163:2020 Report Ready'}
                </p>
                <p className="text-[10px] text-emerald-400/80">
                  {isUk ? 'Готовий для подання в державні органи та Реєстр Збитків' : 'Ready for submission to official registers'}
                </p>
              </div>
              <a
                href={satDocxUrl}
                download
                className="px-4 py-2 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold rounded-xl flex items-center gap-1.5 shadow-lg shadow-emerald-500/20 font-mono transition-all"
              >
                <Download className="w-4 h-4" />
                <span>{isUk ? 'Завантажити DOCX' : 'Download DOCX'}</span>
              </a>
            </div>
          )}

          <div className="text-slate-200 text-sm leading-relaxed font-mono space-y-2">
            {renderFormattedReport(report)}
          </div>
        </div>
      )}

      {/* Lightbox Modal for Satellite Imagery */}
      {activeLightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 backdrop-blur-md flex items-center justify-center p-4 sm:p-8 animate-fadeIn"
          onClick={() => setActiveLightboxImage(null)}
        >
          <div 
            className="relative max-w-5xl max-h-[90vh] bg-slate-900 border border-slate-800 rounded-2xl overflow-hidden shadow-2xl flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between p-4 bg-slate-950 border-b border-slate-800">
              <span className="text-xs font-mono font-bold text-emerald-400 flex items-center gap-2 truncate">
                <ImageIcon className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>{activeLightboxImage.alt}</span>
              </span>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => triggerDeviceDownload(activeLightboxImage.src, `Sat_Fullscreen_${Date.now()}.jpg`)}
                  className="px-3 py-1.5 bg-emerald-500 hover:bg-emerald-400 text-slate-950 text-xs font-bold rounded-lg flex items-center gap-1.5 transition-colors font-mono cursor-pointer"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span>{isUk ? 'Завантажити JPG' : 'Download JPG'}</span>
                </button>
                <button
                  type="button"
                  onClick={() => setActiveLightboxImage(null)}
                  className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-slate-800 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="p-4 overflow-auto flex items-center justify-center bg-black/80">
              <img 
                src={activeLightboxImage.src} 
                alt={activeLightboxImage.alt}
                className="max-h-[75vh] w-auto object-contain rounded-lg"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
